<svelte:options strictprops={false} />

<script>
  import mitt from "mitt";
  import cloneDeep from "lodash/cloneDeep";
  import get from "lodash/get";
  import set from "lodash/set";
  import range from "lodash/range";
  import uniq from "lodash/uniq";
  import uniqBy from "lodash/uniqBy";
  import isEmpty from "lodash/isEmpty";
  import isEqual from "lodash/isEqual";
  import { add, subtract, multiply, midpoint, distSq } from "vector";
  import { onMount, tick, getContext, setContext } from "svelte";
  import { writable } from "svelte/store";
  import { Drawing } from "drawing";
  import { bucketArray, objectify, bucketArrayMultiple } from "overline";
  import { OSDViewer } from "svelte-openseadragon";
  import { IIIFTileSource, ImageTileSource } from "openseadragon";
  import { Link, navigate } from "svelte-routing";

  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import FrameXPanel from "#lib/framex/FrameXPanel.svelte";
  import RightPanelTabs from "#lib/framex/rightpanel/RightPanelTabs.svelte";
  import CaretLeftIcon from "@local/assets/icons/caret-left.svg";
  import ShowIcon from "@local/assets/icons/show.svg";
  import HideIcon from "@local/assets/icons/hide.svg";
  import PrevNext from "#lib/sidebar/PrevNext.svelte";
  import SelectedActions from "#lib/SelectedActions.svelte";
  import DocSearchWidget from "#lib/DocSearchWidget.svelte";
  import DocPageRelabelWidget from "#lib/DocPageRelabelWidget.svelte";
  import DocQuickSelectWidget from "#lib/DocQuickSelectWidget.svelte";
  import DocRulerWidget from "#lib/DocRulerWidget.svelte";
  import DocCornerPanels from "#lib/DocCornerPanels.svelte";
  import AiClipWidget from "#lib/AiClipWidget.svelte";
  import DrawHintBanner from "#lib/framex/DrawHintBanner.svelte";
  import FilterWidget from "#lib/FilterWidget.svelte";
  import CountGrid from "#lib/CountGrid.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import Item2dEditor from "#lib/Item2dEditor.svelte";

  import { Modal, Portal } from "svelte-utilities";
  import Sidebar from "#lib/sidebar/Sidebar.svelte";
  import PageProperties from "#lib/sidebar/PageProperties.svelte";
  import DocumentProperties from "#lib/sidebar/DocumentProperties.svelte";
  import LocationProperties from "#lib/sidebar/LocationProperties.svelte";
  import ItemProperties from "#lib/sidebar/ItemProperties.svelte";
  import ListDownloadModal from "#lib/home/ListDownloadModal.svelte";
  import CustomScaleModal from "#lib/sidebar/CustomScaleModal.svelte";
  import SidebarSection from "#lib/sidebar/SidebarSection.svelte";
  import DocLocationProperties from "#lib/sidebar/DocLocationProperties.svelte";
  import {
    showRightPanel,
    showLeftPanel,
    currentTool,
    selectedLocations,
    selectedDimensions,
    hiddenLocations,
    selectedAnnotations,
    showSearchResults,
    useSelectedInRelabel,
    selectedRecords,
    selectedPages,
    hiddenPages,
    currentDocid,
    clipboard,
    expandRightPanel,
    documentViewShowItems,
    documentViewShowCollections,
    documentViewShowTypes,
    documentViewShowNotes,
    documentViewShowDimensions,
    documentViewLocationFilter,
    documentViewItemFilter,
    documentViewCollectionFilter,
    documentViewTypeFilter,
    documentViewNoteFilter,
    preserveDimensions,
    useDocMeasurementScale,
    docMeasurementScale,
    settingScrollSpeed,
    settingTriopsUrl as triopsUrl,
    settingOsdInitialLoad,
    settingOsdTimeout,
    settingOsdTileRetryMax,
    settingOsdTileRetryDelay,
    itemOverlayMode,
    itemOverlay,
    showingTutorial,
    createSelectedObjects,
    leftPanelMode,
  } from "#src/stores/ui.js";
  import { alerts } from "#src/stores/alerts.js";
  import { fetchGroup } from "#src/stores/group.js";
  import { lengthDim, rectangleDims } from "#src/extensions/dimension-from-points.js";
  import dimSettings from "@local/extensions/utilities/doc-dim-settings.js";
  import {
    orderedList,
    removeFromSortableList,
    addToSortableListAtIndex,
    sortListBy,
  } from "@local/extensions/collections/sortable-list.js";
  import { user, profile } from "#src/stores/auth.js";
  import markColor from "@local/extensions/drawing/mark-color.js";
  import {
    clipRectangle,
    draggedClip,
    screenScaledClip,
    constrainClip,
  } from "@local/extensions/drawing/clip-rectangle.js";

  import { getFiles, getFileData, getProgress } from "#src/extensions/check-document-status.js";
  import {
    createItem as defaultItem,
    createType as defaultType,
    createNote as defaultNote,
    createLocation as defaultLocation,
    createDimension as defaultDimension,
    itemFromVertices,
    hydrateItem,
    hydrateGroup,
    gridlinePositions,
    positionsToLines,
  } from "@local/lamina-core";
  import copyToClipboard from "#src/extensions/copy-to-clipboard.js";
  import drawingSymbol from "@local/extensions/drawing/drawing-symbol.js";
  import editSymbol from "@local/extensions/drawing/edit-symbol.js";
  import eb from "#src/extensions/event-bus.js";
  import nearColors from "#src/extensions/near-colors.js";
  import { incrementMarkCopy, nextMark as nextMarkFromList } from "@local/extensions/identifiers/mark.js";
  import { isLocationVisible, locRecord } from "#src/extensions/filter-items.js";
  import MarkUpdateModal from "#lib/marks/MarkUpdateModal.svelte";
  import {
    orderFrameRecords,
    otherMarksInScope,
    startMarkFor,
    changedUpdates,
  } from "#lib/marks/frame-selection.js";
  import {
    sheetScaleFactor,
    transformSheetPoint,
  } from "@local/extensions/geometry/transform-sheet-points.js";
  import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
  import extension from "@local/extensions/utilities/extension.js";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import validDropIndices from "#src/extensions/valid-drop-indices.js";
  import linkPosition from "#src/extensions/link-position.js";
  import shapeOverlay from "@local/extensions/drawing/shape-overlay.js";
  import translateRectangle from "@local/extensions/geometry/translate-rectangle.js";
  import parseDim from "@local/extensions/parsers/parse-dim.js";
  import { discontiguousKey, modifierKey } from "#src/extensions/event-keys.js";
  import { locationDrawingUpdate, imageDrawingFromClip } from "#src/extensions/docs/location-drawing.js";
  import {
    scaleOptions,
    multiplier,
    definedMultipliers,
    getScaleLabel,
  } from "@local/extensions/utilities/scales.js";
  import { api } from "#src/api";
  import ScaleFromDimensionModal from "#src/lib/ScaleFromDimensionModal.svelte";
  import { UX_MODE } from "#src/env.js";
  import { isNotApplicable, isScaled } from "#src/lib/framex/derive.js";
  import { forceScaleFor } from "#lib/framex/force-scale.js";
  import { framexPageRing, framexRepaintKey } from "#lib/framex/canvas.js";

  export let group;
  export let docid;
  export let doc;
  export let items;
  export let collections;
  export let types;
  export let documentNotes;
  export let disabled;
  export let customColumns;
  export let customColColumns;
  export let standardColumns;
  export let queryParams;

  const CLIP_THRESHOLD = 600;
  const DRAG_THRESHOLD = 50;
  const controller = mitt();
  const org = getContext("org");
  const shpxAssistPages = getContext("shpxAssistPages");
  const fileData = writable({});
  setContext("fileData", fileData);

  let loaded = false;

  // Assuming 300 DPI and 72 pts/inch for PDF rasterization
  const sf = 300 / 72;

  const markShapes = {
    item: "hexagon",
    collection: "rectangle",
    type: "circle",
    document_note: "rectangle",
  };

  const pointerbuttons = {
    dwgbtn: true,
    editbtn: true,
    link: true,
    location: true,
    docdim: true,
    annotation: true,
    "page-outline": true,
  };

  const pdfUnits = {
    "'": "feet",
    '"': "inches",
    "''": "inches",
    ft: "feet",
    in: "inches",
    cm: "centimeters",
    m: "meters",
    mm: "millimeters",
  };

  const statusColors = {
    processing: "#00B9A7",
    submitted: "#00B9A7",
    complete: "#3AA4FF",
    flagged: "#FF5E68",
    canceled: "#999",
  };

  function transform(coll, xf, defaultValue = []) {
    return coll ? xf(coll) : defaultValue;
  }

  let isSetup = false;
  let initialPage;
  let initialBounds;
  let dpi = window.devicePixelRatio;

  let viewer;
  let adding = null;
  let confirmRemoveLocationsModal;
  let scaleFromDimensionModal;
  let locationsToDelete = [];
  let dimensionsToDelete = [];
  let confirmRemovePagesModal;
  let pagesToDelete = [];
  let confirmRemoveNotesModal;
  let notesToDelete = [];
  let tileSources = [];
  let links = {};
  let annotations = [];
  let searchResults = [];
  let importSearchResultsAs = "item";
  let hoveredFile = null;
  let hoveredPage = null;
  let lastSelectedPage = null;
  let hoveredAnnotation = null;
  let hoveredSearchResult = null;
  let hoveredLink = null;

  let overlayCanvas;
  let overlayCtx;
  let overlayFresh = false;

  let hiddenPageModal;
  let hiddenPageStatus = [];

  let timeout;
  let timeoutInterval = 1000;

  let width;
  let height;
  let container;
  let viewerContainer;
  let ioExtents;
  let selectedFeatures = [];
  let spinners = createSelectedObjects();

  let hitHashes = {};
  let overallHitHashes = {};
  let multiclicks = { tool: null, clicks: [] };
  let currentHb = null;
  let currentMousePos = null;
  let pannable = $currentTool === "pan";
  let tempTool = null;
  let dragStart = null;
  let dragging = null;
  let overlayRefreshKey = false;
  let selecting = null;
  let clipping = null;
  let hoveredLocations = {};
  let cursor = "default";
  let highlightedText = null;
  let showPageRelabelRegions = false;
  let pageRegions;
  let tempPageRegion;
  let relabeledPages = {};
  let overlayDwgs;
  let clicking = false;
  let clickStart = null;
  let aiClipRegion = null;
  let markUpdateModal;
  let altDown = false;
  let shiftDown = false;
  let itemOverlayBbox = null;
  let customScaleModal;

  let setPageScaleModal;
  let pagesToSetScale = [];
  let pageScaleValue = null;
  let reverseMarkUpdate = false;

  // Deliberately plain variables, not `$:` derivations. A reactive statement
  // here depends on selectedLocationList and visibleIndices, which are declared
  // further down, so Svelte hoists that whole chain to the top of the
  // component's update pass — moving `checkSelected(docid)` after
  // `visiblePages`. checkSelected restores $hiddenPages from the document's
  // saved filters, so running it later means visiblePages is first computed
  // with no pages hidden, initTileSources captures every sheet, and the canvas
  // keeps painting sheets the rest of the app has since hidden.
  // Snapshotting on open also guarantees the marks the dialog previewed map
  // onto exactly the records it previewed them for.
  let markUpdateSelection = [];
  let markUpdateMarks = [];
  let markUpdateOtherMarks = [];
  let markUpdateStart = "";

  $: origDoc = docid !== doc.id ? $group.documents[docid] : null;
  $: isVirtual = origDoc?.is_virtual;
  $: assistPages = $shpxAssistPages[origDoc?.id] || {};
  $: isPageEditingDisabled = disabled || isVirtual;
  $: checkSelected(docid);
  $: drawer = $profile?.enable_hardware_acceleration
    ? ["webgl", "canvas", "html"]
    : ["canvas", "webgl", "html"];
  $: allAttachments = $group && bucketArrayMultiple($group.attachments, "item_id", "type_id");
  $: visiblePages = getVisiblePages(doc, $hiddenPages);
  $: visibleIndices = getVisibleIndices(visiblePages);
  $: files = getFiles(doc);
  $: {
    $hiddenPages;
    refreshAnnotations();
  }
  $: fileIndex = getFileIndex(doc);
  $: progress = isVirtual ? getProgress(doc, $fileData, origDoc.data.filters) : getProgress(doc, $fileData);
  $: checkStatus(doc, $fileData);
  $: sidebarPosition = getSidebarPosition(width, height);
  $: paddingX = width >= 640 ? 180 : 160;
  $: paddingY = width >= 640 ? 150 : 120;
  $: docLocations = locationsByDocument($group);
  $: locations = docLocations[doc?.id] || [];
  $: docDimensions = locationsByDocument($group, "document_dimensions");
  $: dimensions = docDimensions[doc?.id] || [];
  $: documents =
    UX_MODE === "shpx"
      ? orderedList($group.documents).filter((d) => d.type === "virtual")
      : orderedList($group.documents).filter((d) => d.type === "imported" || d.type === "virtual");
  $: docIndex = documents.findIndex((f) => f.id === docid);
  $: nextDoc = documents[docIndex + 1];
  $: prevDoc = documents[docIndex - 1];
  $: locationsObj = objectify(locations);
  $: annosObj = objectify(annotations);
  $: dimsObj = objectify(dimensions);
  $: searchResultsObj = bucketArray(searchResults, "page_id");
  $: hoveredPageId = currentMousePos?.index != null && visiblePages[currentMousePos.index];
  $: filters = {
    show_items: $documentViewShowItems,
    show_collections: $documentViewShowCollections,
    show_types: $documentViewShowTypes,
    show_notes: $documentViewShowNotes,
    show_dimensions: $documentViewShowDimensions,
    location_filter: cloneDeep($documentViewLocationFilter),
    item_filter: cloneDeep($documentViewItemFilter),
    collection_filter: cloneDeep($documentViewCollectionFilter),
    type_filter: cloneDeep($documentViewTypeFilter),
    note_filter: cloneDeep($documentViewNoteFilter),
    hidden_pages: cloneDeep($hiddenPages),
    hidden_locations: cloneDeep($hiddenLocations),
  };
  $: filtersApplied =
    !$documentViewShowItems ||
    !$documentViewShowCollections ||
    !$documentViewShowTypes ||
    !$documentViewShowNotes ||
    !$documentViewShowDimensions ||
    $documentViewLocationFilter ||
    $documentViewItemFilter ||
    $documentViewCollectionFilter ||
    $documentViewTypeFilter ||
    $documentViewNoteFilter;
  $: indexedLocations = indexLocations(locations, filters);
  $: indexedDimensions = indexDimensions(dimensions, filters);
  $: locationsByRecord = bucketArray(locations, "record_id");
  $: collectionItems = bucketArray(items, "collection_id");
  $: reactToQueryParams(queryParams);
  $: nextMark = getNextMark(adding, $group, items, collections, types, documentNotes);
  $: selectedAnnotationList = Object.keys($selectedAnnotations);
  $: selectedLocationList = Object.keys($selectedLocations)
    .map((id) => locationsObj[id])
    .filter((loc) => loc);
  $: selectedDimensionList = Object.keys($selectedDimensions)
    .map((id) => dimsObj[id])
    .filter((dim) => dim);
  $: hiddenLocationList = Object.keys($hiddenLocations)
    .map((id) => locationsObj[id])
    .filter((loc) => loc);
  $: hiddenPageList = Object.keys($hiddenPages)
    .map((id) => doc.data.pages[id])
    .filter((page) => page);
  $: selectedPageList = Object.keys($selectedPages)
    .map((id) => doc.data.pages[id])
    .filter((page) => page);
  $: uniqLocations = uniqBy(selectedLocationList, "record_id").filter((l) => l && !!locRecord(l, $group));
  $: location = uniqLocations[0];
  $: prevLoc = getPrevLocation(location, indexedLocations);
  $: nextLoc = getNextLocation(location, indexedLocations);
  $: locationRecord = locRecord(location, $group);
  $: checkCurrentTool($currentTool);
  $: vddirty = isVdChanged(origDoc, filters);
  $: {
    if (loaded) {
      indexedLocations;
      $selectedLocations;
      $selectedDimensions;
      $selectedAnnotations;
      hoveredLocations;
      hoveredAnnotation;
      annosObj;
      $fileData;
      highlightedText;
      searchResultsObj;
      $showSearchResults;
      hoveredSearchResult;
      importSearchResultsAs;
      showPageRelabelRegions;
      pageRegions;
      aiClipRegion;
      $currentTool;
      shiftDown;
      tempTool;
      overlayRefreshKey;
      assistPages;
      $leftPanelMode;
      overlayDwgs = makeOverlayDwgs();
    }
  }
  $: marquee = makeMarqueeDwg(selecting, clipping);
  $: draggedMark = makeDraggedMark(dragging, shiftDown);
  $: pageDropTarget = makePageDropTarget(dragging);
  $: spinnersList = Object.values($spinners);
  // Only the FrameX per-sheet flags should force a canvas repaint, and only in
  // SHPX-ai. Depending on `doc.data` re-fired this on every group mutation,
  // including in the default product.
  $: framexPaintKey = UX_MODE === "shpx" ? framexRepaintKey(visiblePages, doc, $selectedPages) : "";
  $: {
    docid;
    overlayDwgs;
    marquee;
    draggedMark;
    pageDropTarget;
    hoveredFile;
    hoveredPage;
    hoveredAnnotation;
    highlightedText;
    $leftPanelMode;
    framexPaintKey;
    if (viewer) {
      controller.emit("redraw");
    }
  }
  $: {
    if (overlayCanvas) {
      dpi;
      overlayCtx = overlayCanvas.getContext("2d");
    }
  }
  $: {
    overlayFresh;
    trackMouse(currentMousePos, shiftDown, adding, dragging, multiclicks, $currentTool);
  }

  function blankWhiteTileSource() {
    return "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAQSURBVHgBAQUA+v8A/////wn7A/2j0UkKAAAAAElFTkSuQmCC";
  }

  function defaultFilters() {
    return {
      show_items: true,
      show_collections: true,
      show_types: true,
      show_notes: true,
      show_dimensions: true,
      location_filter: null,
      item_filter: null,
      collection_filter: null,
      type_filter: null,
      note_filter: null,
      hidden_pages: {},
      hidden_locations: {},
    };
  }

  function isVdChanged(od, filters) {
    if (!od) return false;

    const f = od.data.filters || defaultFilters();

    if (f.show_items !== filters.show_items) return true;
    if (f.show_collections !== filters.show_collections) return true;
    if (f.show_types !== filters.show_types) return true;
    if (f.show_notes !== filters.show_notes) return true;
    if (f.show_dimensions !== filters.show_dimensions) return true;
    if (!isEqual(f.location_filter, filters.location_filter)) return true;
    if (!isEqual(f.item_filter, filters.item_filter)) return true;
    if (!isEqual(f.collection_filter, filters.collection_filter)) return true;
    if (!isEqual(f.type_filter, filters.type_filter)) return true;
    if (!isEqual(f.note_filter, filters.note_filter)) return true;
    if (!isEqual(f.hidden_pages, filters.hidden_pages)) return true;
    if (!isEqual(f.hidden_locations, filters.hidden_locations)) return true;

    return false;
  }

  function backoff() {
    timeoutInterval = Math.min(timeoutInterval * 2, 10000);
  }

  function resetInterval() {
    timeoutInterval = 1000;
  }

  async function initTileSources() {
    // Perform async update, requesting signed URL for image tiles
    const tiles = visiblePages.map((pageId, index) => {
      const page = doc.data.pages[pageId];
      const file = $fileData[page.name];
      if (file?.status[page.page]?.state !== "completed") {
        const current = tileSources[index];

        if (current?.tileSource instanceof ImageTileSource) {
          return null;
        }

        return new ImageTileSource({
          url: blankWhiteTileSource(),
        });
      }

      if (file.file.metadata?.mimetype.startsWith("image")) {
        return api.storage
          .from("documents")
          .createSignedUrl(file.file.name, 60 * 60)
          .then(({ data }) => {
            if (!data) {
              return new ImageTileSource({
                url: blankWhiteTileSource(),
              });
            }

            return new ImageTileSource({
              url: data.signedUrl,
            });
          });
      } else if (file.file.metadata?.mimetype === "application/pdf") {
        const nme = nameMinusExtension(page.name);
        const id = `${$triopsUrl}/raw/iiif/2/drawings/${nme}.${page.page}.tiff`;
        const current = tileSources[index];

        // Don't replace the given tile source if it is unchanged
        if (current?.tileSource?.["@id"] === id) {
          return null;
        }

        const path = encodeURIComponent(nme);
        if ($settingOsdInitialLoad === "pre-fetch") {
          return fetch(`${$triopsUrl}/iiif/2/drawings/${path}.${page.page}.tiff/info.json`)
            .then((res) => res.json())
            .then((info) => new IIIFTileSource(info))
            .catch(() => {
              return new ImageTileSource({
                url: blankWhiteTileSource(),
              });
            });
        } else {
          return `${$triopsUrl}/iiif/2/drawings/${path}.${page.page}.tiff/info.json`;
        }
      }
    });

    const ts = await Promise.all(tiles);
    ts.forEach((tileSource, index) => {
      if (tileSource) {
        tileSources[index] = { tileSource };
      }
    });

    if (ts.length < tileSources.length) {
      const removed = tileSources.length - ts.length;
      tileSources.splice(-removed);
    }

    tileSources = tileSources;
  }

  function getInitialPageValue(queryParams, visibleIndices) {
    const param = queryParams?.["doc-page"];
    if (param == undefined) {
      return null;
    }

    if (Array.isArray(param)) {
      return param.map((v) => visibleIndices[v]);
    }

    return visibleIndices[param];
  }

  function getInitialBounds(queryParams, visibleIndices) {
    const docBounds = queryParams?.["doc-bounds"];
    const selectedRecords = queryParams?.["selected-records"];
    if (docBounds == undefined && selectedRecords == undefined) {
      return null;
    }

    if (docBounds) {
      const params = Array.isArray(docBounds) ? docBounds : [docBounds];
      const bounds = params
        .map((b) => {
          if (!b) return null;
          const [page, x, y] = b.split(",");
          if (!page || !x || !y) return null;
          const index = visibleIndices[page];
          if (index == undefined) return null;

          return {
            index,
            point: { x: parseFloat(x), y: parseFloat(y) },
          };
        })
        .filter((b) => b);

      if (!bounds.length) {
        return null;
      }

      return bounds;
    } else if (selectedRecords) {
      const recs = selectedRecords.split(",");
      const locs = recordLocs(recs);
      const bounds = [];

      locs.forEach((l) => {
        if (visibleIndices[l.page_id] != undefined) {
          bounds.push({
            index: visibleIndices[l.page_id],
            point: { x: l.position.x, y: l.position.y },
          });
        }

        if (l.clip) {
          bounds.push(
            {
              index: visibleIndices[l.page_id],
              point: { x: l.clip.x, y: l.clip.y + l.clip.height },
            },
            {
              index: visibleIndices[l.page_id],
              point: { x: l.clip.x + l.clip.width, y: l.clip.y },
            },
          );
        }
      });

      if (!bounds.length) {
        return null;
      }

      return bounds;
    }
  }

  function createLocationLink() {
    const locpoints = selectedLocationList.map((loc) => {
      if (loc.clip) {
        const x = [loc.position.x, loc.clip.x, loc.clip.x + loc.clip.width];
        const y = [loc.position.y, loc.clip.y, loc.clip.y + loc.clip.height];
        const minX = Math.min(...x);
        const minY = Math.min(...y);
        const maxX = Math.max(...x);
        const maxY = Math.max(...y);
        return `doc-bounds=${loc.page_id},${minX},${minY}&doc-bounds=${loc.page_id},${maxX},${maxY}`;
      }

      return `doc-bounds=${loc.page_id},${loc.position.x},${loc.position.y}`;
    });

    const dimpoints = selectedDimensionList.map((dim) => {
      const a = dim.data.points[0];
      const b = dim.data.points[1];
      return `doc-bounds=${dim.page_id},${a.x},${a.y}&doc-bounds=${dim.page_id},${b.x},${b.y}`;
    });

    const points = [...locpoints, ...dimpoints].join("&");

    const path = window.location.href.split("?")[0];
    const url = `${path}?${points}`;
    copyToClipboard(url);
    navigate(`./${docid}?${points}`, { replace: true });
  }

  function createPageLink() {
    const page = selectedPageList.map((p) => `doc-page=${p.id}`).join("&");
    const path = window.location.href.split("?")[0];
    const url = `${path}?${page}`;
    copyToClipboard(url);
    navigate(`./${docid}?${page}`, { replace: true });
  }

  function updateLinks() {
    const pageMap = visiblePages.reduce((map, pageId) => {
      const page = doc.data.pages[pageId];
      const file = $fileData[page.name];
      if (file?.status[page.page]?.state === "completed") {
        if (!map[page.name]) map[page.name] = {};
        map[page.name][page.page] = pageId;
      }

      return map;
    }, {});

    links = files.reduce((acc, fileId) => {
      const file = $fileData[fileId];
      if (!file?.sheets) return acc;
      Object.keys(file.sheets).forEach((idx) => {
        const sheet = file.sheets[idx];
        if (pageMap[fileId]?.[sheet.idx]) {
          const pageId = pageMap[fileId][sheet.idx];
          acc[pageId] = sheet.dng_links;
        }
      });

      return acc;
    }, {});
  }

  async function checkStatus(doc, fd) {
    if (!isSetup) return;
    if (timeout) {
      clearTimeout(timeout);
    }

    if (progress === 1) {
      if (!doc.ready) {
        group.updateDocument(doc.id, { ready: true });
      }
      return;
    }

    const incomplete =
      doc.data?.pages.order.reduce((incomplete, pageId) => {
        const page = doc.data.pages[pageId];
        if (fd[page.name]?.status[page.page]?.state !== "completed") {
          incomplete[page.name] = true;
        }
        return incomplete;
      }, {}) || {};

    const pages = doc.data?.pages.order.reduce((p, pageId) => {
      const page = doc.data.pages[pageId];
      if (!p[page.name]) p[page.name] = [];
      p[page.name].push(page.page);
      return p;
    }, {});

    const check = async () => {
      let refreshTiles = false;
      const s = await Promise.all(Object.keys(incomplete).map((f) => getFileData(f)));

      let bo = true;
      s.forEach((st) => {
        if (st?.file?.name) {
          pages[st.file.name].forEach((index) => {
            if (st.status[index]?.state !== $fileData[st.file.name]?.status[index]?.state) {
              refreshTiles = true;
            }
          });

          if (
            Object.keys(st.status).some(
              (k) => st.status[k]?.state !== $fileData[st.file.name]?.status[k]?.state,
            )
          ) {
            bo = false;
          }

          $fileData[st.file.name] = st;
        }
      });

      // Find any pending pages that are now complete
      const counts = {};
      doc.data.pages.order.forEach((pid) => {
        const page = doc.data.pages[pid];
        if (page?.pending && $fileData[page.name]) {
          const sheets = Object.keys($fileData[page.name].sheets);
          if (sheets.length) {
            counts[pid] = sheets.length;
          }
        }
      });

      if (Object.keys(counts).length) {
        const data = cloneDeep(doc.data);

        Object.keys(counts)
          .toReversed()
          .forEach((pid) => {
            const page = data.pages[pid];
            delete page.pending;
            const count = counts[pid];

            const newIds = count > 1 ? Array.from({ length: count - 1 }, (_) => crypto.randomUUID()) : [];
            const newPages = newIds.map((id, i) => ({
              ...page,
              id,
              page: i + 1,
            }));

            if (newPages.length) {
              const pageIndex = data.pages.order.indexOf(pid);
              addToSortableListAtIndex(data.pages, pageIndex + 1, ...newPages);
            }
          });

        group.updateDocument(doc.id, { data });
      }

      if (bo) {
        backoff();
      } else {
        resetInterval();
      }

      if (refreshTiles) {
        initTileSources();
      }
    };

    timeout = setTimeout(check, timeoutInterval);
  }

  function getVisiblePages(doc, hiddenPages) {
    if (!doc?.data) return [];

    return doc.data.pages.order.filter((pageId) => {
      const page = doc.data.pages[pageId];
      return !page.hidden && !hiddenPages[pageId];
    });
  }

  function getVisibleIndices(visiblePages) {
    return visiblePages.reduce((obj, pageId, index) => {
      obj[pageId] = index;
      return obj;
    }, {});
  }

  function getFileIndex(doc) {
    const idx = {};
    doc.data.pages.order.forEach((pageId) => {
      const page = doc.data.pages[pageId];
      if (!idx[page.name]) idx[page.name] = {};
      idx[page.name][page.page] = pageId;
    });
    return idx;
  }

  function getNextLocation(location, indexedLocations) {
    if (!location) return null;
    const pageLocs = indexedLocations[location.page_id];
    const last = pageLocs[pageLocs.length - 1];

    if (location.id === last?.id) {
      const pageIndex = doc.data.pages.indices[location.page_id];
      const next = range(pageIndex + 1, doc.data.pages.order.length).find((i) => {
        const id = doc.data.pages.order[i];
        const page = doc.data.pages[id];
        return page && !page?.hidden && indexedLocations[id]?.length;
      });
      if (next !== undefined) {
        const id = doc.data.pages.order[next];
        return indexedLocations[id][0];
      }
    } else {
      const currentIndex = pageLocs.findIndex((l) => l.id === location.id);
      return pageLocs[currentIndex + 1];
    }
  }

  function getPrevLocation(location, indexedLocations) {
    if (!location) return null;
    const pageLocs = indexedLocations[location.page_id];
    const first = pageLocs[0];

    if (location.id === first?.id) {
      const pageIndex = doc.data.pages.indices[location.page_id];
      const prev = range(pageIndex - 1, -1, -1).find((i) => {
        const id = doc.data.pages.order[i];
        const page = doc.data.pages[id];
        return page && !page?.hidden && indexedLocations[id]?.length;
      });
      if (prev !== undefined) {
        const id = doc.data.pages.order[prev];
        const index = indexedLocations[id].length - 1;
        return indexedLocations[id][index];
      }
    } else {
      const currentIndex = pageLocs.findIndex((l) => l.id === location.id);
      return pageLocs[currentIndex - 1];
    }
  }

  function clipVertex(clip, index) {
    if (index == 0) return { x: clip.x, y: clip.y + clip.height };
    if (index == 1) return { x: clip.x + clip.width, y: clip.y + clip.height };
    if (index == 2) return { x: clip.x + clip.width, y: clip.y };
    return { x: clip.x, y: clip.y };
  }

  function itemFromLinearDim(annotation, dimParser, importedDim = "width", alternateDim) {
    let width;
    let height;

    if (importedDim === "width") {
      width = dimParser(annotation.own_label);
      height = alternateDim;
    } else {
      height = dimParser(annotation.own_label);
      width = alternateDim;
    }

    const width_offset = dimParser("0");
    const height_offset = dimParser("0");

    return hydrateItem({
      width,
      width_offset,
      height,
      height_offset,
      data: {},
      shape: { type: "rect" },
      rectangle_offset: {},
      is_imported: true,
    });
  }

  async function updateAnnotations() {
    if (!doc) {
      annotations = [];
      return;
    }

    const pdfIds = files.filter((name) => extension(name) === "pdf");

    const polyreq = api
      .from("dng_polygon_notes")
      .select("*,sheet:dng_sheets(limits:limits_json,rotation)")
      .eq("document_bucket_id", "documents")
      .in("document_name", pdfIds);

    const lindimreq = api
      .from("dng_linear_dimensions")
      .select("*,sheet:dng_sheets(limits:limits_json,rotation)")
      .eq("document_bucket_id", "documents")
      .in("document_name", pdfIds);

    const [polys, lindims] = await Promise.all([polyreq, lindimreq]);

    if (polys.error || lindims.error) {
      annotations = [];
      return;
    }

    const importUnit = $group?.data.settings?.display_unit || "inches";
    const dimParser = parseDim(importUnit);

    const pi = visiblePages.reduce((obj, pageId) => {
      const page = doc.data.pages[pageId];
      if (!obj[page.name]) obj[page.name] = [];
      obj[page.name].push(page.page);
      return obj;
    }, {});

    annotations = polys.data
      .concat(lindims.data)
      .filter((a) => {
        if (a.type === "/PolygonDimension") {
          if (a.measure_scale === null) return false;

          const indices = pi[a.document_name];
          return indices?.includes(a.sheet_idx);
        } else if (a.type === "/LineDimension") {
          try {
            const dim = dimParser(a.own_label);
            return true;
          } catch (e) {
            return false;
          }
        }

        return false;
      })
      .map((a) => ({
        ...a,
        page: visibleIndices[fileIndex[a.document_name]?.[a.sheet_idx]],
        page_id: fileIndex[a.document_name]?.[a.sheet_idx],
        item:
          a.type === "/PolygonDimension"
            ? itemFromVertices(a.scaled_vertices, pdfUnits[a.measure_unit], a.sheet?.rotation)
            : itemFromLinearDim(a, dimParser),
      }))
      .filter((a) => a.page !== undefined)
      .sort((a, b) => {
        if (a.page !== b.page) {
          return a.page - b.page;
        }
        return a.id - b.id;
      });
  }

  function updateAnnotationDimensions(e) {
    const { importedDim, alternateDim } = e.detail;
    const importUnit = $group?.data.settings?.display_unit || "inches";
    const dimParser = parseDim(importUnit);
    annotations = annotations.map((annotation) => {
      if (annotation.type === "/LineDimension") {
        annotation.item = itemFromLinearDim(annotation, dimParser, importedDim, alternateDim);
      }

      return annotation;
    });
  }

  async function loadFileData() {
    const s = await Promise.all(files.map((f) => getFileData(f)));
    files.forEach((file, index) => {
      $fileData[file] = s[index];
    });
  }

  async function refreshFileData() {
    if (!isSetup) return;
    await loadFileData();
    await updateAnnotations();
  }

  async function refreshAnnotations() {
    if (!isSetup) return;
    await updateAnnotations();
  }

  async function setup() {
    await loadFileData();
    await initTileSources();
    updateAnnotations();
    updateLinks();
    initialPage = getInitialPageValue(queryParams, visibleIndices);
    initialBounds = getInitialBounds(queryParams, visibleIndices);
    isSetup = true;
  }

  async function finish() {
    loaded = true;
  }

  function checkSelected(docid) {
    if ($currentDocid && $currentDocid !== docid) {
      deselectAll();
      $hiddenLocations = {};
      $hiddenPages = {};

      $documentViewShowItems = true;
      $documentViewShowCollections = true;
      $documentViewShowTypes = true;
      $documentViewShowNotes = true;
      $documentViewShowDimensions = true;

      $documentViewLocationFilter = null;
      $documentViewItemFilter = null;
      $documentViewCollectionFilter = null;
      $documentViewTypeFilter = null;
      $documentViewNoteFilter = null;
    }

    if (isVirtual && origDoc?.data?.filters) {
      revertFilters();
    }

    if (docid) {
      $currentDocid = docid;
    }

    const toDeselect = Object.keys($selectedLocations).filter((id) => !locRecord(locationsObj?.[id], $group));
    if (toDeselect.length) {
      selectedLocations.deselect(...toDeselect);
    }
  }

  function saveFilters() {
    const filters = {
      show_items: $documentViewShowItems,
      show_collections: $documentViewShowCollections,
      show_types: $documentViewShowTypes,
      show_notes: $documentViewShowNotes,
      show_dimensions: $documentViewShowDimensions,
      location_filter: cloneDeep($documentViewLocationFilter),
      item_filter: cloneDeep($documentViewItemFilter),
      collection_filter: cloneDeep($documentViewCollectionFilter),
      type_filter: cloneDeep($documentViewTypeFilter),
      note_filter: cloneDeep($documentViewNoteFilter),
      hidden_pages: cloneDeep($hiddenPages),
      hidden_locations: cloneDeep($hiddenLocations),
    };

    const data = cloneDeep(origDoc.data);
    data.filters = filters;

    group.updateDocument(docid, { data });
  }

  function revertFilters() {
    const filters = origDoc.data.filters;
    $documentViewShowItems = filters?.show_items ?? true;
    $documentViewShowCollections = filters?.show_collections ?? true;
    $documentViewShowTypes = filters?.show_types ?? true;
    $documentViewShowNotes = filters?.show_notes ?? true;
    $documentViewShowDimensions = filters?.show_dimensions ?? true;

    $documentViewLocationFilter = filters ? cloneDeep(filters.location_filter) : null;
    $documentViewItemFilter = filters ? cloneDeep(filters.item_filter) : null;
    $documentViewCollectionFilter = filters ? cloneDeep(filters.collection_filter) : null;
    $documentViewTypeFilter = filters ? cloneDeep(filters.type_filter) : null;
    $documentViewNoteFilter = filters ? cloneDeep(filters.note_filter) : null;

    $hiddenLocations = filters?.hidden_locations ? cloneDeep(filters.hidden_locations) : {};

    let shouldRefreshPages = !isEqual($hiddenPages, filters?.hidden_pages || {});
    $hiddenPages = filters?.hidden_pages ? cloneDeep(filters.hidden_pages) : {};

    if (shouldRefreshPages) refreshPageList();
  }

  function getNextMark(adding, grp, items, collections, types, documentNotes) {
    if (!adding?.clone) return null;
    let record;
    let marks = [];

    if (adding.record_type === "type") {
      record = grp.types[adding.record_id];
      marks = types.map((t) => t.mark);
    } else if (adding.record_type === "item") {
      record = grp.items[adding.record_id];
      marks = items.map((i) => i.mark);
    } else if (adding.record_type === "collection") {
      record = grp.items[adding.record_id];
      marks = collections.map((c) => c.mark);
    } else if (adding.record_type === "document_note") {
      record = grp.document_notes[adding.record_id];
      marks = documentNotes.map((c) => c.mark);
    }

    if (!record) return null;

    return incrementMarkCopy(record.mark, marks);
  }

  function getSidebarPosition(width, height) {
    if (width >= 640) {
      return "right";
    }

    return height > width ? "bottom" : "right";
  }

  function checkCurrentTool(ct) {
    if (
      !["location-item", "location-type", "location-collection", "location-document_note"].includes(ct) &&
      !tempTool
    ) {
      adding = null;
    }

    if (!["dimension-linear", "dimension-rectangular", "pick-scale-dim"].includes(ct) && !tempTool) {
      if (multiclicks?.tool != null) {
        multiclicks = { tool: null, clicks: [] };
      }
    }

    pannable = ct === "pan";
  }

  function indexLocations(locations, filters) {
    const indexed = doc.data.pages.order.reduce((o, pageId) => {
      o[pageId] = [];
      return o;
    }, {});

    const toDeselect = [];

    locations.forEach((location) => {
      const isVisible = isLocationVisible(location, $group, filters);
      if ($selectedLocations[location.id] && !isVisible) {
        toDeselect.push(location.id);
      }

      if (indexed[location?.page_id] && isVisible) {
        indexed[location.page_id].push(location);
      }
    });

    if (toDeselect.length) {
      selectedLocations.deselect(...toDeselect);
    }

    return indexed;
  }

  function indexDimensions(dimensions, filters) {
    const indexed = doc.data.pages.order.reduce((o, pageId) => {
      o[pageId] = [];
      return o;
    }, {});

    if (!filters.show_dimensions) {
      $selectedDimensions = {};
      return indexed;
    }

    dimensions.forEach((dimension) => {
      if (indexed[dimension?.page_id]) {
        indexed[dimension.page_id].push(dimension);
      }
    });

    Object.entries(indexed).forEach(([pageId, dimensions]) => {
      indexed[pageId] = dimensions.sort((a, b) => {
        if (a.data.points[0].x !== b.data.points[0].x) return a.data.points[0].x - b.data.points[0].x;
        return a.data.points[0].y - b.data.points[0].y;
      });
    });

    return indexed;
  }

  function recordMark(location, record) {
    if (location.record_type === "document_note") {
      if (!record.title) return record.mark;
      return `${record.mark} - ${record.title}`;
    } else if (location.record_type !== "type") {
      const labelBy = record.is_collection
        ? $group.data.settings.collections_label_by
        : $group.data.settings.label_by;

      return labelBy ? get(record, labelBy) : record.mark;
    } else {
      return record.mark;
    }
  }

  function annotationPoint(annotation) {
    const pt = annotation.own_label_point;
    const tile = viewer.tile(annotation.page);
    if (!tile) return null;
    const size = tile.getContentSize();

    const initPt = {
      x: pt[0] - annotation.sheet.limits[0][0],
      y: pt[1] - annotation.sheet.limits[0][1],
    };

    const ssf = sheetScaleFactor(annotation.sheet.limits, annotation.sheet.rotation, size);
    const pos = transformSheetPoint(initPt, annotation.sheet.rotation, ssf, size);
    const p = { x: pos.x, y: -pos.y };
    return p;
  }

  function annotationMark(annotation, style, name = null) {
    const p = annotationPoint(annotation);
    if (!p) return null;

    const mk = new Drawing().mark(p, "✓", { shape: "hexagon" }).style(style);
    if (name) {
      return mk.name(name);
    }

    return mk;
  }

  function locationIcon(location) {
    const icon =
      {
        circle: "circle",
        square: "square",
        rectangle: "square",
        hexagon: "hexagon",
        note: "note",
        triangle: "triangle",
        diamond: "diamond",
        anchor: "anchor",
        hard_splice: "splice",
        working_splice: "splice_circle",
      }[location?.symbol] || null;

    if (location.record_type === "document_note" && location.symbol === "default") {
      return "note";
    }

    return icon;
  }

  function makeOverlayDwgs() {
    if (!viewer) return {};
    let currentSection = null;
    return visiblePages.reduce((dwgs, pageId) => {
      const locations = indexedLocations[pageId] || [];
      const dimensions = indexedDimensions[pageId] || [];
      const index = visibleIndices[pageId];
      if (index === undefined) return dwgs;
      const tile = viewer.tile(index);
      if (!tile) return dwgs;
      const size = tile.getContentSize();
      let shapedrawings = [];

      const linkOverlays = [];
      if (links[pageId]?.length) {
        const page = doc.data.pages[pageId];
        if (page) {
          const sheet = $fileData[page.name]?.sheets[page.page];
          if (sheet) {
            links[pageId].forEach((link, idx) => {
              const { insertion_point, angle, width, height } = link;
              const { x, y } = insertion_point;
              const c2 = { x: x + width, y: y + height };
              const corners = [insertion_point, c2]
                .map((c) => ({
                  x: c.x - sheet.limits[0][0],
                  y: c.y - sheet.limits[0][1],
                }))
                .map((c) => transformSheetPoint(c, sheet.rotation || 0, sf, size))
                .map((c) => ({ x: c.x, y: -c.y }));

              const hovered = hoveredLink?.page_id === pageId && hoveredLink?.link_index === idx;

              const dwg = new Drawing()
                .rectangle(corners[0], corners[1])
                .style({
                  stroke: "transparent",
                  fill: hovered ? "rgba(59,125,216,0.15)" : "transparent",
                  lineWidth: 0,
                })
                .name(`link_${pageId}_${idx}`);

              linkOverlays.push(dwg);
            });
          }
        }
      }

      const ddims = dimensions.map((d) => {
        if (d.type === "aligned") {
          const pta = { x: d.data.points[0].x, y: -d.data.points[0].y };
          const ptb = { x: d.data.points[1].x, y: -d.data.points[1].y };
          const lw = $selectedDimensions[d.id] ? 3 : 1;
          return new Drawing()
            .aligned_dim(pta, ptb, {
              override: d.data.values.length,
              annotative: d.scale_reference === "screen_space",
              scale: getMarkerScale(d),
            })
            .style({
              stroke: d.color_override || "#FF0000",
              lineWidth: lw,
              textColor: d.color_override || "#FF0000",
            })
            .name(`docdim_${d.id}`);
        } else if (d.type === "rectangular") {
          const pta = { x: d.data.points[0].x, y: -d.data.points[0].y };
          const ptb = { x: d.data.points[1].x, y: -d.data.points[1].y };
          const minX = Math.min(pta.x, ptb.x);
          const minY = Math.min(pta.y, ptb.y);
          const maxX = Math.max(pta.x, ptb.x);
          const maxY = Math.max(pta.y, ptb.y);
          const lw = $selectedDimensions[d.id] ? 3 : 1;

          const wmp = { x: (minX + maxX) / 2, y: minY };
          const hmp = { x: minX, y: (minY + maxY) / 2 };
          const centroid = { x: wmp.x, y: hmp.y };

          return new Drawing()
            .rectangle(pta, ptb)
            .style({
              fill: null,
              stroke: d.color_override || "#FF0000",
              textColor: d.color_override || "#FF0000",
              lineWidth: lw,
            })
            .text(d.data.values.width, wmp, {
              offset: { x: 0, y: 10 },
              annotative: d.scale_reference === "screen_space",
              scale: getMarkerScale(d),
            })
            .text(d.data.values.height, hmp, {
              rotation: Math.PI / 2,
              offset: { x: 15, y: 0 },
              annotative: d.scale_reference === "screen_space",
              scale: getMarkerScale(d),
            })
            .text(d.data.values.area ?? " ", centroid, {
              annotative: d.scale_reference === "screen_space",
              scale: getMarkerScale(d),
            })
            .name(`docdim_${d.id}`);
        }
      });

      // Overlap prefilter: bucket markers into 10-unit grid cells so the
      // any-marker proximity warning stays linear on dense sheets. Two
      // markers closer than the 10-unit threshold always land in the same
      // or adjacent cells, so only those need real distance checks; the
      // warning semantics are identical to checking every pair (LNA-32).
      const overlapGrid = new Map();
      for (const loc of locations) {
        if (!loc) continue;
        const key = `${Math.floor(loc.position.x / 10)},${Math.floor(loc.position.y / 10)}`;
        const bucket = overlapGrid.get(key);
        if (bucket) bucket.push(loc);
        else overlapGrid.set(key, [loc]);
      }
      const overlapsAny = (loc) => {
        const cx = Math.floor(loc.position.x / 10);
        const cy = Math.floor(loc.position.y / 10);
        for (let gx = cx - 1; gx <= cx + 1; gx++) {
          for (let gy = cy - 1; gy <= cy + 1; gy++) {
            const bucket = overlapGrid.get(`${gx},${gy}`);
            if (!bucket) continue;
            for (const other of bucket) {
              if (other === loc) continue;
              const ox = other.position.x - loc.position.x;
              const oy = other.position.y - loc.position.y;
              if (ox * ox + oy * oy < 100) return true;
            }
          }
        }
        return false;
      };

      const smarks = [];
      const marks = locations
        .filter((loc) => loc)
        .map((loc, li, locs) => {
          const record = locRecord(loc, $group);
          const highlighted =
            hoveredLocations?.[loc.id] || $selectedLocations?.[loc.id] || $selectedRecords[record.id];
          const pos = { x: loc.position.x, y: -loc.position.y };
          // Stacked markers must warn regardless of draw order: a pasted or
          // late-added marker is appended to the list, so comparing only to
          // the previous element misses almost every real stack — the border
          // then appears after a reload reorders the list (LNA-32). Both
          // members of a stack warn, not just the later one.
          const overlaps = overlapsAny(loc);

          const icon = locationIcon(loc);
          const icon_only = ["symbol_only", "content_with_symbol"].includes(loc.representation);
          const shape = loc.shape === "default" ? markShapes[loc.record_type] : loc.shape;
          let color;
          if (dragging && dragging.selected?.[loc.id] && !dragging.copy) {
            color = { fill: "#FFFFFF", textColor: "#FFFFFF", stroke: "#AAAAAA" };
          } else {
            color = markColor(record, {
              highlighted,
              emphasized: true,
              warning: !!overlaps,
              override: loc.color_override,
            });
          }

          const markText = recordMark(loc, record);

          if (
            loc.id === record.drawing?.attachment_id &&
            ["item", "collection"].includes(loc.record_type) &&
            record.drawing.clip?.drawing_scale &&
            record.show_drawing_overlay
          ) {
            shapedrawings.push(
              shapeOverlay(record, {
                dragging: dragging?.location?.id === loc.id,
              }),
            );
          }

          if ($selectedLocations[loc.id]) {
            const scale_factor = getMarkerScale(loc);

            let mk;
            if (loc.representation !== "content") {
              smarks.push(
                new Drawing()
                  .mark(pos, markText, {
                    shape,
                    highlighted: true,
                    icon,
                    annotative: loc.scale_reference === "screen_space",
                    scale: scale_factor,
                    icon_only,
                    show_checkbox: loc.show_checkbox,
                    checked: loc.checked,
                  })
                  .style({ ...color, lineWidth: 2 })
                  .name(`location_${loc.id}`),
              );
            }

            let clip;
            let dwgBtn;
            let editBtn;
            let warning;
            let content;

            if (loc.clip) {
              let clipStyle;
              if (dragging?.location?.id === loc.id) {
                clipStyle = { stroke: "#CCCCCC" };
              }
              clip = clipRectangle("loc", loc.clip, loc.id, clipStyle);
              const h = record.drawing && record.drawing.attachment_id === loc.id;

              dwgBtn = drawingSymbol(
                loc.id,
                {
                  x: loc.clip.x + loc.clip.width,
                  y: -loc.clip.y - loc.clip.height,
                },
                h,
              );

              if (["item", "collection"].includes(loc.record_type)) {
                editBtn = editSymbol(loc.id, {
                  x: loc.clip.x + loc.clip.width,
                  y: -loc.clip.y - loc.clip.height,
                });
              }

              if (h && record.show_drawing_overlay && !record.drawing.clip?.drawing_scale) {
                warning = new Drawing()
                  .text(
                    "NO SCALE",
                    {
                      x: loc.clip.x + loc.clip.width,
                      y: -loc.clip.y,
                    },
                    {
                      offset: { x: 0, y: 10 },
                      alignment: "right",
                      font: "sans-serif",
                    },
                  )
                  .style({ fontSize: 10, textColor: "#FF0000" });
              }
            }

            if (["content", "content_with_symbol"].includes(loc.representation)) {
              if (loc.record_type === "document_note" && record.body) {
                const textContent = record.title ? `${record.title}\n${record.body}` : record.body;
                content = new Drawing()
                  .text(
                    textContent,
                    { x: loc.position.x, y: -loc.position.y },
                    {
                      alignment: "left",
                      multiline: true,
                      annotative: loc.scale_reference === "screen_space",
                      outline: true,
                      highlighted: true,
                      scale: scale_factor,
                      offset: { x: 15, y: 0 },
                    },
                  )
                  .style({ textColor: loc.color_override || "#FF0000", lineWidth: 2 })
                  .name(`location_${loc.id}`);
              }
            }

            return new Drawing().add(clip, mk, dwgBtn, editBtn, content, warning);
          } else {
            if (loc.representation === "content" && loc.record_type === "document_note" && record.body) {
              const textContent = record.title ? `${record.title}\n${record.body}` : record.body;
              return new Drawing()
                .text(
                  textContent,
                  { x: loc.position.x, y: -loc.position.y },
                  {
                    alignment: "left",
                    multiline: true,
                    annotative: loc.scale_reference === "screen_space",
                    outline: true,
                    scale: getMarkerScale(loc),
                    offset: { x: 15, y: 0 },
                  },
                )
                .style({ textColor: loc.color_override || "#FF0000", lineWidth: 1 })
                .name(`location_${loc.id}`);
            } else if (
              loc.representation === "content_with_symbol" &&
              loc.record_type === "document_note" &&
              record.body
            ) {
              const mk = new Drawing()
                .mark(pos, markText, {
                  shape,
                  icon,
                  annotative: loc.scale_reference === "screen_space",
                  scale: getMarkerScale(loc),
                  icon_only,
                  show_checkbox: loc.show_checkbox,
                  checked: loc.checked,
                })
                .style({ ...color, lineWidth: overlaps ? 4 : 2 })
                .name(`location_${loc.id}`);

              const textContent = record.title ? `${record.title}\n${record.body}` : record.body;
              const content = new Drawing()
                .text(
                  textContent,
                  { x: loc.position.x, y: -loc.position.y },
                  {
                    alignment: "left",
                    multiline: true,
                    annotative: loc.scale_reference === "screen_space",
                    outline: true,
                    scale: getMarkerScale(loc),
                    offset: { x: 15, y: 0 },
                  },
                )
                .style({ textColor: loc.color_override || "#FF0000", lineWidth: 1 })
                .name(`location_${loc.id}`);

              return new Drawing().add(mk, content);
            } else {
              return new Drawing()
                .mark(pos, markText, {
                  shape,
                  icon,
                  annotative: loc.scale_reference === "screen_space",
                  scale: getMarkerScale(loc),
                  icon_only,
                  show_checkbox: loc.show_checkbox,
                  checked: loc.checked,
                })
                .style({ ...color, lineWidth: overlaps ? 4 : 2 })
                .name(`location_${loc.id}`);
            }
          }
        });

      const samarks = Object.keys($selectedAnnotations)
        .map((id) => annosObj[id])
        .filter((a) => a && a.page === parseInt(index))
        .map((a) => annotationMark(a, { fill: "#F7F7B7", lineWidth: 2 }, `annotation_${a.id}`));

      let pr;
      if (showPageRelabelRegions && pageRegions && relabeledPages[pageId]) {
        const relabelSelected = $useSelectedInRelabel && selectedPageList.length;
        if (!relabelSelected || $selectedPages[pageId]) {
          pr = new Drawing();
          if (pageRegions.title) {
            const title = translateRectangle(pageRegions.title, size);
            const clip = clipRectangle("region", title, "title")
              .text(
                "Page Title",
                { x: title.x, y: -title.y - title.height },
                {
                  offset: { x: 5, y: 10 },
                  alignment: "left",
                  font: "sans-serif",
                },
              )
              .style({ textColor: "#0066E5" });
            pr = pr.add(clip);
          }

          if (pageRegions.number) {
            const number = translateRectangle(pageRegions.number, size);
            const clip = clipRectangle("region", number, "number")
              .text(
                "Page Number",
                { x: number.x, y: -number.y - number.height },
                {
                  offset: { x: 5, y: 10 },
                  alignment: "left",
                  font: "sans-serif",
                },
              )
              .style({ textColor: "#0066E5" });
            pr = pr.add(clip);
          }

          if (tempPageRegion && dragging?.tile_index === index) {
            const region = translateRectangle(tempPageRegion, size);
            const clip = clipRectangle("region", region, "temp", { stroke: "#333333", lineWidth: 1 });
            pr = pr.add(clip);
          }
        }
      } else if (aiClipRegion?.page_id === pageId) {
        let clipStyle;
        if (dragging?.type === "ai-clip-vertex" || dragging?.type === "ai-clip-outline") {
          clipStyle = { stroke: "#CCCCCC" };
        }
        pr = clipRectangle("ai", aiClipRegion.clip, "region", clipStyle);
      }

      let ham;
      if (hoveredAnnotation) {
        const annotation = annosObj[hoveredAnnotation];
        if (annotation.page === parseInt(index)) {
          ham = annotationMark(
            annotation,
            { fill: "#000000", lineWidth: 2, textColor: "#FFFFFF" },
            `annotation_${hoveredAnnotation}`,
          );
        }
      }

      let hsrm;
      if (hoveredSearchResult) {
        const hovered = searchResultsObj?.[pageId]?.find((res) => res.id === hoveredSearchResult);
        if (hovered) {
          hsrm = new Drawing()
            .mark({ x: hovered.center.x, y: -hovered.center.y }, hovered.label, {
              shape: markShapes[importSearchResultsAs],
            })
            .style({ fill: "#000000", lineWidth: 2, textColor: "#FFFFFF" })
            .name(`searchresult_${hoveredSearchResult}`);
        }
      }

      let label;
      let section;
      {
        const page = doc.data.pages[pageId];
        if (page) {
          const pageIndex = doc.data.pages.indices[pageId];
          const sheet = $fileData[page.name]?.sheets?.[page.page];
          let labelText = pageIndex + 1;
          if (page.label || sheet?.name) {
            labelText += "  " + (page.label || sheet?.name);
          }
          label = new Drawing()
            .text(
              labelText,
              { x: 0, y: -size.y },
              {
                offset: { x: 5, y: -10 },
                alignment: "left",
                font: "sans-serif",
                width: size.x,
                truncate: true,
              },
            )
            .style({ fontSize: 10 });

          if (page.section != currentSection) {
            currentSection = page.section;
            const text = new Drawing()
              .text(
                page.section || "No section",
                { x: 0, y: 0 },
                {
                  offset: { x: 20, y: 10 },
                  alignment: "left",
                  font: "sans-serif",
                  width: size.x,
                  truncate: true,
                },
              )
              .style({ fontSize: 10, fontWeight: page.section == null ? "italic" : "normal" });

            const triangle = new Drawing()
              .symbol(
                [
                  {
                    type: "polyface",
                    faces: [
                      [
                        { x: -5, y: 4 },
                        { x: 0, y: -4 },
                        { x: 5, y: 4 },
                      ],
                    ],
                  },
                ],
                { x: 0, y: 0 },
                { offset: { x: 10, y: 10 } },
              )
              .style({ fill: "#DDDDDD", stroke: page.section == null ? "#AAAAAA" : "#000000" });

            section = new Drawing().add(triangle, text);
          }
        }
      }

      let textsearchdwg;
      if (highlightedText?.[pageId]) {
        const txt = highlightedText[pageId].map((t) => {
          const { x, y, width, height, angle } = t;
          return new Drawing()
            .rectangle(0, 0, width, height)
            .rotate(-angle)
            .translate(x, -y)
            .style({ fill: "transparent", stroke: "#EBEB06", lineWidth: 2 });
        });
        textsearchdwg = new Drawing().add(...txt);
      }

      let htdwg;
      if (highlightedText?.highlighted?.page_id === pageId) {
        const { x, y, width, height, angle } = highlightedText.highlighted;
        htdwg = new Drawing()
          .rectangle(0, 0, width, height)
          .rotate(-angle)
          .translate(x, -y)
          .style({ fill: "rgba(255, 255, 0, 0.5)", stroke: "#EBEB06", lineWidth: 2 });
      }

      let stmarks = [];
      if ($showSearchResults && searchResultsObj?.[pageId]) {
        stmarks = searchResultsObj[pageId].map((result) => {
          const { id, center, label } = result;
          const p = { x: center.x, y: -center.y };
          const dwg = new Drawing()
            .mark(p, label, { shape: markShapes[importSearchResultsAs] })
            .style({ fill: "#F7F7B7", lineWidth: 2 })
            .name(`searchresult_${id}`);
          return dwg;
        });
      }

      const dwg = new Drawing().add(
        ...shapedrawings,
        ...linkOverlays,
        ...ddims,
        ...marks,
        ...smarks,
        ...samarks,
        ...stmarks,
        pr,
        ham,
        hsrm,
        label,
        section,
        textsearchdwg,
        htdwg,
      );

      dwgs[index] = dwg;
      return dwgs;
    }, {});
  }

  function makeMarqueeDwg(selecting, clipping) {
    let start;
    let end;
    let style;
    if (selecting) {
      start = selecting.start;
      end = selecting.end;
      style = { fill: "transparent", stroke: "black" };
    } else if (clipping) {
      start = clipping.start;
      end = clipping.end;
      style = { stroke: "#0066E5", fill: "transparent", lineWidth: 2 };
    } else {
      return null;
    }

    const x = Math.min(start.x, end.x) * dpi;
    const y = Math.min(start.y, end.y) * dpi;
    const w = Math.abs(start.x - end.x) * dpi;
    const h = Math.abs(start.y - end.y) * dpi;

    return new Drawing().rectangle(x, -y, w, -h).style(style);
  }

  function getMarkerScale(location) {
    if (location.scale_reference === "paper_space") {
      return location.size / 3;
    } else {
      return location.size / 10;
    }
  }

  function makeDraggedMark(dragging, shiftDown) {
    if (dragging?.type === "location") {
      if (!dragging.location) return null;
      const { start, end, selected } = dragging;

      // Marks the clones will receive, minted in drag order so a
      // multi-frame ghost shows (2), (3), ... exactly as the drop will.
      const previewMarks =
        UX_MODE === "shpx" && dragging.copy ? collections.map((c) => c.mark) : null;

      const marks = Object.keys(selected)
        .filter((locid) => locationsObj[locid])
        .map((locid) => {
          const loc = locationsObj[locid];
          const record = locRecord(loc, $group);
          const mark = recordMark(loc, record);
          const index = visibleIndices[loc.page_id];
          const pos = viewer.pixelCoords({ ...loc.position, index });
          if (!pos) return null;
          const delta = subtract(end, start);
          let newPos = add(pos, delta);

          if (shiftDown) {
            const dx = Math.abs(newPos.x - pos.x);
            const dy = Math.abs(newPos.y - pos.y);
            if (dx > dy) {
              newPos = { x: newPos.x, y: pos.y };
            } else {
              newPos = { x: pos.x, y: newPos.y };
            }
          }

          const newTc = viewer.tileCoords(newPos);
          const isViable = newTc.index >= 0;
          const color = isViable
            ? markColor(record, { emphasized: true, override: loc.color_override })
            : { fill: "#FFFFFF", textColor: "#FFFFFF", stroke: "#AAAAAA" };

          const scaledPos = multiply(newPos, { x: dpi, y: -dpi });
          const icon = locationIcon(loc);
          const shape = loc.shape === "default" ? markShapes[loc.record_type] : loc.shape;
          const icon_only = ["symbol_only", "content_with_symbol"].includes(loc.representation);
          const scale_factor = getMarkerScale(loc);

          if (loc.representation === "content" && loc.record_type === "document_note" && record.body) {
            const textContent = record.title ? `${record.title}\n${record.body}` : record.body;
            return new Drawing()
              .text(
                textContent,
                { x: scaledPos.x, y: scaledPos.y },
                {
                  alignment: "left",
                  multiline: true,
                  // annotative: loc.scale_reference === "screen_space",
                  outline: true,
                  // scale: scale_factor,
                  offset: { x: 15, y: 0 },
                },
              )
              .style({ textColor: loc.color_override || "#FF0000", lineWidth: 1 });
          } else if (
            loc.representation === "content_with_symbol" &&
            loc.record_type === "document_note" &&
            record.body
          ) {
            const mk = new Drawing()
              .mark(scaledPos, mark, {
                shape,
                icon,
                icon_only,
                show_checkbox: loc.show_checkbox,
                checked: loc.checked,
              })
              .style(color);
            const textContent = record.title ? `${record.title}\n${record.body}` : record.body;
            const content = new Drawing()
              .text(
                textContent,
                { x: scaledPos.x, y: scaledPos.y },
                {
                  alignment: "left",
                  multiline: true,
                  // annotative: loc.scale_reference === "screen_space",
                  outline: true,
                  // scale: scale_factor,
                  offset: { x: 15, y: 0 },
                },
              )
              .style({ textColor: loc.color_override || "#FF0000", lineWidth: 1 });

            return new Drawing().add(mk, content);
          }

          const mk = new Drawing()
            .mark(scaledPos, mark, {
              shape,
              icon,
              icon_only,
              show_checkbox: loc.show_checkbox,
              checked: loc.checked,
            })
            .style(color);

          // FrameX alt-drag copies the whole frame: preview the boundary in
          // its standard styling and label the ghost with the mark the clone
          // will receive on drop.
          if (UX_MODE === "shpx" && dragging.copy && loc.record_type === "collection" && loc.clip) {
            const ca = viewer.pixelCoords({ x: loc.clip.x, y: loc.clip.y, index });
            const cb = viewer.pixelCoords({
              x: loc.clip.x + loc.clip.width,
              y: loc.clip.y + loc.clip.height,
              index,
            });
            if (ca && cb) {
              const pd = subtract(newPos, pos);
              const pa = multiply(add(ca, pd), { x: dpi, y: -dpi });
              const pb = multiply(add(cb, pd), { x: dpi, y: -dpi });
              const nextMark = incrementMarkCopy(record?.mark || "", previewMarks);
              previewMarks.push(nextMark);
              const ghost = new Drawing()
                .mark(scaledPos, nextMark, {
                  shape,
                  icon,
                  icon_only,
                  show_checkbox: loc.show_checkbox,
                  checked: loc.checked,
                })
                .style(color);
              const boundary = clipRectangle(
                "drag-ghost",
                {
                  x: Math.min(pa.x, pb.x),
                  y: Math.min(pa.y, pb.y),
                  width: Math.abs(pb.x - pa.x),
                  height: Math.abs(pb.y - pa.y),
                },
                loc.id,
                { inverted: false },
              );
              return new Drawing().add(ghost, boundary);
            }
          }

          return mk;
        });

      return new Drawing().add(...marks);
    } else if (dragging?.type === "docdim") {
      if (!dragging.dimension) return null;
      const { start, end, selected } = dragging;

      const dims = Object.keys(selected)
        .filter((dimid) => dimsObj[dimid])
        .map((dimid) => {
          const dim = dimsObj[dimid];
          const index = visibleIndices[dim.page_id];
          const pta = viewer.pixelCoords({ ...dim.data.points[0], index });
          const ptb = viewer.pixelCoords({ ...dim.data.points[1], index });
          if (!pta || !ptb) return null;
          const delta = subtract(end, start);
          const newA = add(pta, delta);
          const newB = add(ptb, delta);
          const newATc = viewer.tileCoords(newA);
          const newBTc = viewer.tileCoords(newB);
          const isViable = newATc.index >= 0 && newBTc.index >= 0;
          const style = isViable
            ? {
                stroke: dim.color_override || "#FF0000",
                lineWidth: 2,
                textColor: dim.color_override || "#FF0000",
                fill: "transparent",
              }
            : { lineWidth: 1, textColor: "#FFFFFF", stroke: "#AAAAAA", fill: "transparent" };
          const sA = multiply(newA, { x: dpi, y: -dpi });
          const sB = multiply(newB, { x: dpi, y: -dpi });

          if (dim.type === "aligned") {
            return new Drawing()
              .aligned_dim(sA, sB, {
                override: dim.data.values.length,
                // annotative: dim.scale_reference === "screen_space",
                // scale: getMarkerScale(dim),
              })
              .style(style);
          } else if (dim.type === "rectangular") {
            return new Drawing().rectangle(sA, sB).style(style);
          }
        });

      return new Drawing().add(...dims);
    }
  }

  function makePageDropTarget(dragging) {
    if (dragging?.type !== "pages") return null;
    const nearest = viewer.nearestTile(dragging.end);
    const { x, y, width, height } = nearest.rect;

    const before = dragging.end.x <= nearest.center.x;
    const gapIndex = before ? nearest.index : nearest.index + 1;
    if (!dragging.droppableIndices[gapIndex]) {
      return null;
    }

    const margin = nearest.margin;

    let dtX;
    if (before) {
      dtX = x - margin / 2;
    } else {
      dtX = x + width + margin / 2;
    }

    return new Drawing()
      .polyline([
        { x: dtX * dpi, y: -(y * dpi) },
        { x: dtX * dpi, y: -(y + height) * dpi },
      ])
      .style({ stroke: "#0066E5", lineWidth: 2 });
  }

  function zoomIn() {
    controller.emit("zoom-in");
  }

  function zoomOut() {
    controller.emit("zoom-out");
  }

  function zoomToFit() {
    controller.emit("home");
  }

  function zoomToPage(id) {
    const ids = Array.isArray(id) ? id : [id];
    const indices = ids.map((id) => visibleIndices[id]).filter((index) => index !== undefined);

    if (indices.length) {
      controller.emit("zoom-to-page", indices);
    }
  }

  function zoomSelected() {
    if (selectedPageList.length) {
      const indices = selectedPageList.map((p) => visibleIndices[p.id]).filter((i) => i != undefined);
      if (indices.length) {
        controller.emit("zoom-to-page", indices);
      }
    } else if (selectedLocationList.length || selectedDimensionList.length) {
      const locPoints = selectedLocationList
        .map((loc) => {
          const index = visibleIndices[loc.page_id];
          const point = { x: loc.position.x, y: loc.position.y };
          return { index, point };
        })
        .filter((pt) => pt.index != undefined);
      const dimPoints = selectedDimensionList
        .map((dim) => {
          const index = visibleIndices[dim.page_id];
          const midpoint = {
            x: (dim.data.points[0].x + dim.data.points[1].x) / 2,
            y: (dim.data.points[0].y + dim.data.points[1].y) / 2,
          };
          return { index, point: midpoint };
        })
        .filter((pt) => pt.index != undefined);
      controller.emit("zoom-to-bounds", { points: [...locPoints, ...dimPoints] });
    } else {
      zoomToFit();
    }
  }

  function zoomSelectedRecords() {
    const recs = Object.keys($selectedRecords);
    const locs = recs.reduce((acc, recId) => {
      const l = locationsByRecord[recId] || [];
      acc.push(...l);
      return acc;
    }, []);
    if (locs.length > 0) {
      const indices = locations.map((l) => visibleIndices[l.page_id]).filter((l) => l !== undefined);
      const pages = uniq(indices);
      controller.emit("zoom-to-page", pages);
    }
  }

  function selectRecords() {
    const recs = Object.keys($selectedRecords);
    const locs = recs.reduce((acc, recId) => {
      const l = locationsByRecord[recId] || [];
      acc.push(...l);
      return acc;
    }, []);
    if (locs.length > 0) {
      selectedLocations.select(...locs.map((l) => l.id));
      $selectedRecords = {};
    }
  }

  function recordLocs(ids) {
    return ids.reduce((acc, recId) => {
      if (locationsByRecord[recId]) {
        acc.push(...locationsByRecord[recId]);
      } else if ($group.locations[recId]) {
        acc.push($group.locations[recId]);
      }
      return acc;
    }, []);
  }

  function reactToQueryParams(queryParams) {
    const sbq = queryParams?.["selected-records"];
    if (!sbq) return;
    const ids = sbq.split(",");
    if (ids.length) {
      const locs = recordLocs(ids);
      if (locs.length > 0) {
        selectedLocations.selectOnly(...locs.map((l) => l.id));
        const points = [];

        locs.forEach((l) => {
          if (visibleIndices[l.page_id] != undefined) {
            points.push({
              index: visibleIndices[l.page_id],
              point: { x: l.position.x, y: l.position.y },
            });

            if (l.clip) {
              points.push(
                {
                  index: visibleIndices[l.page_id],
                  point: { x: l.clip.x, y: l.clip.y + l.clip.height },
                },
                {
                  index: visibleIndices[l.page_id],
                  point: { x: l.clip.x + l.clip.width, y: l.clip.y },
                },
              );
            }
          }
        });
        if (points.length) {
          controller.emit("zoom-to-bounds", { points });
        }
      }
    }
  }

  function zoomToBounds(points) {
    controller.emit("zoom-to-bounds", { points });
  }

  function moveToLocation(location) {
    const { page_id, point } = location;
    const index = visibleIndices[page_id];
    controller.emit("pan-to-location", { point, page: index });
  }

  function tileOverlay(options) {
    const dwg = overlayDwgs?.[options.index];

    let dwgHash;
    if (dwg) {
      dwg.render({ ctx: options.context, annoScale: 1 / options.zoom });
      dwgHash = dwg.renderHitbox({ ctx: options.hitContext, annoScale: 1 / options.zoom });
    }

    hitHashes[options.index] = dwgHash;
  }

  function overlay(options) {
    if (marquee) {
      marquee.render({ ctx: options.context });
    }

    if (draggedMark) {
      draggedMark.render({ ctx: options.context, annoScale: dpi });
    }

    if (pageDropTarget) {
      pageDropTarget.render({ ctx: options.context });
    }

    let pageHitBoxes = [];
    let pageOutlines = [];
    let sectionLabels = [];

    const contextWidth = options.context.canvas.width;
    const contextHeight = options.context.canvas.height;

    let currentSection = null;
    visiblePages.forEach((pageId, index) => {
      const page = doc.data.pages[pageId];
      if (!page || !viewer) return;

      const tile = viewer.tile(index);

      if (!tile) return;
      const size = tile.getContentSize();

      const p1 = viewer.pixelCoords({ x: 0, y: 0, index });
      const p2 = viewer.pixelCoords({ x: size.x, y: size.y, index });

      if (!p1 || !p2) return;

      const tl = multiply(p1, { x: dpi, y: -dpi });
      const br = multiply(p2, { x: dpi, y: -dpi });

      const newSection = page.section != currentSection;
      if (newSection) {
        currentSection = page.section;
      }

      if (br.x < 0 || tl.x > contextWidth || br.y > 0 || tl.y < -contextHeight) return;

      const outline = new Drawing().polyline([
        { x: br.x, y: br.y },
        { x: tl.x, y: br.y },
        { x: tl.x, y: tl.y },
        { x: br.x, y: tl.y },
        { x: br.x, y: br.y },
      ]);
      const title = new Drawing().rectangle(br.x, br.y - 20, tl.x - br.x, 20);

      pageHitBoxes.push(
        new Drawing().add(outline, title).style({ lineWidth: 30, fill: null }).name(`page-outline_${pageId}`),
      );

      const highlighted = $selectedPages[pageId] || hoveredFile === page.name || hoveredPage === pageId;
      const assist = assistPages[pageId]?.status;
      const color = assist && assist !== "acknowledged" ? statusColors[assist] || "#CCCCCC" : "#CCCCCC";

      if (UX_MODE === "shpx") {
        const ring = framexPageRing(page, { br, tl }, { active: $leftPanelMode === "framex" });
        if (ring) pageOutlines.push(ring);
      }

      pageOutlines.push(
        new Drawing()
          .rectangle(br.x, br.y, tl.x - br.x, tl.y - br.y)
          .style({
            stroke: highlighted ? "#0066E5" : color,
            fill: "transparent",
            lineWidth: highlighted && UX_MODE === "shpx" ? 3 : 1,
          }),
      );

      if (!newSection && page.section && options.zoom > 0.0005) {
        const section = new Drawing()
          .text(page.section, tl, { offset: { x: 5, y: 10 }, alignment: "left", font: "sans-serif" })
          .style({ fontSize: 10 });
        sectionLabels.push(section);
      }

      if (assist && assist !== "acknowledged") {
        const note = new Drawing()
          .text(
            "SHPX ASSIST: " + assist.toUpperCase(),
            { x: br.x, y: tl.y },
            {
              offset: { x: -5, y: 10 },
              alignment: "right",
              font: "sans-serif",
            },
          )
          .style({ fontSize: 10, textColor: color });
        pageOutlines.push(note);
      } else if (UX_MODE === "shpx" || indexedLocations[pageId]?.length) {
        const na = UX_MODE === "shpx" && isNotApplicable(page);
        const scaleNote = new Drawing()
          .text(
            na ? "N/A" : page.scale ? getScaleLabel(page.scale) : "NO SCALE",
            { x: br.x, y: tl.y },
            {
              offset: { x: -5, y: 10 },
              alignment: "right",
              font: "sans-serif",
            },
          )
          .style({
            fontSize: 10,
            ...(na ? { textColor: "#9CA3AF" } : page.scale ? {} : { textColor: "#FF0000" }),
          });
        pageOutlines.push(scaleNote);
      }
    });

    const pageHb = new Drawing().add(...pageHitBoxes);
    const pageOl = new Drawing().add(...pageOutlines, ...sectionLabels);
    pageOl.render({ ctx: options.context, annoScale: dpi });
    overallHitHashes = pageHb.renderHitbox({ ctx: options.hitContext });
  }

  function handleViewportChange() {
    overlayFresh = false;
  }

  function isLoadedPage(index) {
    const page_id = visiblePages[index];
    const page = doc.data.pages?.[page_id];
    const file = $fileData[page?.name];
    return file?.status[page?.page]?.state === "completed";
  }

  async function toggleLocationDrawing(locid) {
    const update = await locationDrawingUpdate(locid, $group);
    if (update) {
      group.update([update]);
    }
  }

  // Tracks the latest edit-mode entry so a deferred (post-zoom) reveal can't
  // fire for a superseded frame. Bumped on every entry and on exit; any pending
  // animation handlers are also removed outright.
  let overlayEntryToken = 0;
  let pendingOverlayAnim = null; // { osd, handlers: [event, fn][] } awaiting reveal
  // FrameX zoom-on-enter: while true, the (already-mounted) edit overlay is kept
  // transparent and non-covering so the OSD zoom animation stays visible; flipped
  // off when the viewport visually arrives at the target. Never set outside the
  // shpx+framex zoom path.
  let itemOverlayPending = false;

  function clearPendingOverlayAnim() {
    if (pendingOverlayAnim) {
      for (const [event, fn] of pendingOverlayAnim.handlers) {
        pendingOverlayAnim.osd?.removeHandler?.(event, fn);
      }
      pendingOverlayAnim = null;
    }
  }

  async function showDetailDrawing(locid) {
    const loc = $group.locations[locid];
    if (!loc) return;
    const item = $group.items[loc.record_id];
    if (!item) return;

    // A new entry supersedes any pending post-zoom reveal from a prior double-click.
    const myToken = ++overlayEntryToken;
    clearPendingOverlayAnim();

    // The editor mounts against a fixed extents snapshot (Viewport.svelte reads
    // initialExtents once, in onMount), so the padding must describe where the
    // clip sits in container pixels *in the view the user will see the editor
    // over* — the live viewport when opening in place, the zoom target when a
    // zoom is in flight.
    const extentsFromPx = (clip, tl, br) => {
      const m = multiplier(clip.drawing_scale);
      const xmin = clip.drawing_offset?.x ?? 0;
      const ymin = clip.drawing_offset?.y ?? 0;
      const xmax = xmin + (clip.width * m) / 300;
      const ymax = ymin + (clip.height * m) / 300;

      return {
        bbox: {
          xmin,
          ymin,
          xmax,
          ymax,
        },
        padding: {
          top: tl.y,
          right: viewerContainer.offsetWidth - br.x,
          bottom: viewerContainer.offsetHeight - br.y,
          left: tl.x,
        },
      };
    };

    const hasEditableClip = () =>
      loc.id === item.drawing?.attachment_id && item.drawing.clip?.drawing_scale;

    // Activates edit mode with the given extents (null = keep prior ioExtents,
    // matching the pre-existing no-clip behavior). Guarded against supersession.
    const activateOverlay = (extents) => {
      if (myToken !== overlayEntryToken) return;
      if (extents) ioExtents = extents;
      $itemOverlay = item.id;
      $itemOverlayMode = loc.record_type;
    };

    // Live-viewport extents: where the clip is on screen right now.
    const liveExtents = () => {
      if (!hasEditableClip()) return null;
      const clip = item.drawing.clip;
      const index = visibleIndices[loc.page_id];
      const tl = viewer.pixelCoords({ x: clip.x, y: clip.y, index });
      const br = viewer.pixelCoords({ x: clip.x + clip.width, y: clip.y + clip.height, index });
      return extentsFromPx(clip, tl, br);
    };

    // FrameX (shpx only): auto-frame the edited frame to the Zoom Selected view on
    // entering edit mode — unless the user has already zoomed in on it. The only
    // "leave the viewport alone" case is the frame's tag being on screen AND the
    // current zoom already being at least as close as Zoom Selected would set;
    // every other case (zoomed out, or tag off screen) recenters.
    if (UX_MODE === "shpx" && $leftPanelMode === "framex") {
      const index = visibleIndices[loc.page_id];
      const viewport = viewer?.getController?.()?.viewer?.viewport;
      if (index != undefined && viewport) {
        // Tag-in-view: the location's tag point projected to container pixels.
        const tagPx = viewer.pixelCoords({ x: loc.position.x, y: loc.position.y, index });
        const tagInView =
          !!tagPx &&
          tagPx.x >= 0 &&
          tagPx.x <= viewerContainer.offsetWidth &&
          tagPx.y >= 0 &&
          tagPx.y <= viewerContainer.offsetHeight;

        // Prospective Zoom Selected zoom, computed without moving the viewport.
        // zoom-to-bounds frames a single point as a fixed 2*padding square
        // (OSDController.zoomToBounds default padding=100), and OSD's
        // Viewport._fitBounds sets newZoom = 1 / newBounds.width where the box is
        // widened to the container aspect — i.e. 1 / (2*padding * max(1, aspect)).
        // Same units as getZoom(), so the two are directly comparable.
        const ZOOM_TO_BOUNDS_PADDING = 100;
        const aspect = viewport.getAspectRatio();
        const prospectiveZoom = 1 / (2 * ZOOM_TO_BOUNDS_PADDING * Math.max(1, aspect));
        const alreadyCloseEnough = viewport.getZoom(true) >= prospectiveZoom;

        // The tiled image is needed both to predict the target framing and as
        // the guard for deferring: zoom-to-bounds no-ops without it, so the
        // animation (and its finish event) would never come.
        const osd = viewer.getController()?.viewer;
        const tiledImage = osd?.world?.getItemAt(index);

        if (!(tagInView && alreadyCloseEnough) && tiledImage) {
          // Zoom case: start the animated Zoom Selected move AND mount the
          // editor in the same tick, so the ~100ms mount cost overlaps the
          // animation instead of following it. The overlay stays transparent
          // and non-covering (the animation is the wayfinding) until the
          // viewport visually arrives, when revealing is a cheap style flip.
          //
          // The editor is framed to the KNOWN zoom target, not the live
          // viewport: the post-zoom view is the 2*padding square centered on
          // the tag, aspect-widened (same box _fitBounds derives), so a clip
          // corner's future pixel position is its viewport-coordinate offset
          // from that box's top-left times containerWidth / boxWidth (OSD
          // scales both axes by width).
          const vcTag = tiledImage.imageToViewportCoordinates(loc.position.x, loc.position.y);
          const boxW = 2 * ZOOM_TO_BOUNDS_PADDING * Math.max(1, aspect);

          let predicted = null;
          if (hasEditableClip()) {
            const clip = item.drawing.clip;
            const boxH = boxW / aspect;
            const scale = viewerContainer.offsetWidth / boxW;
            const targetPx = (x, y) => {
              const v = tiledImage.imageToViewportCoordinates(x, y);
              return {
                x: (v.x - (vcTag.x - boxW / 2)) * scale,
                y: (v.y - (vcTag.y - boxH / 2)) * scale,
              };
            };
            predicted = extentsFromPx(
              clip,
              targetPx(clip.x, clip.y),
              targetPx(clip.x + clip.width, clip.y + clip.height),
            );
          }

          // Reveal on VISUAL arrival, not on formal spring settle. The viewer
          // runs springStiffness 50 with OSD's default ~1.2s animationTime
          // (OSDController.ts), so the visible motion lands in the first few
          // hundred ms while the springs keep settling imperceptibly — and
          // animation-finish only fires after that invisible tail. Waiting for
          // it read as a dead pause. Instead, check each "animation" frame
          // against the known target (the prospective zoom, centered on the
          // tag) and flip the reveal once both are within 2%. animation-finish
          // stays as the fallback in case constraints keep the epsilon from
          // ever being hit (fitBoundsWithConstraints may clamp the target).
          const reveal = () => {
            clearPendingOverlayAnim();
            if (myToken === overlayEntryToken) {
              itemOverlayPending = false;
            }
          };
          const onFrame = () => {
            const z = viewport.getZoom(true);
            const c = viewport.getCenter(true);
            const zoomArrived = Math.abs(z / prospectiveZoom - 1) <= 0.02;
            const centerArrived = Math.hypot(c.x - vcTag.x, c.y - vcTag.y) <= 0.02 * boxW;
            if (zoomArrived && centerArrived) {
              reveal();
            }
          };
          const onFinish = () => {
            reveal();
          };
          // Plain addHandler on purpose: addOnceHandler wraps the callback, so
          // removeHandler(original) cannot detach it and cleanup would silently
          // fail. reveal()/clearPendingOverlayAnim remove both handlers.
          pendingOverlayAnim = {
            osd,
            handlers: [
              ["animation", onFrame],
              ["animation-finish", onFinish],
            ],
          };
          osd.addHandler("animation", onFrame);
          osd.addHandler("animation-finish", onFinish);

          itemOverlayPending = true;
          activateOverlay(predicted);
          controller.emit("zoom-to-bounds", {
            points: [{ index, point: { x: loc.position.x, y: loc.position.y } }],
          });
          return;
        }
      }
    }

    // No zoom (do-nothing case) or legacy: open immediately, visible, framed to
    // the live viewport.
    itemOverlayPending = false;
    activateOverlay(liveExtents());
  }

  function trackDetailDrawing(e) {
    // While the FrameX entry zoom is in flight, the hidden editor's mount-time
    // updatebbox echo must not emit move-to: moveTo pans/zooms IMMEDIATELY,
    // which teleports the viewport to the target and truncates the animation.
    // The animation is already heading to the same framing; the user cannot pan
    // the editor while it is pending (pointer-events: none).
    if (itemOverlayPending) return;
    if (!$itemOverlay) return;
    const item = $group.items[$itemOverlay];
    if (!item?.drawing?.clip?.drawing_scale) return;
    const loc = $group.locations[item.drawing.attachment_id];
    if (!loc) return;
    const { bbox } = e.detail;
    if (!bbox.width || !bbox.height) return;
    if (!viewer) return;
    if (isEqual(itemOverlayBbox, bbox)) return;
    itemOverlayBbox = bbox;

    const clip = item.drawing.clip;
    const m = multiplier(clip.drawing_scale);
    const sc = 300 / m;
    const cx = clip.drawing_offset?.x ?? 0;
    const cy = clip.drawing_offset?.y ?? 0;
    const dx = (cx - bbox.x) * sc;
    const dy = (bbox.y - cy) * sc;
    const x = clip.x - dx;
    const y = clip.y - (dy - clip.height);
    const width = bbox.width * sc;
    const height = bbox.height * sc;

    tick().then(() => {
      controller.emit("move-to", {
        index: visibleIndices[loc.page_id],
        bounds: { x: x, y: y, width, height },
      });
    });
  }

  function hideDetailDrawing() {
    // Cancel any pending post-zoom reveal so exiting mid-animation can't reopen.
    overlayEntryToken++;
    clearPendingOverlayAnim();
    itemOverlayPending = false;
    ioExtents = null;
    $itemOverlay = null;
    $itemOverlayMode = null;
  }

  function deleteFeatures(features) {
    if (!$itemOverlay) return;
    const item = $group.items[$itemOverlay];
    if (item?.data.reference_planes?.type !== "grid") return;

    const vrefplanes = features.filter((f) => f.type === "refplane" && f.class === "vertgrid");
    const hrefplanes = features.filter((f) => f.type === "refplane" && f.class === "horgrid");

    if (vrefplanes.length || hrefplanes.length) {
      const data = cloneDeep(item.data);
      const planes = data.reference_planes;

      let x_subdivisions = item.x_subdivisions;
      if (vrefplanes.length) {
        const v = gridlinePositions(planes, "vertical", item.cache.bbox.xmin);
        const toDelete = vrefplanes.map((p) => parseInt(p.id)).sort((a, b) => b - a);
        toDelete.forEach((i) => v.splice(i, 1));
        const lines = positionsToLines(v, "vertical", item.cache.bbox.xmin);
        planes.x_offset = lines.x_offset;
        planes.vertical = lines.vertical;
        x_subdivisions = lines.vertical.length;
      }

      let y_subdivisions = item.y_subdivisions;
      if (hrefplanes.length) {
        const v = gridlinePositions(planes, "horizontal", item.cache.bbox.ymin);
        const toDelete = hrefplanes.map((p) => parseInt(p.id)).sort((a, b) => b - a);
        toDelete.forEach((i) => v.splice(i, 1));
        const lines = positionsToLines(v, "horizontal", item.cache.bbox.ymin);
        planes.y_offset = lines.y_offset;
        planes.horizontal = lines.horizontal;
        y_subdivisions = lines.horizontal.length;
      }

      group.updateItem(item.id, { data, x_subdivisions, y_subdivisions });
    }
  }

  function followLink(page_id, idx) {
    const link = links[page_id]?.[idx];
    if (!link) return;
    if (link.url) {
      window.open(link.url, "_blank");
    } else if (link.dest_page_idx != undefined) {
      const linkPage = doc.data.pages[page_id];
      const destPageId = doc.data.pages.order.find((id) => {
        const page = doc.data.pages[id];
        return page.name === linkPage.name && page.page === link.dest_page_idx;
      });
      const destPage = doc.data.pages[destPageId];
      const pageIndex = visibleIndices[destPageId];
      if (!destPage || pageIndex == undefined) return;
      const sheet = $fileData[destPage.name]?.sheets[destPage.page];
      const bounds = linkPosition(link.dest, sheet);
      if (bounds) {
        controller.emit("zoom-to-bounds", {
          page: pageIndex,
          bounds,
        });
      }
    }
  }

  async function handleLeftUp(e) {
    if (!clicking) return;
    if (dragStart) dragStart = null;
    if (dragging) dragging = null;
    if (selecting) selecting = null;
    if (clipping) clipping = null;

    const { tilePosition } = e;

    const position = {
      x: tilePosition.x,
      y: tilePosition.y,
    };
    const page_id = visiblePages[tilePosition.index];
    const isPage = isLoadedPage(tilePosition.index);

    if ($currentTool === "select") {
      if (!isEmpty($selectedRecords)) $selectedRecords = {};
      const hb = eventObj(e);
      const ohb = overallEventObj(e);
      const hitbox = ohb || hb;
      if (hitbox) {
        const [type, id, idx] = hitbox.name.split("_");
        if (type !== "page-outline" && !isEmpty($selectedPages)) $selectedPages = {};

        if (type === "location" || type === "shapeoverlay") {
          if (e.originalEvent.shiftKey) {
            selectedLocations.select(id);
          } else if (discontiguousKey(e.originalEvent)) {
            if ($selectedLocations[id]) {
              selectedLocations.deselect(id);
            } else {
              selectedLocations.select(id);
            }
          } else {
            selectedLocations.selectOnly(id);
            if (!isEmpty($selectedDimensions)) {
              $selectedDimensions = {};
            }
          }
        } else if (type === "docdim") {
          if (e.originalEvent.shiftKey) {
            selectedDimensions.select(id);
          } else if (discontiguousKey(e.originalEvent)) {
            if ($selectedDimensions[id]) {
              selectedDimensions.deselect(id);
            } else {
              selectedDimensions.select(id);
            }
          } else {
            selectedDimensions.selectOnly(id);
            if (!isEmpty($selectedLocations)) {
              $selectedLocations = {};
            }
          }
        } else if (type === "annotation") {
          selectedAnnotations.deselect(id);
          if (hoveredAnnotation === id) {
            hoveredAnnotation = null;
          }
        } else if (type === "searchresult") {
          searchResults = searchResults.filter((r) => r.id !== id);
          if (hoveredSearchResult === id) {
            hoveredSearchResult = null;
          }
        } else if (type === "dwgbtn") {
          toggleLocationDrawing(id);
        } else if (type === "editbtn") {
          showDetailDrawing(id);
        } else if (type === "link") {
          followLink(page_id, idx);
        } else if (type === "page-outline") {
          $selectedLocations = {};
          hoveredLocations = {};
          if (e.originalEvent.shiftKey) {
            if (!selectedPageList.length) {
              selectedPages.selectOnly(id);
              lastSelectedPage = id;
            } else {
              const prev = lastSelectedPage || visiblePages[0];
              const prevIndex = visibleIndices[prev];
              const currentIndex = visibleIndices[id];
              const min = Math.min(prevIndex, currentIndex);
              const max = Math.max(prevIndex, currentIndex);
              const newSelected = range(min, max + 1).map((i) => visiblePages[i]);
              selectedPages.select(...newSelected);
              lastSelectedPage = id;
            }
          } else if (discontiguousKey(e.originalEvent)) {
            selectedPages.toggle(id);
            lastSelectedPage = id;
            hoveredPage = null;
          } else {
            selectedPages.selectOnly(id);
            lastSelectedPage = id;
          }
        }
      } else {
        if (!modifierKey(e.originalEvent)) {
          deselectAll();
        }
        if (!isEmpty(hoveredLocations)) hoveredLocations = {};
      }
    } else if ($currentTool === "dimension-linear" && isPage) {
      const page = doc.data.pages[page_id];
      if (!page?.scale && !$useDocMeasurementScale) {
        beginSettingPageScale([page_id]);
      } else if (multiclicks.clicks.length === 1 && multiclicks.clicks[0].index === tilePosition.index) {
        const scale = $useDocMeasurementScale ? $docMeasurementScale : page.scale;
        let pt;
        if (shiftDown) {
          const sp = multiclicks.clicks[0];
          const cp = tilePosition;
          const dx = Math.abs(cp.x - sp.x);
          const dy = Math.abs(cp.y - sp.y);
          pt = dx > dy ? { x: cp.x, y: sp.y, index: cp.index } : { x: sp.x, y: cp.y, index: cp.index };
        } else {
          pt = tilePosition;
        }

        if ($preserveDimensions) {
          const ds = dimSettings($group.data.settings);
          const dim = defaultDimension({
            group_id: $group.id,
            document_id: doc.id,
            index: dimensions.length,
            page_id,
            type: "aligned",
            scale: scale,
            data: {
              values: {
                length: lengthDim(multiclicks.clicks[0], pt, scale, ds, shiftDown),
              },
              points: [multiclicks.clicks[0], pt],
            },
            display_unit: $group.data.settings.display_unit,
            format: $group.data.settings.dimension_format,
            decimal_precision: $group.data.settings.dim_decimal_precision,
            fractional_precision: $group.data.settings.dim_fractional_precision,
            mm_precision: $group.data.settings.dim_mm_precision,
          });
          group.update([{ type: "document_dimension", action: "add", records: [dim] }]);
          if (!$documentViewShowDimensions) {
            alerts.add({ type: "warning", message: "Dimensions are currently hidden." });
          }
          multiclicks = { tool: "dimension-linear", clicks: [] };
        } else {
          multiclicks.clicks.push(pt);
          multiclicks = multiclicks;
        }
      } else {
        multiclicks = { tool: "dimension-linear", clicks: [tilePosition] };
      }
    } else if ($currentTool === "pick-scale-dim" && isPage) {
      const page = doc.data.pages[page_id];
      if (multiclicks.clicks.length === 1 && multiclicks.clicks[0].index === tilePosition.index) {
        let pt;
        if (shiftDown) {
          const sp = multiclicks.clicks[0];
          const cp = tilePosition;
          const dx = Math.abs(cp.x - sp.x);
          const dy = Math.abs(cp.y - sp.y);
          pt = dx > dy ? { x: cp.x, y: sp.y, index: cp.index } : { x: sp.x, y: cp.y, index: cp.index };
        } else {
          pt = tilePosition;
        }

        multiclicks.clicks.push(pt);
        multiclicks = multiclicks;

        const sheet = $fileData[page.name]?.sheets[page.page];
        scaleFromDimensionModal.open({ pageId: page_id, pts: multiclicks.clicks.slice(), sheet });
      } else {
        multiclicks = { tool: "pick-scale-dim", clicks: [tilePosition] };
      }
    } else if ($currentTool === "dimension-rectangular" && isPage) {
      const page = doc.data.pages[page_id];
      if (!page?.scale && !$useDocMeasurementScale) {
        beginSettingPageScale([page_id]);
      } else if (multiclicks.clicks.length === 1 && multiclicks.clicks[0].index === tilePosition.index) {
        const scale = $useDocMeasurementScale ? $docMeasurementScale : page.scale;
        if ($preserveDimensions) {
          const ds = dimSettings($group.data.settings);
          const d = rectangleDims(multiclicks.clicks[0], tilePosition, scale, ds);
          if (d) {
            const dim = defaultDimension({
              group_id: $group.id,
              document_id: doc.id,
              index: dimensions.length,
              page_id,
              type: "rectangular",
              scale: scale,
              data: {
                values: {
                  width: d[0],
                  height: d[1],
                  area: d[2],
                },
                points: [multiclicks.clicks[0], tilePosition],
              },
              display_unit: $group.data.settings.display_unit,
              format: $group.data.settings.dimension_format,
              decimal_precision: $group.data.settings.dim_decimal_precision,
              fractional_precision: $group.data.settings.dim_fractional_precision,
              mm_precision: $group.data.settings.dim_mm_precision,
            });
            group.update([{ type: "document_dimension", action: "add", records: [dim] }]);
            if (!$documentViewShowDimensions) {
              alerts.add({ type: "warning", message: "Dimensions are currently hidden." });
            }
          }
          multiclicks = { tool: "dimension-rectangular", clicks: [] };
        } else {
          multiclicks.clicks.push(tilePosition);
          multiclicks = multiclicks;
        }
      } else {
        multiclicks = { tool: "dimension-rectangular", clicks: [tilePosition] };
      }
    } else if (
      ["location-item", "location-collection"].includes($currentTool) &&
      tilePosition.index >= 0 &&
      isLoadedPage(tilePosition.index) &&
      adding
    ) {
      if (adding.clone) {
        const record_type = adding.record_type;
        const record = cloneDeep($group.items[adding.record_id]);
        record.id = crypto.randomUUID();
        record.mark = nextMark;
        const id = crypto.randomUUID();

        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type,
            record_id: record.id,
          },
          $documentViewLocationFilter,
        );

        if (UX_MODE === "shpx") {
          location.clip = defaultClip(location, page_id);
        }

        group.update([
          { type: record_type, action: "add", records: [record] },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      } else if (adding.record_id) {
        const id = crypto.randomUUID();

        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type: adding.record_type,
            record_id: adding.record_id,
          },
          $documentViewLocationFilter,
        );

        if (UX_MODE === "shpx") {
          location.clip = defaultClip(location, page_id);
        }

        group.addLocation(location);

        selectedLocations.select(id);

        if (adding.remaining) {
          const next = adding.remaining.shift();
          if (next) {
            adding.record_type = next.record_type;
            adding.record_id = next.record_id;
          } else {
            $currentTool = "select";
          }
        }
      } else {
        const record_type = adding.record_type;
        const p = record_type === "collection" ? { is_collection: true } : null;
        const records = record_type === "collection" ? collections : items;
        const item = defaultItem($group.id, $user.id, records, types, p, {
          default_item_mark: $org?.default_item_mark,
        });

        if (record_type === "collection" && $documentViewCollectionFilter) {
          if ($documentViewCollectionFilter.operator === "is_equal_to") {
            set(item, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
          }
        } else if ($documentViewItemFilter) {
          if ($documentViewItemFilter.operator === "is_equal_to") {
            set(item, $documentViewItemFilter.field, $documentViewItemFilter.value);
          }
        }

        const id = crypto.randomUUID();
        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type,
            record_id: item.id,
          },
          $documentViewLocationFilter,
        );

        if (UX_MODE === "shpx") {
          location.clip = defaultClip(location, page_id);
        }

        group.update([
          { type: "item", action: "add", records: [item], block: true },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      }
    } else if (
      $currentTool === "location-type" &&
      tilePosition.index >= 0 &&
      isLoadedPage(tilePosition.index) &&
      adding
    ) {
      if (adding.clone) {
        const record = cloneDeep($group.types[adding.record_id]);
        record.id = crypto.randomUUID();
        record.mark = nextMark;

        const id = crypto.randomUUID();
        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type: "type",
            record_id: record.id,
          },
          $documentViewLocationFilter,
        );

        group.update([
          { type: "type", action: "add", records: [record], block: true },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      } else if (adding.record_id) {
        const id = crypto.randomUUID();
        group.addLocation(
          defaultLocation(
            {
              id,
              group_id: $group.id,
              document_id: doc.id,
              index: locations.length,
              position,
              page_id,
              record_type: "type",
              record_id: adding.record_id,
            },
            $documentViewLocationFilter,
          ),
        );
        selectedLocations.select(id);

        if (adding.remaining) {
          const next = adding.remaining.shift();
          if (next) {
            adding.record_type = next.record_type;
            adding.record_id = next.record_id;
          } else {
            $currentTool = "select";
          }
        }
      } else {
        const type = defaultType($group.id, types);
        const id = crypto.randomUUID();

        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type: "type",
            record_id: type.id,
          },
          $documentViewLocationFilter,
        );

        if ($documentViewTypeFilter) {
          if ($documentViewTypeFilter.operator === "is_equal_to") {
            set(type, $documentViewTypeFilter.field, $documentViewTypeFilter.value);
          }
        }

        group.update([
          { type: "type", action: "add", records: [type], block: true },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      }
    } else if (
      $currentTool === "location-document_note" &&
      tilePosition.index >= 0 &&
      isLoadedPage(tilePosition.index) &&
      adding
    ) {
      if (adding.clone) {
        const record_type = adding.record_type;
        const record = cloneDeep($group.document_notes[adding.record_id]);

        record.id = crypto.randomUUID();
        record.mark = nextMark;
        const id = crypto.randomUUID();

        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type,
            record_id: record.id,
          },
          $documentViewLocationFilter,
        );

        group.update([
          { type: "document_note", action: "add", records: [record] },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      } else if (adding.record_id) {
        const id = crypto.randomUUID();

        group.addLocation(
          defaultLocation(
            {
              id,
              group_id: $group.id,
              document_id: doc.id,
              index: locations.length,
              type: "pin",
              position,
              page_id,
              record_type: adding.record_type,
              record_id: adding.record_id,
            },
            $documentViewLocationFilter,
          ),
        );
        selectedLocations.select(id);

        if (adding.remaining) {
          const next = adding.remaining.shift();
          if (next) {
            adding.record_type = next.record_type;
            adding.record_id = next.record_id;
          } else {
            $currentTool = "select";
          }
        }
      } else {
        const record_type = adding.record_type;
        const records = documentNotes;
        const note = defaultNote($group.id, records);

        if ($documentViewNoteFilter) {
          if ($documentViewNoteFilter.operator === "is_equal_to") {
            set(note, $documentViewNoteFilter.field, $documentViewNoteFilter.value);
          }
        }

        const id = crypto.randomUUID();
        const location = defaultLocation(
          {
            id,
            group_id: $group.id,
            document_id: doc.id,
            index: locations.length,
            position,
            page_id,
            record_type,
            record_id: note.id,
          },
          $documentViewLocationFilter,
        );

        group.update([
          { type: "document_note", action: "add", records: [note], block: true },
          { type: "location", action: "add", records: [location] },
        ]);

        selectedLocations.select(id);
      }
    } else {
      $currentTool = "select";
      adding = null;
    }
  }

  function handleCanvasDoubleClick(e) {
    if ($currentTool === "select") {
      const { tilePosition } = e;
      const hb = eventObj(e);
      const overallHb = overallEventObj(e);

      if (hb) {
        const [type, id] = hb.name.split("_");
        if (["location", "loc-clip", "shapeoverlay"].includes(type)) {
          showDetailDrawing(id);
          return;
        }
      }

      if (tilePosition.index >= 0) {
        controller.emit("zoom-to-page", tilePosition.index);
      } else if (overallHb) {
        const [type, id] = overallHb.name.split("_");
        if (type !== "page-outline") return;
        const pageIndex = visibleIndices[id];
        if (pageIndex != undefined) {
          controller.emit("zoom-to-page", pageIndex);
        }
      }
    }
  }

  function eventObj(e) {
    if (!e.hitContext) return null;
    const x = e.position.x * dpi;
    const y = e.position.y * dpi;
    const p = e.hitContext.getImageData(x, y, 1, 1).data;
    if (p[0] === 0 && p[1] === 0 && p[2] === 0) {
      return null;
    }

    const color = `rgb(${p[0]},${p[1]},${p[2]})`;
    const hash = hitHashes[e.tilePosition.index];
    if (!hash) return null;
    if (hash[color]) return hash[color];

    const near = nearColors(p[0], p[1], p[2], 1);
    const match = near.find((c) => hash[c]);
    if (match) return hash[match];

    return null;
  }

  function overallEventObj(e) {
    if (!e.overallHitContext) return null;
    const x = e.position.x * dpi;
    const y = e.position.y * dpi;
    const p = e.overallHitContext.getImageData(x, y, 1, 1).data;
    if (p[0] === 0 && p[1] === 0 && p[2] === 0) {
      return null;
    }

    const color = `rgb(${p[0]},${p[1]},${p[2]})`;
    const hash = overallHitHashes;
    if (!hash) return null;
    if (hash[color]) return hash[color];

    const near = nearColors(p[0], p[1], p[2], 1);
    const match = near.find((c) => hash[c]);
    if (match) return hash[match];

    return null;
  }

  function hitboxChange(current, hitbox) {
    if (!current && !hitbox) return false;
    if (!current && hitbox) return true;
    if (current && !hitbox) return true;
    if (current && hitbox) {
      return current.name !== hitbox.name;
    }
  }

  function unhover() {
    if (!isEmpty(hoveredLocations)) hoveredLocations = {};
    if (hoveredAnnotation) hoveredAnnotation = null;
    if (hoveredSearchResult) hoveredSearchResult = null;
    if (hoveredLink) hoveredLink = null;
    if (hoveredPage) hoveredPage = null;
    cursor = "default";
  }

  function defMouseCursor(type, id, index) {
    if ($currentTool === "select") {
      if (["loc-clip", "region-clip", "ai-clip"].includes(type)) {
        if (["0", "2"].includes(index)) {
          return "nesw-resize";
        } else if (["1", "3"].includes(index)) {
          return "nwse-resize";
        } else if (index === "outline") {
          return "move";
        }
      } else if (pointerbuttons[type]) {
        return "pointer";
      } else if (type === "shapeoverlay") {
        return "move";
      } else {
        return "default";
      }
    } else if ($currentTool === "ai-clip") {
      if (type === "ai-clip") {
        if (["0", "2"].includes(index)) {
          return "nesw-resize";
        } else if (["1", "3"].includes(index)) {
          return "nwse-resize";
        } else if (index === "outline") {
          return "move";
        }
      } else {
        return "default";
      }
    }

    return "default";
  }

  async function handleMousemove(e) {
    const { tilePosition } = e;

    if (tilePosition.index >= 0 && isLoadedPage(tilePosition.index)) {
      currentMousePos = tilePosition;
    } else {
      currentMousePos = null;
    }

    const hb = eventObj(e);
    const ohb = overallEventObj(e);
    const hitbox = ohb || hb;

    if (hitboxChange(currentHb, hitbox)) {
      currentHb = hitbox;
      unhover();
      if (hitbox) {
        const [type, id, index] = hitbox.name.split("_");
        cursor = defMouseCursor(type, id, index);

        if (type === "location" || type === "docdim") {
          hoveredLocations = { [id]: true };
        } else if (type === "annotation") {
          hoveredAnnotation = id;
        } else if (type === "searchresult") {
          hoveredSearchResult = id;
        } else if (type === "link") {
          hoveredLink = {
            page_id: id,
            link_index: parseInt(index, 10),
          };
        } else if (type === "page-outline") {
          hoveredPage = id;
        }
      }
    }
  }

  function handleLeftDown(e) {
    clicking = true;
    if (!viewer) return;
    const { position, tilePosition } = e;
    clickStart = {
      x: e.originalEvent.clientX,
      y: e.originalEvent.clientY,
    };
    if (
      ["location-item", "location-collection", "location-type", "location-document_note"].includes(
        $currentTool,
      )
    ) {
      if (tilePosition.index >= 0 && adding && isLoadedPage(tilePosition.index)) {
        const page_id = visiblePages[tilePosition.index];
        if ($currentTool === "location-collection" && blockFrameOnUnscaledPage(page_id)) {
          $currentTool = "select";
          adding = null;
          return;
        }
        const tpPos = { x: tilePosition.x, y: tilePosition.y };
        clipping = { start: position, end: position, tpStart: tpPos, tpEnd: tpPos };
      }
    } else if ($currentTool === "ai-clip" && tilePosition.index >= 0) {
      const hitbox = eventObj(e);

      if (hitbox && !disabled) {
        const [type, id, index] = hitbox.name.split("_");
        if (type === "ai-clip") {
          const tile = viewer.tile(tilePosition.index);
          if (!tile) return;
          const size = tile.getContentSize();
          if (index === "outline") {
            const vertex = clipVertex(aiClipRegion.clip, 3);
            const pageIndex = visibleIndices[aiClipRegion.page_id];
            const p = viewer.pixelCoords({ ...vertex, index: pageIndex });
            const delta = subtract(position, p);
            dragStart = {
              type: "ai-clip-outline",
              id,
              start: position,
              end: position,
              delta,
              hitbox,
              tile_index: tilePosition.index,
              tile_size: size,
            };
          } else {
            const vertex = clipVertex(aiClipRegion.clip, index);
            const pageIndex = visibleIndices[aiClipRegion.page_id];
            const p = viewer.pixelCoords({ ...vertex, index: pageIndex });
            if (p) {
              dragStart = {
                type: "ai-clip-vertex",
                index,
                start: position,
                end: position,
                delta: subtract(position, p),
                hitbox,
                tile_index: tilePosition.index,
                tile_size: size,
              };
            }
          }
        } else {
          selecting = { start: position, end: position, hitbox, page_id: visiblePages[tilePosition.index] };
        }
      } else {
        selecting = { start: position, end: position, hitbox, page_id: visiblePages[tilePosition.index] };
      }
    }

    if ($currentTool !== "select") return;
    const hb = eventObj(e);
    const ohb = overallEventObj(e);
    const hitbox = ohb || hb;
    if (hitbox && !disabled && !modifierKey(e.originalEvent)) {
      const [type, id, index] = hitbox.name.split("_");

      if (type === "location") {
        let selected;
        if ($selectedLocations[id]) {
          selected = cloneDeep($selectedLocations);
        } else {
          // $selectedLocations = {};
          $selectedPages = {};
          selected = { [id]: true };
        }

        const location = locationsObj[id];
        const pageIndex = visibleIndices[location.page_id];
        if (pageIndex === undefined) return;
        const p = viewer.pixelCoords({ ...location.position, index: pageIndex });
        if (p) {
          dragStart = {
            type: "location",
            start: position,
            end: position,
            delta: subtract(position, p),
            hitbox,
            location,
            selected,
            copy: altDown,
          };
        }
      } else if (type === "docdim") {
        let selected;
        if ($selectedDimensions[id]) {
          selected = cloneDeep($selectedDimensions);
        } else {
          $selectedPages = {};
          selected = { [id]: true };
        }

        const dim = dimsObj[id];
        const pageIndex = visibleIndices[dim?.page_id];
        if (pageIndex === undefined) return;
        const p1 = viewer.pixelCoords({ ...dim.data.points[0], index: pageIndex });
        const p2 = viewer.pixelCoords({ ...dim.data.points[1], index: pageIndex });
        if (p1 || p2) {
          const p = p1 || p2;
          dragStart = {
            type: "docdim",
            start: position,
            end: position,
            delta: subtract(position, p),
            hitbox,
            dimension: dim,
            selected,
            copy: altDown,
          };
        }
      } else if (type === "loc-clip") {
        const location = locationsObj[id];
        const pageIndex = visibleIndices[location.page_id];
        if (pageIndex === undefined) return;
        const tile = viewer.tile(pageIndex);
        if (!tile) return;
        const size = tile.getContentSize();
        if (index === "outline") {
          const p = viewer.pixelCoords({ ...location.position, index: pageIndex });
          if (p) {
            dragStart = {
              type: "loc-clip-outline",
              start: position,
              end: position,
              delta: subtract(position, p),
              hitbox,
              location,
              selected: { [id]: true },
              copy: altDown,
              tile_size: size,
            };
          }
        } else {
          const vertex = clipVertex(location.clip, index);
          const p = viewer.pixelCoords({ ...vertex, index: pageIndex });
          if (p) {
            dragStart = {
              type: "loc-clip-vertex",
              index,
              start: position,
              end: position,
              delta: subtract(position, p),
              hitbox,
              location,
              tile_size: size,
            };
          }
        }
      } else if (type === "region-clip") {
        const tile = viewer.tile(tilePosition.index);
        if (!tile) return;
        const size = tile.getContentSize();
        const clip = translateRectangle(pageRegions[id], size);
        if (index === "outline") {
          const vertex = clipVertex(clip, 3);
          const p = viewer.pixelCoords({ ...vertex, index: tilePosition.index });
          const delta = subtract(position, p);
          dragStart = {
            type: "region-clip-outline",
            id,
            start: position,
            end: position,
            delta,
            hitbox,
            tile_index: tilePosition.index,
          };
        } else {
          const vertex = clipVertex(clip, index);
          const p = viewer.pixelCoords({ ...vertex, index: tilePosition.index });
          const delta = subtract(position, p);
          dragStart = {
            type: "region-clip-vertex",
            id,
            index,
            start: position,
            end: position,
            delta,
            hitbox,
            tile_index: tilePosition.index,
          };
        }
      } else if (type === "ai-clip") {
        const tile = viewer.tile(tilePosition.index);
        if (!tile) return;
        const size = tile.getContentSize();
        if (index === "outline") {
          const vertex = clipVertex(aiClipRegion.clip, 3);
          const pageIndex = visibleIndices[aiClipRegion.page_id];
          const p = viewer.pixelCoords({ ...vertex, index: pageIndex });
          const delta = subtract(position, p);
          dragStart = {
            type: "ai-clip-outline",
            id,
            start: position,
            end: position,
            delta,
            hitbox,
            tile_index: tilePosition.index,
            tile_size: size,
          };
        } else {
          const vertex = clipVertex(aiClipRegion.clip, index);
          const pageIndex = visibleIndices[aiClipRegion.page_id];
          const p = viewer.pixelCoords({ ...vertex, index: pageIndex });
          if (p) {
            dragStart = {
              type: "ai-clip-vertex",
              index,
              start: position,
              end: position,
              delta: subtract(position, p),
              hitbox,
              tile_index: tilePosition.index,
              tile_size: size,
            };
          }
        }
      } else if (type === "page-outline") {
        const pageIndex = visibleIndices[id];
        const draggedPages = {
          ...$selectedPages,
          [id]: true,
        };
        const selectedIndices = Object.keys(draggedPages).map((id) => visibleIndices[id]);
        const droppableIndices = validDropIndices(selectedIndices, pageIndex, visiblePages.length);

        if (Object.keys(droppableIndices).length) {
          dragStart = {
            type: "pages",
            index: pageIndex,
            start: position,
            end: position,
            selectedIndices,
            draggedPages,
            droppableIndices,
          };
        }
      } else if (type === "shapeoverlay") {
        const location = locationsObj[id];

        if (location) {
          dragStart = {
            type: "shapeoverlay",
            location: location,
            start: position,
            end: position,
            delta: { x: 0, y: 0 },
          };
        }
      }
    } else if (
      tilePosition.index >= 0 &&
      $selectedPages[visiblePages[tilePosition.index]] &&
      !modifierKey(e.originalEvent)
    ) {
      const selectedIndices = Object.keys($selectedPages).map((id) => visibleIndices[id]);
      const droppableIndices = validDropIndices(selectedIndices, tilePosition.index, visiblePages.length);

      if (Object.keys(droppableIndices).length) {
        dragStart = {
          type: "pages",
          index: tilePosition.index,
          start: position,
          end: position,
          selectedIndices,
          draggedPages: { ...$selectedPages },
          droppableIndices,
        };
      }
    } else {
      selecting = { start: position, end: position, hitbox };
    }
  }

  function handleCanvasDrag(e) {
    if (clicking && clickStart) {
      const pos = {
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
      };
      if (distSq(pos, clickStart) < DRAG_THRESHOLD) {
        return;
      }
    }

    clicking = false;
    if (!viewer) return;
    const { position, tilePosition } = e;

    const isValidPos = tilePosition.index >= 0 && isLoadedPage(tilePosition.index);
    if (isValidPos) {
      currentMousePos = tilePosition;
    } else {
      currentMousePos = null;
    }

    if (
      ["location-item", "location-collection", "location-type", "location-document_note"].includes(
        $currentTool,
      )
    ) {
      if (isValidPos && adding) {
        if (clipping) {
          clipping.end = position;
          clipping.tpEnd = { x: tilePosition.x, y: tilePosition.y };
        }
      } else {
        clipping = null;
      }
    } else if ($currentTool === "ai-clip") {
      if (selecting) {
        const pageIndex = visibleIndices[selecting.page_id];
        const tile = viewer.tile(pageIndex);
        const size = tile.getContentSize();
        const max = viewer.pixelCoords({ ...size, index: pageIndex });
        const min = viewer.pixelCoords({ x: 0, y: 0, index: pageIndex });
        selecting.end = {
          x: Math.max(min.x, Math.min(position.x, max.x)),
          y: Math.max(min.y, Math.min(position.y, max.y)),
        };
        return;
      }
    }

    if (!["select", "ai-clip"].includes($currentTool)) return;

    if (selecting) {
      selecting.end = position;
      return;
    }

    if (dragStart) {
      if (!dragging) {
        dragging = { ...dragStart };
        overlayRefreshKey = !overlayRefreshKey;
      }

      dragging.end = position;

      if (dragging.type === "region-clip-outline") {
        const { delta, id, end, tile_index } = dragging;
        const clip = pageRegions[id];
        const pos = subtract(end, delta);
        const tile = viewer.tile(tile_index);
        const size = tile.getContentSize();
        const tc = viewer.tileCoords(pos, tile_index);
        const x = Math.max(0, Math.min(tc.x, size.x - clip.width));
        const y = Math.max(0, Math.min(tc.y, size.y - clip.height));
        const rx = x - size.x;
        const ry = y - size.y;

        tempPageRegion = { ...pageRegions[id], x: rx, y: ry };
      } else if (dragging.type === "region-clip-vertex") {
        const { delta, id, end, index, tile_index } = dragging;
        const tile = viewer.tile(tile_index);
        const size = tile.getContentSize();
        const clip = translateRectangle(pageRegions[id], size);
        const pos = subtract(end, delta);
        const tc = viewer.tileCoords(pos, tile_index);
        const x = Math.max(0, Math.min(tc.x, size.x));
        const y = Math.max(0, Math.min(tc.y, size.y));
        const to = { x, y };
        const d = draggedClip(clip, index, to);
        const rx = d.x - size.x;
        const ry = d.y - size.y;
        tempPageRegion = { ...d, x: rx, y: ry };
      } else if (dragging.type === "pages" && (isVirtual || hiddenPageList.length)) {
        cursor = "not-allowed";
      }

      return;
    }
  }

  async function updateClipItem(record, locUpdate, location) {
    const drawing = await imageDrawingFromClip(location, doc);
    if (!drawing) return;

    const recordType = location.record_type === "type" ? "type" : "item";
    const updates = [locUpdate, { type: recordType, id: record.id, prop: "drawing", value: drawing }];
    group.update(updates);
  }

  async function handleCanvasDragEnd(e) {
    if (!viewer) return;

    // Handle selecting finish
    if (selecting && $currentTool === "ai-clip") {
      const sStart = selecting.start;
      const sEnd = selecting.end;
      const start = viewer.tileCoords(selecting.start);
      const end = viewer.tileCoords(selecting.end);
      const tile = viewer.tile(start.index);
      const size = tile.getContentSize();

      const minX = Math.max(0, Math.min(start.x, end.x));
      const minY = Math.max(0, Math.min(start.y, end.y));
      const maxX = Math.min(size.x, Math.max(start.x, end.x));
      const maxY = Math.min(size.y, Math.max(start.y, end.y));

      const pageId = visiblePages[start.index];
      const page = doc.data.pages[pageId];

      const clip = {
        page_id: pageId,
        x: minX,
        y: minY,
        width: Math.max(maxX - minX, 10),
        height: Math.max(maxY - minY, 10),
        drawing_scale: page?.scale || null,
        drawing_offset: { x: 0, y: 0 },
      };

      aiClipRegion = {
        page_id: pageId,
        clip,
        tile_size: size,
        selection: {
          start: sStart,
          end: sEnd,
        },
      };
    } else if (selecting) {
      const minX = Math.min(selecting.start.x, selecting.end.x);
      const minY = Math.min(selecting.start.y, selecting.end.y);
      const maxX = Math.max(selecting.start.x, selecting.end.x);
      const maxY = Math.max(selecting.start.y, selecting.end.y);

      const locIds = locations
        .filter((loc) => {
          if ($hiddenLocations[loc.id]) return false;
          const visible = isLocationVisible(loc, $group, filters);
          if (!visible) return false;
          const index = visibleIndices[loc.page_id];
          if (index == null) return false;
          const p = viewer.pixelCoords({ ...loc.position, index });
          return p && p.x > minX && p.x < maxX && p.y > minY && p.y < maxY;
        })
        .map((l) => l.id);
      const dimIds = dimensions
        .filter((dim) => {
          if (!$documentViewShowDimensions) return false;
          const index = visibleIndices[dim.page_id];
          if (index == null) return false;
          const a = dim.data.points[0];
          const b = dim.data.points[1];
          const pa = viewer.pixelCoords({ ...a, index });
          const pb = viewer.pixelCoords({ ...b, index });
          if (!pa || !pb) return false;

          if (dim.type === "aligned") {
            return (
              (pa.x > minX && pa.x < maxX && pa.y > minY && pa.y < maxY) ||
              (pb.x > minX && pb.x < maxX && pb.y > minY && pb.y < maxY)
            );
          } else if (dim.type === "rectangular") {
            const pc = { x: pa.x, y: pb.y };
            const pd = { x: pb.x, y: pa.y };
            return (
              (pa.x > minX && pa.x < maxX && pa.y > minY && pa.y < maxY) ||
              (pb.x > minX && pb.x < maxX && pb.y > minY && pb.y < maxY) ||
              (pc.x > minX && pc.x < maxX && pc.y > minY && pc.y < maxY) ||
              (pd.x > minX && pd.x < maxX && pd.y > minY && pd.y < maxY)
            );
          }
        })
        .map((d) => d.id);

      const hitbox = eventObj(e);
      if (locIds.length) {
        if (e.shift) {
          if (hitbox && hitbox.name === selecting.hitbox?.name) {
            if (hitbox.name.startsWith("location")) {
              const m = hitbox.name.match(/location_(.+)/);
              selectedLocations.select(m[1]);
            }
          } else {
            selectedLocations.select(...locIds);
          }
        } else if (discontiguousKey(e.originalEvent)) {
          selectedLocations.deselect(...locIds);
        } else {
          selectedLocations.selectOnly(...locIds);
        }

        $selectedPages = {};
      }

      if (dimIds.length) {
        if (e.shift) {
          if (hitbox && hitbox.name === selecting.hitbox?.name) {
            if (hitbox.name.startsWith("docdim")) {
              const m = hitbox.name.match(/docdim_(.+)/);
              selectedDimensions.select(m[1]);
            }
          } else {
            selectedDimensions.select(...dimIds);
          }
        } else if (discontiguousKey(e.originalEvent)) {
          selectedDimensions.deselect(...dimIds);
        } else {
          selectedDimensions.selectOnly(...dimIds);
        }

        $selectedPages = {};
      }

      if ($showSearchResults && searchResults.length) {
        searchResults = searchResults.filter((r) => {
          const index = visibleIndices[r.page_id];
          const p = viewer.pixelCoords({ ...r.center, index });
          return !p || p.x < minX || p.x > maxX || p.y < minY || p.y > maxY;
        });
      }

      if (selectedAnnotationList.length) {
        const sel = selectedAnnotationList.filter((id) => {
          const a = annosObj[id];
          const pageId = visiblePages[a.page];
          const index = visibleIndices[pageId];
          const point = annotationPoint(a);
          if (!point) return false;
          const p = viewer.pixelCoords({ x: point.x, y: -point.y, index });
          return p && p.x > minX && p.x < maxX && p.y > minY && p.y < maxY;
        });

        if (sel.length) {
          selectedAnnotations.deselect(...sel);
        }
      }
    }

    // Handle clipping finish
    else if (adding && clipping) {
      const { tilePosition } = e;
      const d = distSq(clipping.end, clipping.start);

      const minX = Math.min(clipping.tpStart.x, tilePosition.x);
      const minY = Math.min(clipping.tpStart.y, tilePosition.y);
      const maxX = Math.max(clipping.tpStart.x, tilePosition.x);
      const maxY = Math.max(clipping.tpStart.y, tilePosition.y);

      const pos = midpoint(clipping.tpStart, tilePosition);

      const page_id = visiblePages[tilePosition.index];
      const page = doc.data.pages[page_id];
      const position = {
        x: pos.x,
        y: pos.y,
      };

      const clip =
        d > CLIP_THRESHOLD
          ? {
              x: minX,
              y: minY,
              width: Math.max(maxX - minX, 10),
              height: Math.max(maxY - minY, 10),
              drawing_scale: page?.scale || null,
              drawing_offset: { x: 0, y: 0 },
              // Distinguishes hand-drawn frames from detector output (see
              // src/lib/framex/locate.js) for canvas chrome. Lives on the
              // jsonb `clip` blob rather than `location.category`, which the
              // location-filter system (documentViewLocationFilter et al.)
              // already owns.
              ...(UX_MODE === "shpx" ? { manual: true } : {}),
            }
          : null;

      const dd = Math.sqrt(d);

      // A bit of a fudge: for small drags, OpenSeadragon will emit both a "click" AND a "dragend" event.
      // To keep from adding two locations (one for each event), we check whether the distance dragged
      // is larger than the default clickDistThreshold property (5).
      if (tilePosition.index >= 0 && isLoadedPage(tilePosition.index) && dd > 5) {
        if (adding.clone) {
          const record_type = adding.record_type;

          let record;
          if (record_type === "type") {
            record = cloneDeep($group.types[adding.record_id]);
          } else if (record_type === "document_note") {
            record = cloneDeep($group.document_notes[adding.record_id]);
          } else {
            record = cloneDeep($group.items[adding.record_id]);
          }

          record.id = crypto.randomUUID();
          record.mark = nextMark;
          const id = crypto.randomUUID();

          const location = defaultLocation(
            {
              id,
              group_id: $group.id,
              document_id: doc.id,
              index: locations.length,
              page_id,
              position,
              record_type,
              record_id: record.id,
              clip,
            },
            $documentViewLocationFilter,
          );

          if (record_type === "collection" && $documentViewCollectionFilter) {
            if ($documentViewCollectionFilter.operator === "is_equal_to") {
              set(record, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
            }
          } else if (record_type === "type" && $documentViewTypeFilter) {
            if ($documentViewTypeFilter.operator === "is_equal_to") {
              set(record, $documentViewTypeFilter.field, $documentViewTypeFilter.value);
            }
          } else if (record_type === "document_note" && $documentViewNoteFilter) {
            if ($documentViewNoteFilter.operator === "is_equal_to") {
              set(record, $documentViewNoteFilter.field, $documentViewNoteFilter.value);
            }
          } else if ($documentViewItemFilter) {
            if ($documentViewItemFilter.operator === "is_equal_to") {
              set(record, $documentViewItemFilter.field, $documentViewItemFilter.value);
            }
          }

          group.update([
            { type: record_type, action: "add", records: [record], block: true },
            { type: "location", action: "add", records: [location] },
          ]);

          selectedLocations.select(id);
        } else if (adding.record_id) {
          const id = crypto.randomUUID();
          group.addLocation(
            defaultLocation(
              {
                id,
                group_id: $group.id,
                document_id: doc.id,
                index: locations.length,
                type: "pin",
                page_id,
                position,
                record_type: adding.record_type,
                record_id: adding.record_id,
                clip,
              },
              $documentViewLocationFilter,
            ),
          );
          selectedLocations.select(id);

          if (adding.remaining) {
            const next = adding.remaining.shift();
            if (next) {
              adding.record_type = next.record_type;
              adding.record_id = next.record_id;
            } else {
              $currentTool = "select";
              adding = null;
            }
          }
        } else {
          const record_type = adding.record_type;
          if (record_type === "type") {
            const type = defaultType($group.id, types);

            if ($documentViewTypeFilter) {
              if ($documentViewTypeFilter.operator === "is_equal_to") {
                set(type, $documentViewTypeFilter.field, $documentViewTypeFilter.value);
              }
            }

            const id = crypto.randomUUID();

            const loc = defaultLocation(
              {
                id,
                group_id: $group.id,
                document_id: doc.id,
                index: locations.length,
                page_id,
                position,
                record_type,
                record_id: type.id,
                clip,
              },
              $documentViewLocationFilter,
            );

            group.update([
              { type: "type", action: "add", records: [type], block: true },
              { type: "location", action: "add", records: [loc] },
            ]);

            selectedLocations.select(id);
          } else if (record_type === "document_note") {
            const note = defaultNote($group.id, documentNotes);

            if ($documentViewNoteFilter) {
              if ($documentViewNoteFilter.operator === "is_equal_to") {
                set(note, $documentViewNoteFilter.field, $documentViewNoteFilter.value);
              }
            }

            const id = crypto.randomUUID();

            const loc = defaultLocation(
              {
                id,
                group_id: $group.id,
                document_id: doc.id,
                index: locations.length,
                page_id,
                position,
                record_type,
                record_id: note.id,
                clip,
              },
              $documentViewLocationFilter,
            );

            group.update([
              { type: "document_note", action: "add", records: [note], block: true },
              { type: "location", action: "add", records: [loc] },
            ]);

            selectedLocations.select(id);
          } else {
            const p = {
              replace_with_drawing: true,
              show_drawing_overlay: true,
            };
            if (record_type === "collection") {
              p.is_collection = true;
            }
            const records = record_type === "collection" ? collections : items;
            const item = defaultItem($group.id, $user.id, records, types, p, {
              default_item_mark: $org?.default_item_mark,
            });

            if (record_type === "collection" && $documentViewCollectionFilter) {
              if ($documentViewCollectionFilter.operator === "is_equal_to") {
                set(item, $documentViewCollectionFilter.field, $documentViewCollectionFilter.value);
              }
            } else if (record_type === "item" && $documentViewItemFilter) {
              if ($documentViewItemFilter.operator === "is_equal_to") {
                set(item, $documentViewItemFilter.field, $documentViewItemFilter.value);
              }
            }

            const id = crypto.randomUUID();
            const loc = defaultLocation(
              {
                id,
                group_id: $group.id,
                document_id: doc.id,
                index: locations.length,
                page_id,
                position,
                record_type,
                record_id: item.id,
                clip,
              },
              $documentViewLocationFilter,
            );

            const page = doc.data.pages[page_id];
            const file = $fileData[page.name];
            const ext = extension(page.name);

            if (clip && file && ext === "pdf") {
              const path = `${nameMinusExtension(page.name)}.${page.page}.tiff`;

              item.drawing = {
                attachment_id: id,
                id: page.name.split("/")[0],
                name: page.label || file.file.user_metadata?.name,
                type: "iiif",
                clip,
                path,
                extension: ext,
                properties: {
                  width: clip.width,
                  height: clip.height,
                },
              };

              group.update([
                { type: "item", action: "add", records: [item], block: true },
                { type: "location", action: "add", records: [loc] },
              ]);

              selectedLocations.select(id);
            } else if (clip) {
              const drawing = await imageDrawingFromClip(loc, doc);
              if (!drawing) return;
              item.drawing = drawing;
              group.update([
                { type: "item", action: "add", records: [item], block: true },
                { type: "location", action: "add", records: [loc] },
              ]);

              selectedLocations.select(id);
            } else {
              group.update([
                { type: "item", action: "add", records: [item], block: true },
                { type: "location", action: "add", records: [loc] },
              ]);

              selectedLocations.select(id);
            }
          }
        }
      }
    }

    // Handle dragging finish
    else if (dragging) {
      if (dragging.type === "location") {
        const { start, end, delta, selected } = dragging;
        const dropPos = subtract(end, delta);
        const ldelta = subtract(end, start);
        const dropTc = viewer.tileCoords(dropPos);
        const isViableDropLocation = dropTc.index >= 0;

        if (isViableDropLocation) {
          if (dragging.copy) {
            // FrameX: alt-dragging a frame creates a NEW frame — cloned
            // record, incremented mark, boundary and image carried to the
            // drop point — never a second marker of the same frame (counts
            // are not in play; same rule as clone-paste, LNA-31).
            const newItems = [];
            const collectionMarks =
              UX_MODE === "shpx" ? collections.map((c) => c.mark) : null;
            const records = Object.keys(selected)
              .filter((locid) => $group.locations[locid])
              .map((locid) => {
                const loc = cloneDeep($group.locations[locid]);
                const index = visibleIndices[loc.page_id];
                const pos = viewer.pixelCoords({
                  ...loc.position,
                  index,
                });
                let newPos = add(pos, ldelta);

                if (shiftDown) {
                  const dx = Math.abs(newPos.x - pos.x);
                  const dy = Math.abs(newPos.y - pos.y);
                  if (dx > dy) {
                    newPos = { x: newPos.x, y: pos.y };
                  } else {
                    newPos = { x: pos.x, y: newPos.y };
                  }
                }

                const newTc = viewer.tileCoords(newPos);
                loc.position = newTc;
                loc.page_id = visiblePages[newTc.index];
                if (loc.clip) {
                  const page = doc.data.pages[loc.page_id];
                  loc.clip.drawing_scale = page?.scale || null;
                }
                loc.id = crypto.randomUUID();

                // The record-existence guard keeps an orphaned marker (its
                // record deleted by legacy flows) from throwing and killing
                // the whole drop; orphans copy the legacy way instead.
                const sourceRecord =
                  UX_MODE === "shpx" && loc.record_type === "collection" && loc.clip
                    ? $group.items[loc.record_id]
                    : null;
                if (sourceRecord) {
                  const orig = $group.locations[locid];
                  const record = cloneDeep(sourceRecord);
                  record.id = crypto.randomUUID();
                  record.created_by = $profile.id;
                  record.created_at = new Date();
                  record.mark = incrementMarkCopy(record.mark || "", collectionMarks);
                  collectionMarks.push(record.mark);
                  // The boundary travels with the drag, keeping the marker's
                  // relative spot inside it (works across sheets too).
                  loc.clip = {
                    ...loc.clip,
                    x: loc.position.x - (orig.position.x - orig.clip.x),
                    y: loc.position.y - (orig.position.y - orig.clip.y),
                  };
                  if (record.drawing) {
                    record.drawing.attachment_id = loc.id;
                    if (record.drawing.clip) {
                      record.drawing.clip = {
                        ...record.drawing.clip,
                        x: loc.clip.x,
                        y: loc.clip.y,
                        width: loc.clip.width,
                        height: loc.clip.height,
                      };
                    }
                  }
                  loc.record_id = record.id;
                  newItems.push(record);
                }

                return loc;
              });

            group.update([
              ...(newItems.length
                ? [{ type: "collection", action: "add", records: newItems }]
                : []),
              { type: "location", action: "add", records },
            ]);
            selectedLocations.selectOnly(...records.map((r) => r.id));
          } else {
            const updates = Object.keys(selected)
              .filter((locid) => $group.locations[locid])
              .map((locid) => {
                const loc = $group.locations[locid];
                const index = visibleIndices[loc.page_id];
                const pos = viewer.pixelCoords({
                  ...loc.position,
                  index,
                });
                let newPos = add(pos, ldelta);

                if (shiftDown) {
                  const dx = Math.abs(newPos.x - pos.x);
                  const dy = Math.abs(newPos.y - pos.y);
                  if (dx > dy) {
                    newPos = { x: newPos.x, y: pos.y };
                  } else {
                    newPos = { x: pos.x, y: newPos.y };
                  }
                }

                const newTc = viewer.tileCoords(newPos);

                const page_id = visiblePages[newTc.index];
                const clip = loc.clip;
                if (clip) {
                  const page = doc.data.pages[page_id];
                  if (page?.scale && !clip.drawing_scale) {
                    clip.drawing_scale = page.scale;
                  }
                }

                return {
                  type: "location",
                  id: locid,
                  diff: {
                    page_id,
                    position: newTc,
                    clip,
                  },
                };
              });

            group.update(updates);
          }
        }
      } else if (dragging.type === "docdim") {
        const { start, end, delta, selected } = dragging;
        const ldelta = subtract(end, start);
        const originalIndex = visibleIndices[dragging.dimension.page_id];
        const apx = viewer.pixelCoords({ ...dragging.dimension.data.points[0], index: originalIndex });
        const bpx = viewer.pixelCoords({ ...dragging.dimension.data.points[1], index: originalIndex });
        const pta = add(apx, ldelta);
        const ptb = add(bpx, ldelta);
        const newATc = viewer.tileCoords(pta);
        const newBTc = viewer.tileCoords(ptb);
        const isViableDropLocation = newATc.index >= 0 && newBTc.index >= 0 && newATc.index === newBTc.index;

        if (isViableDropLocation) {
          if (dragging.copy) {
            const dims = Object.keys(selected)
              .filter((dimid) => $group.document_dimensions[dimid])
              .map((dimid) => {
                const dim = cloneDeep($group.document_dimensions[dimid]);
                const index = visibleIndices[dim.page_id];
                const a = viewer.pixelCoords({ ...dim.data.points[0], index });
                const b = viewer.pixelCoords({ ...dim.data.points[1], index });
                const newA = add(a, ldelta);
                const newB = add(b, ldelta);
                const newATc = viewer.tileCoords(newA);
                const newBTc = viewer.tileCoords(newB);
                dim.data.points[0] = newATc;
                dim.data.points[1] = newBTc;
                dim.page_id = visiblePages[newATc.index];
                dim.id = crypto.randomUUID();
                return dim;
              });

            group.update([{ type: "document_dimension", action: "add", records: dims }]);
          } else {
            const updates = Object.keys(selected)
              .filter((dimid) => $group.document_dimensions[dimid])
              .map((dimid) => {
                const dim = $group.document_dimensions[dimid];
                const index = visibleIndices[dim.page_id];
                const a = viewer.pixelCoords({ ...dim.data.points[0], index });
                const b = viewer.pixelCoords({ ...dim.data.points[1], index });
                const newA = add(a, ldelta);
                const newB = add(b, ldelta);
                const newATc = viewer.tileCoords(newA);
                const newBTc = viewer.tileCoords(newB);

                return {
                  type: "document_dimension",
                  id: dimid,
                  diff: {
                    page_id: visiblePages[newATc.index],
                    data: {
                      ...dim.data,
                      points: [newATc, newBTc],
                    },
                  },
                };
              });

            group.update(updates);
          }
        }
      } else if (dragging.type === "loc-clip-outline") {
        const { location, end, delta } = dragging;
        const pos = subtract(end, delta);
        const lindex = visibleIndices[location.page_id];
        const tc = viewer.tileCoords(pos, lindex);

        const minX = location.position.x - location.clip.x;
        const minY = location.position.y - location.clip.y;
        const maxX = tc.width - (location.clip.x + location.clip.width - location.position.x);
        const maxY = tc.height - (location.clip.y + location.clip.height - location.position.y);
        const x = Math.max(minX, Math.min(tc.x, maxX));
        const y = Math.max(minY, Math.min(tc.y, maxY));
        const td = subtract({ x, y }, location.position);

        const loc = $group.locations[location.id];
        const d = {
          ...loc.clip,
          x: loc.clip.x + td.x,
          y: loc.clip.y + td.y,
          width: loc.clip.width,
          height: loc.clip.height,
        };
        if (!d.drawing_scale) {
          const page = doc.data.pages[loc.page_id];
          d.drawing_scale = page?.scale || null;
        }

        let record;
        if (loc.record_type === "type") {
          record = $group.types[loc.record_id];
        } else if (["item", "collection"].includes(loc.record_type)) {
          record = $group.items[loc.record_id];
        }

        if (dragging.copy) {
          const newLoc = cloneDeep(loc);
          newLoc.id = crypto.randomUUID();
          newLoc.page_id = visiblePages[tc.index];
          newLoc.position = { x, y };
          newLoc.clip = d;

          // FrameX: an alt-drag of a frame creates a NEW frame from the
          // boundary exactly as from the marker (LNA-31) — never a second
          // marker of the same frame. Same clone recipe as the marker drag.
          if (UX_MODE === "shpx" && newLoc.record_type === "collection" && newLoc.clip && record) {
            const clone = cloneDeep(record);
            clone.id = crypto.randomUUID();
            clone.created_by = $profile.id;
            clone.created_at = new Date();
            clone.mark = incrementMarkCopy(
              clone.mark || "",
              collections.map((c) => c.mark),
            );
            if (clone.drawing) {
              clone.drawing.attachment_id = newLoc.id;
              if (clone.drawing.clip) {
                clone.drawing.clip = {
                  ...clone.drawing.clip,
                  x: d.x,
                  y: d.y,
                  width: d.width,
                  height: d.height,
                };
              }
            }
            newLoc.record_id = clone.id;

            group.update([
              { type: "collection", action: "add", records: [clone] },
              { type: "location", action: "add", records: [newLoc] },
            ]);
            selectedLocations.selectOnly(newLoc.id);
          } else {
            group.update([{ type: "location", action: "add", records: [newLoc] }]);
            selectedLocations.selectOnly(newLoc.id);
          }
        } else {
          const locUpdate = {
            type: "location",
            id: location.id,
            diff: {
              clip: d,
              page_id: visiblePages[tc.index],
              position: { x, y },
            },
          };
          if (record?.drawing?.attachment_id === loc.id) {
            const drawing = cloneDeep(record.drawing);
            drawing.clip = d;

            const recordType = loc.record_type === "type" ? "type" : "item";
            const updates = [locUpdate, { type: recordType, id: record.id, prop: "drawing", value: drawing }];
            group.update(updates);
          } else {
            group.update([locUpdate]);
          }
        }
      } else if (dragging.type === "loc-clip-vertex") {
        const { location, index, start, end, delta } = dragging;
        const pos = subtract(end, delta);
        const lindex = visibleIndices[location.page_id];
        const tc = viewer.tileCoords(pos, lindex);

        const x = Math.max(0, Math.min(tc.x, tc.width));
        const y = Math.max(0, Math.min(tc.y, tc.height));
        const to = { x, y };

        const loc = $group.locations[location.id];

        const d = draggedClip(loc.clip, index, to);

        if (d.drawing_scale && d.drawing_offset && !isEqual(d.drawing_offset, { x: 0, y: 0 })) {
          const st = subtract(start, delta);
          const from = viewer.tileCoords(st, lindex);
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const m = multiplier(d.drawing_scale);
          const sc = m / 300;
          const idx = parseInt(index);

          if ([0, 3].includes(idx)) {
            d.drawing_offset.x += dx * sc;
          }

          if ([0, 1].includes(idx)) {
            d.drawing_offset.y -= dy * sc;
          }
        }

        if (!d.drawing_scale) {
          const page = doc.data.pages[loc.page_id];
          d.drawing_scale = page?.scale || null;
        }

        let record;
        if (loc.record_type === "type") {
          record = $group.types[loc.record_id];
        } else if (["item", "collection"].includes(loc.record_type)) {
          record = $group.items[loc.record_id];
        }
        const locUpdate = {
          type: "location",
          id: location.id,
          diff: {
            clip: d,
            page_id: visiblePages[tc.index],
          },
        };
        if (record?.drawing?.attachment_id === loc.id) {
          const pageId = visiblePages[lindex];
          const page = doc.data.pages[pageId];
          const ext = extension(page.name);

          if (ext === "pdf") {
            const drawing = cloneDeep(record.drawing);
            drawing.clip = d;
            drawing.properties.width = d.width;
            drawing.properties.height = d.height;

            const recordType = loc.record_type === "type" ? "type" : "item";
            const updates = [locUpdate, { type: recordType, id: record.id, prop: "drawing", value: drawing }];
            group.update(updates);
          } else {
            await updateClipItem(record, locUpdate, loc);
          }
        } else {
          group.update([locUpdate]);
        }
      } else if (dragging.type === "ai-clip-outline") {
        const { tile_index, end, delta } = dragging;
        const pos = subtract(end, delta);
        const tc = viewer.tileCoords(pos, tile_index);
        const size = dragging.tile_size;
        const td = subtract(tc, aiClipRegion.clip);
        const translated = translateRectangle(aiClipRegion.clip, td);
        const constrained = constrainClip(translated, 0, 0, size.x, size.y);
        aiClipRegion.clip = constrained;
      } else if (dragging.type === "ai-clip-vertex") {
        const { tile_index, index, end, delta } = dragging;
        const pos = subtract(end, delta);
        const tc = viewer.tileCoords(pos, tile_index);
        const x = Math.max(0, Math.min(tc.x, tc.width));
        const y = Math.max(0, Math.min(tc.y, tc.height));
        const to = { x, y };
        const d = draggedClip(aiClipRegion.clip, index, to);
        aiClipRegion.clip = d;
      } else if (dragging.type === "pages" && !isVirtual && hiddenPageList.length === 0) {
        const { end, droppableIndices, draggedPages } = dragging;
        const nearest = viewer.nearestTile(end);

        const before = end.x <= nearest.center.x;
        const visibleGapIndex = before ? nearest.index : nearest.index + 1;

        if (!droppableIndices[visibleGapIndex]) return null;

        const gapId = visiblePages[visibleGapIndex];
        const gapIndex = gapId ? doc.data.pages.indices[gapId] : doc.data.pages.order.length;

        const newOrder = doc.data.pages.order.filter((id) => !draggedPages[id]);
        const dragged = Object.keys(draggedPages);

        const [newTileSources, draggedTileSources] = tileSources.reduce(
          (acc, ts, idx) => {
            const id = visiblePages[idx];
            if (draggedPages[id]) {
              acc[1].push(ts);
            } else {
              acc[0].push(ts);
            }
            return acc;
          },
          [[], []],
        );
        const ntsIds = visiblePages.filter((id) => !draggedPages[id]);

        if (gapIndex === doc.data.pages.order.length) {
          newOrder.push(...dragged);
        } else {
          const dragTo = doc.data.pages.order[gapIndex];
          const dropIndex = newOrder.indexOf(dragTo);
          newOrder.splice(dropIndex, 0, ...dragged);
        }

        if (visibleGapIndex === visiblePages.length) {
          newTileSources.push(...draggedTileSources);
        } else {
          const dragTo = visiblePages[visibleGapIndex];
          const dropIndex = ntsIds.indexOf(dragTo);
          newTileSources.splice(dropIndex, 0, ...draggedTileSources);
        }

        const data = cloneDeep(doc.data);
        sortListBy(data.pages, newOrder);

        group.updateDocument(doc.id, { data });
        tileSources = newTileSources;
      } else if (dragging.type === "region-clip-outline") {
        const { delta, id, end, tile_index } = dragging;
        const clip = pageRegions[id];
        const pos = subtract(end, delta);
        const tile = viewer.tile(tile_index);
        const size = tile.getContentSize();
        const tc = viewer.tileCoords(pos, tile_index);
        const x = Math.max(0, Math.min(tc.x, size.x - clip.width));
        const y = Math.max(0, Math.min(tc.y, size.y - clip.height));
        const rx = x - size.x;
        const ry = y - size.y;

        pageRegions[id] = { ...pageRegions[id], x: rx, y: ry };
        tempPageRegion = null;
      } else if (dragging.type === "region-clip-vertex") {
        const { delta, id, end, index, tile_index } = dragging;
        const tile = viewer.tile(tile_index);
        const size = tile.getContentSize();
        const clip = translateRectangle(pageRegions[id], size);
        const pos = subtract(end, delta);
        const tc = viewer.tileCoords(pos, tile_index);
        const x = Math.max(0, Math.min(tc.x, size.x));
        const y = Math.max(0, Math.min(tc.y, size.y));
        const to = { x, y };
        const d = draggedClip(clip, index, to);
        const rx = d.x - size.x;
        const ry = d.y - size.y;

        pageRegions[id] = { ...d, x: rx, y: ry };
        tempPageRegion = null;
      } else if (dragging.type === "ai-clip-outline") {
      } else if (dragging.type === "ai-clip-vertex") {
      } else if (dragging.type === "shapeoverlay") {
        const loc = dragging.location;
        const pageIndex = visibleIndices[loc.page_id];
        const rec = locRecord(loc, $group);
        if (pageIndex != null && rec) {
          const { width, height, xmin, ymin } = rec.cache.bbox;
          const drawing = cloneDeep(rec.drawing);
          const start = viewer.tileCoords(dragging.start, pageIndex);
          const end = viewer.tileCoords(dragging.end, pageIndex);
          const delta = subtract(end, start);
          const m = multiplier(rec.drawing.clip.drawing_scale);
          const sc = m / 300;
          const sd = { x: delta.x * sc, y: -delta.y * sc };
          const nx = (drawing.clip.drawing_offset?.x || 0) - sd.x;
          const ny = (drawing.clip.drawing_offset?.y || 0) - sd.y;
          const clipWd = drawing.clip.width * sc;
          const clipHt = drawing.clip.height * sc;
          const minX = -(clipWd - width);
          const minY = -(clipHt - height);

          drawing.clip.drawing_offset = {
            x: Math.max(minX + xmin, Math.min(nx, xmin)),
            y: Math.max(minY + ymin, Math.min(ny, ymin)),
          };

          group.update([
            {
              type: "item",
              id: rec.id,
              diff: { drawing },
            },
            {
              type: "location",
              id: loc.id,
              diff: { clip: drawing.clip },
            },
          ]);
        }
      }
    }

    dragStart = null;
    dragging = null;
    selecting = null;
    clipping = null;
  }

  function gotoNext() {
    // slide = "left";
    navigate(`./${nextDoc.id}`);
  }

  function gotoPrev() {
    // slide = "right";
    navigate(`./${prevDoc.id}`);
  }

  function gotoFirstLoc() {
    if (!location) return;
    const page = visiblePages.find((id) => indexedLocations[id]?.length);
    if (!page) return;
    const firstLoc = indexedLocations[page][0];
    const pc = viewer.pixelCoords({
      ...firstLoc.position,
      index: visibleIndices[firstLoc.page_id],
    });
    const isVisible = viewer.isPointVisible(pc);
    selectedLocations.selectOnly(firstLoc.id);
    if (!isVisible) {
      controller.emit("zoom-to-page", visibleIndices[firstLoc.page_id]);
    }
  }

  function gotoNextLoc() {
    if (!nextLoc) return;
    const pc = viewer.pixelCoords({
      ...nextLoc.position,
      index: visibleIndices[nextLoc.page_id],
    });
    const isVisible = viewer.isPointVisible(pc);
    selectedLocations.selectOnly(nextLoc.id);
    if (!isVisible) {
      controller.emit("zoom-to-page", visibleIndices[nextLoc.page_id]);
    }
  }

  function gotoPrevLoc() {
    if (!prevLoc) return;
    const pc = viewer.pixelCoords({
      ...prevLoc.position,
      index: visibleIndices[prevLoc.page_id],
    });
    const isVisible = viewer.isPointVisible(pc);
    selectedLocations.selectOnly(prevLoc.id);
    if (!isVisible) {
      controller.emit("zoom-to-page", visibleIndices[prevLoc.page_id]);
    }
  }

  function nudgeLocations(direction) {
    const nudgeDistance = 100;
    const updates = selectedLocationList
      .map((loc) => {
        const page = doc.data.pages[loc.page_id];
        const index = visibleIndices[loc.page_id];
        if (!page || index == null) return null;
        const tile = viewer.tile(index);
        const size = tile.getContentSize();

        const position = cloneDeep(loc.position);
        let x = position?.x || 0;
        let y = position?.y || 0;
        if (direction === "up") {
          y = Math.max(0, y - nudgeDistance);
        } else if (direction === "left") {
          x = Math.max(0, x - nudgeDistance);
        } else if (direction === "down") {
          y = Math.min(size.y, y + nudgeDistance);
        } else if (direction === "right") {
          x = Math.min(size.x, x + nudgeDistance);
        }
        return {
          type: "location",
          id: loc.id,
          diff: {
            position: { x, y },
          },
        };
      })
      .filter((u) => u);

    if (updates.length) {
      group.update(updates);
    }
  }

  // shpx+framex-only: a location-collection frame is about to be placed
  // (click or drag-start) on a page with no scale. Intercepted at the very
  // start of the gesture -- before `clipping` is ever set -- so no drag
  // preview or partial frame is created; the tool exits and FrameX Step 1's
  // scale flow opens for that page instead. Returns false (no-op) on every
  // legacy / non-FrameX / already-scaled path, so the caller falls through.
  function blockFrameOnUnscaledPage(pageId) {
    if (UX_MODE !== "shpx" || $leftPanelMode !== "framex") return false;
    if (isScaled(doc.data.pages[pageId])) return false;

    selectedPages.selectOnly(pageId);
    forceScaleFor.set({ pageId });
    return true;
  }

  function beginSettingPageScale(pageIds) {
    pagesToSetScale = pageIds;
    setPageScaleModal.open();
  }

  function setPageScale() {
    const data = cloneDeep(doc.data);
    pagesToSetScale.forEach((pageId) => {
      const page = data.pages[pageId];
      if (page) {
        page.scale = pageScaleValue;
      }
    });
    group.updateDocument(doc.id, { data });
    setPageScaleModal.close();
  }

  function beginAdding(e) {
    const { record_type, record_id } = e.detail;
    $currentTool = `location-${record_type}`;
    adding = {
      record_type,
      record_id,
    };
  }

  function beginCloning(e) {
    const { record_type, record_id } = e.detail;
    $currentTool = `location-${record_type}`;
    adding = {
      record_type,
      record_id,
      clone: true,
    };
  }

  function defaultClip(loc, page_id) {
    const page = doc.data.pages[page_id];

    const clip = {
      x: loc.position.x - 150,
      y: loc.position.y - 150,
      width: 300,
      height: 300,
      drawing_scale: page?.scale || null,
      drawing_offset: { x: 0, y: 0 },
      // Click-to-place path for FrameX chrome; every caller is already
      // gated on UX_MODE === "shpx". handleCanvasDragEnd sets the same flag
      // for the drag-to-create path, where the gate has to be inline.
      manual: true,
    };

    return clip;
  }

  function beginPlacingSelected(e) {
    const remaining = Object.keys($selectedRecords)
      .map((id) => {
        if ($group.items[id]) {
          const item = $group.items[id];
          if (item.is_collection) {
            return { record_type: "collection", record_id: id, index: $group.items.indices[id] };
          } else {
            return { record_type: "item", record_id: id, index: $group.items.indices[id] };
          }
        } else if ($group.types[id]) {
          return { record_type: "type", record_id: id, index: $group.types.indices[id] };
        } else if ($group.document_notes[id]) {
          return { record_type: "document_note", record_id: id, index: $group.document_notes.indices[id] };
        }
      })
      .filter((r) => r)
      .sort((a, b) => a.index - b.index);
    if (!remaining.length) return;

    const first = remaining.shift();
    $currentTool = `location-${first.record_type}`;
    adding = {
      record_type: first.record_type,
      record_id: first.record_id,
      remaining,
    };
  }

  function handleLocationToolClick() {
    adding = {
      record_type: $currentTool.split("-")[1],
      record_id: null,
    };
  }

  function beginPickingScaleDim() {
    $currentTool = "pick-scale-dim";
  }

  function confirmRemoveLocations() {
    locationsToDelete = selectedLocationList.map((l) => l.id);
    dimensionsToDelete = selectedDimensionList.map((d) => d.id);
    confirmRemoveLocationsModal.open();
  }

  async function removeLocations() {
    $selectedLocations = {};
    $selectedDimensions = {};
    const updates = [];
    if (dimensionsToDelete.length) {
      updates.push({ type: "document_dimension", action: "remove", ids: dimensionsToDelete });
    }

    if (locationsToDelete.length) {
      updates.push({ type: "location", action: "remove", ids: locationsToDelete });
    }

    if (updates.length) {
      group.update(updates);
    }
  }

  function confirmRemoveNote(e) {
    const { record_id } = e.detail;
    notesToDelete = [record_id];
    confirmRemoveNotesModal.open();
  }

  function removeNotes() {
    group.removeDocumentNote(...notesToDelete);
    notesToDelete = [];
  }

  function hideLocations() {
    const selectedLocs = selectedLocationList.map((l) => l.id);
    hiddenLocations.select(...selectedLocs);
    $selectedLocations = {};
  }

  function selectSimilarLocations() {
    const selectedRecords = selectedLocationList
      .map((l) => l.record_id)
      .reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
    const similarLocs = locations
      .filter((l) => {
        const page = doc.data.pages[l.page_id];
        return (
          page &&
          !page.hidden &&
          !$hiddenPages[l.page_id] &&
          selectedRecords[l.record_id] &&
          !$hiddenLocations[l.id]
        );
      })
      .map((l) => l.id);
    selectedLocations.selectOnly(...similarLocs);
  }

  function locPoint(locationOrDim) {
    const isDimension = $group.document_dimensions[locationOrDim.id] != null;
    if (isDimension) {
      const a = locationOrDim.data.points[0];
      const b = locationOrDim.data.points[1];
      return midpoint(a, b);
    }

    return locationOrDim.position;
  }

  function alignLocationsHorizontal() {
    const locsByPage = [...selectedLocationList, ...selectedDimensionList].reduce((acc, l) => {
      if (!acc[l.page_id]) acc[l.page_id] = [];
      acc[l.page_id].push(l);
      return acc;
    }, {});

    const updates = [];
    Object.values(locsByPage).forEach((locList) => {
      if (locList.length > 1) {
        const y = locList.map((l) => locPoint(l).y);
        const avgY = y.reduce((acc, v) => acc + v, 0) / y.length;
        updates.push(
          ...locList.map((l) => {
            const isDimension = $group.document_dimensions[l.id] != null;

            if (isDimension) {
              const a = l.data.points[0];
              const b = l.data.points[1];
              const mY = (a.y + b.y) / 2;
              const dY = avgY - mY;

              return {
                type: "document_dimension",
                id: l.id,
                diff: {
                  data: {
                    ...l.data,
                    points: [
                      { x: a.x, y: a.y + dY },
                      { x: b.x, y: b.y + dY },
                    ],
                  },
                },
              };
            } else {
              return {
                type: "location",
                id: l.id,
                diff: {
                  position: {
                    x: l.position.x,
                    y: avgY,
                  },
                },
              };
            }
          }),
        );
      }
    });

    if (updates.length) {
      group.update(updates);
    }
  }

  function alignLocationsVertical() {
    const locsByPage = [...selectedLocationList, ...selectedDimensionList].reduce((acc, l) => {
      if (!acc[l.page_id]) acc[l.page_id] = [];
      acc[l.page_id].push(l);
      return acc;
    }, {});

    const updates = [];
    Object.values(locsByPage).forEach((locList) => {
      if (locList.length > 1) {
        const x = locList.map((l) => locPoint(l).x);
        const avgX = x.reduce((acc, v) => acc + v, 0) / x.length;
        updates.push(
          ...locList.map((l) => {
            const isDimension = $group.document_dimensions[l.id] != null;

            if (isDimension) {
              const a = l.data.points[0];
              const b = l.data.points[1];
              const mX = (a.x + b.x) / 2;
              const dX = avgX - mX;

              return {
                type: "document_dimension",
                id: l.id,
                diff: {
                  data: {
                    ...l.data,
                    points: [
                      { x: a.x + dX, y: a.y },
                      { x: b.x + dX, y: b.y },
                    ],
                  },
                },
              };
            } else {
              return {
                type: "location",
                id: l.id,
                diff: {
                  position: {
                    x: avgX,
                    y: l.position.y,
                  },
                },
              };
            }
          }),
        );
      }
    });

    if (updates.length) {
      group.update(updates);
    }
  }

  function locWidth(location) {
    const isDimension = $group.document_dimensions[location.id] != null;
    if (isDimension) {
      const a = location.data.points[0];
      const b = location.data.points[1];
      return Math.abs(b.x - a.x);
    }
    return 0;
  }

  function locHeight(location) {
    const isDimension = $group.document_dimensions[location.id] != null;
    if (isDimension) {
      const a = location.data.points[0];
      const b = location.data.points[1];
      return Math.abs(b.y - a.y);
    }
    return 0;
  }

  function distributeLocationsHorizontal() {
    const locsByPage = [...selectedLocationList, ...selectedDimensionList].reduce((acc, l) => {
      if (!acc[l.page_id]) acc[l.page_id] = [];
      acc[l.page_id].push(l);
      return acc;
    }, {});

    const updates = [];
    Object.values(locsByPage).forEach((locList) => {
      if (locList.length > 2) {
        locList.sort((a, b) => {
          const ap = locPoint(a);
          const bp = locPoint(b);
          return ap.x - bp.x;
        });
        const first = locList[0];
        const middle = locList.slice(1, locList.length - 1);
        const last = locList[locList.length - 1];

        const totalWidth = locList.reduce((acc, l) => acc + locWidth(l), 0);
        const containerWidth =
          locPoint(last).x + locWidth(last) / 2 - (locPoint(first).x - locWidth(first) / 2);
        const spacing = (containerWidth - totalWidth) / (locList.length - 1);

        let lastEdge = locPoint(first).x + locWidth(first) / 2;
        middle.forEach((l, i) => {
          const isDimension = $group.document_dimensions[l.id] != null;
          const w = locWidth(l);
          const x = lastEdge + spacing + w / 2;
          const p = locPoint(l);
          const dx = x - p.x;

          if (isDimension) {
            const a = l.data.points[0];
            const b = l.data.points[1];
            updates.push({
              type: "document_dimension",
              id: l.id,
              diff: {
                data: {
                  ...l.data,
                  points: [
                    { x: a.x + dx, y: a.y },
                    { x: b.x + dx, y: b.y },
                  ],
                },
              },
            });
          } else {
            updates.push({
              type: "location",
              id: l.id,
              diff: {
                position: {
                  x: p.x + dx,
                  y: l.position.y,
                },
              },
            });
          }

          lastEdge = x + w / 2;
        });
      }
    });

    if (updates.length) {
      group.update(updates);
    }
  }

  function distributeLocationsVertical() {
    const locsByPage = [...selectedLocationList, ...selectedDimensionList].reduce((acc, l) => {
      if (!acc[l.page_id]) acc[l.page_id] = [];
      acc[l.page_id].push(l);
      return acc;
    }, {});

    const updates = [];
    Object.values(locsByPage).forEach((locList) => {
      if (locList.length > 2) {
        locList.sort((a, b) => {
          const ap = locPoint(a);
          const bp = locPoint(b);
          return ap.y - bp.y;
        });
        const first = locList[0];
        const middle = locList.slice(1, locList.length - 1);
        const last = locList[locList.length - 1];

        const totalHeight = locList.reduce((acc, l) => acc + locHeight(l), 0);
        const containerHeight =
          locPoint(last).y + locHeight(last) / 2 - (locPoint(first).y - locHeight(first) / 2);
        const spacing = (containerHeight - totalHeight) / (locList.length - 1);

        let lastEdge = locPoint(first).y + locHeight(first) / 2;
        middle.forEach((l, i) => {
          const isDimension = $group.document_dimensions[l.id] != null;
          const h = locHeight(l);
          const y = lastEdge + spacing + h / 2;
          const p = locPoint(l);
          const dy = y - p.y;

          if (isDimension) {
            const a = l.data.points[0];
            const b = l.data.points[1];
            updates.push({
              type: "document_dimension",
              id: l.id,
              diff: {
                data: {
                  ...l.data,
                  points: [
                    { x: a.x, y: a.y + dy },
                    { x: b.x, y: b.y + dy },
                  ],
                },
              },
            });
          } else {
            updates.push({
              type: "location",
              id: l.id,
              diff: {
                position: {
                  x: l.position.x,
                  y: p.y + dy,
                },
              },
            });
          }

          lastEdge = y + h / 2;
        });
      }
    });

    if (updates.length) {
      group.update(updates);
    }
  }

  function showHiddenLocations() {
    $hiddenLocations = {};
  }

  async function showHiddenPages() {
    $hiddenPages = {};
    await tick();
    initTileSources();
    updateLinks();
  }

  function showHiddenPageModal() {
    hiddenPageStatus = doc.data.pages.order
      .filter((id) => !doc.data.pages[id]?.hidden)
      .map((id) => {
        const page = doc.data.pages[id];
        const sheet = $fileData[page.name]?.sheets[page.page];
        const index = doc.data.pages.indices[page.id];
        const label = page.label || sheet?.name || index + 1;
        return { id, label, hidden: $hiddenPages[id] ?? false };
      });

    hiddenPageModal.open();
  }

  async function updateHiddenPages() {
    const nhp = hiddenPageStatus.reduce((acc, p) => {
      if (p.hidden) acc[p.id] = true;
      return acc;
    }, {});
    $hiddenPages = nhp;
    await tick();
    initTileSources();
    updateLinks();
  }

  function confirmRemovePages() {
    pagesToDelete = selectedPageList.map((l) => l.id);
    confirmRemovePagesModal.open();
  }

  function refreshMarkUpdateData() {
    markUpdateSelection = orderFrameRecords(
      selectedLocationList,
      visibleIndices,
      $group,
      locRecord,
      reverseMarkUpdate,
    );
    markUpdateMarks = markUpdateSelection.map((r) => r.record.mark || "");
    markUpdateOtherMarks = otherMarksInScope(markUpdateSelection, $group);
    markUpdateStart = startMarkFor(selectedLocationList, visibleIndices, $group, locRecord);
  }

  async function confirmUpdateMarks() {
    refreshMarkUpdateData();
    // The modal reads defaultStart in its own open(), so let the new props
    // reach it before opening.
    await tick();
    markUpdateModal.open();
  }

  // Reverse Label Order changes which record gets which mark, so the caller
  // re-derives the selection rather than the dialog reordering it — reversing
  // has to happen before de-duplication.
  function setMarkUpdateReverse(evt) {
    reverseMarkUpdate = evt.detail.reverse;
    refreshMarkUpdateData();
  }

  function downloadLocations() {
    eb.dispatch("open-location-download");
  }

  function updateMarks(evt) {
    const updates = changedUpdates(markUpdateSelection, evt.detail.marks);
    if (updates.length) group.update(updates);
  }

  function removePages() {
    selectedPages.deselect(...pagesToDelete);
    tileSources = tileSources.filter((ts, i) => {
      const id = visiblePages[i];
      return !pagesToDelete.includes(id);
    });
    const data = cloneDeep(doc.data);
    removeFromSortableList(data.pages, ...pagesToDelete);

    const deletedPages = pagesToDelete.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
    const deletedLocations = locations.filter((l) => deletedPages[l.page_id]).map((l) => l.id);
    if (deletedLocations.length) {
      group.update([
        { type: "document", id: doc.id, diff: { data } },
        { type: "location", action: "remove", ids: deletedLocations },
      ]);
    } else {
      group.updateDocument(doc.id, { data });
    }
  }

  // function hidePages(e) {
  //   tileSources = tileSources.filter((ts, i) => {
  //     const id = visiblePages[i];
  //     return !$selectedPages[id];
  //   });
  //   const data = cloneDeep(doc.data);
  //   const ids = selectedPageList.map((l) => l.id);
  //   ids.forEach((id) => {
  //     data.pages[id].hidden = true;
  //   });
  //   group.updateDocument(doc.id, { data });
  // }

  async function tempHidePages() {
    if (!selectedPageList.length) return;
    const pagesToHide = selectedPageList.map((l) => l.id);
    hiddenPages.select(...pagesToHide);
    refreshPageList();
  }

  async function tempShowPages() {
    if (!selectedPageList.length) return;
    const pagesToShow = selectedPageList.map((l) => l.id);
    hiddenPages.deselect(...pagesToShow);
    refreshPageList();
    if (selectedPageList.some((p) => p.hidden)) {
      await tick();
      showPages();
    }
  }

  async function isolatePages() {
    if (!selectedPageList.length) return;
    const pagesToHide = doc.data.pages.order.filter(
      (id) => !$selectedPages[id] && !doc.data.pages[id]?.hidden,
    );
    hiddenPages.selectOnly(...pagesToHide);
    refreshPageList();
  }

  async function isolateEnumeratedPages(pages) {
    const pageObj = pages.reduce((acc, pageId) => {
      acc[pageId] = true;
      return acc;
    }, {});
    const pagesToHide = doc.data.pages.order.filter((id) => !pageObj[id] && !doc.data.pages[id]?.hidden);
    hiddenPages.selectOnly(...pagesToHide);
    refreshPageList();
  }

  async function refreshPageList() {
    await tick();
    initTileSources();
    updateLinks();
  }

  async function isolateLocationPages() {
    if (!selectedLocationList.length && !selectedDimensionList.length) return;
    const pagesToKeep = [...selectedLocationList, ...selectedDimensionList].reduce((acc, l) => {
      acc[l.page_id] = true;
      return acc;
    }, {});
    const pagesToHide = doc.data.pages.order.filter((id) => {
      const page = doc.data.pages[id];
      return !page.hidden && !pagesToKeep[id];
    });
    hiddenPages.selectOnly(...pagesToHide);
    await tick();
    initTileSources();
    updateLinks();
  }

  async function showPages() {
    const data = cloneDeep(doc.data);
    const ids = selectedPageList.map((l) => l.id);
    ids.forEach((id) => {
      data.pages[id].hidden = false;
    });
    await group.updateDocument(doc.id, { data });
    initTileSources();
    updateLinks();
  }

  function handleRightDown() {
    tempTool = $currentTool;
    $currentTool = "pan";
  }

  function handleRightDrag(e) {
    const { tilePosition } = e;

    if (tilePosition.index >= 0 && isLoadedPage(tilePosition.index)) {
      currentMousePos = tilePosition;
    } else {
      currentMousePos = null;
    }
  }

  function handleRightUp() {
    if (tempTool) {
      $currentTool = tempTool;
      tempTool = null;
    }
  }

  function handleLocationDblClick(e) {
    const { locations } = e.detail;
    const visibleLocs = locations.filter((l) => {
      const page = doc.data.pages[l.page_id];
      return page && !page.hidden && !$hiddenPages[l.page_id];
    });
    if (visibleLocs.length > 0) {
      const indices = visibleLocs.map((l) => visibleIndices[l.page_id]).filter((l) => l !== undefined);
      const pages = uniq(indices);
      controller.emit("zoom-to-page", pages);
      selectedLocations.selectOnly(...visibleLocs.map((l) => l.id));
    }
  }

  function copyLocationsToClipboard() {
    copyToClipboard({
      type: "locations",
      data: {
        locations: cloneDeep(selectedLocationList),
        document_id: doc.id,
        group_id: $group.id,
      },
    });
  }

  async function pasteLocationsFromGroup() {
    const groupData = await fetchGroup($clipboard.data.group_id);
    if (!groupData) {
      alerts.add({ type: "warning", message: "Cannot paste locations from this group." });
      return;
    }
    const grp = hydrateGroup(groupData);
    const locs = $clipboard.data.locations;
    const pdoc = grp.documents[$clipboard.data.document_id];
    const pdocVisible = getVisiblePages(pdoc, {});
    const pdocIndices = getVisibleIndices(pdocVisible);
    let minIndex = 0;
    const indices = locs.map((l) => pdocIndices[l.page_id]).filter((l) => l !== undefined);
    if (indices.length) {
      minIndex = Math.min(...indices);
    }
    const selectedIndices = Object.keys($selectedPages).map((id) => visibleIndices[id]);
    const p = selectedIndices.length ? Math.min(...selectedIndices) : minIndex;

    const itemsByMark = $group.items.order.reduce((acc, id) => {
      const item = $group.items[id];
      acc[item.mark] = id;
      return acc;
    }, {});
    const typesByMark = $group.types.order.reduce((acc, id) => {
      const type = $group.types[id];
      acc[type.mark] = id;
      return acc;
    }, {});
    const docNotesByMark = $group.document_notes.order.reduce((acc, id) => {
      const note = $group.document_notes[id];
      acc[note.mark] = id;
      return acc;
    }, {});

    const newLocs = locs
      .map((l) => {
        const index = pdocIndices[l.page_id];
        const newIndex = index - minIndex + p;
        const page_id = visiblePages[newIndex];

        if (!page_id) return null;

        let record = locRecord(l, grp);
        let record_id;
        if (l.record_type === "type") {
          record_id = typesByMark[record?.mark];
        } else if (l.record_type === "document_note") {
          record_id = docNotesByMark[record?.mark];
        } else {
          record_id = itemsByMark[record?.mark];
        }

        let category = l.category;
        if ($documentViewLocationFilter?.operator === "is_equal_to") {
          category = $documentViewLocationFilter.value;
        }

        return {
          ...l,
          category,
          group_id: $group.id,
          record_id,
          id: crypto.randomUUID(),
          document_id: doc.id,
          index: locations.length,
          page_id,
        };
      })
      .filter((l) => l && locRecord(l, $group));

    if (newLocs.length) {
      group.update([{ type: "location", action: "add", records: newLocs }]);
    }
  }

  function pasteFromClipboard() {
    if ($clipboard?.type === "locations") {
      pasteLocationsFromClipboard();
    } else if ($clipboard?.type === "item-drawing") {
      pasteItemDrawingsFromClipboard();
    }
  }

  // FrameX-only (Ctrl+Shift+V): clone-paste at the exact source position
  // instead of offset to the side. Outside the shpx build the shortcut is
  // never bound (Group.svelte), and this guard keeps the event inert too.
  function pasteFromClipboardInPlace() {
    if (UX_MODE !== "shpx") return;
    if ($clipboard?.type === "locations") {
      pasteLocationsFromClipboard({ inPlace: true });
    }
  }

  function pasteItemDrawingsFromClipboard() {
    if (!selectedLocationList.length) {
      alerts.add({ type: "warning", message: "Select at least one marker to paste drawing." });
      return;
    }

    if ($clipboard.data.item_id && $group.items[$clipboard.data.item_id]) {
      const item = $group.items[$clipboard.data.item_id];

      const receivingItems = {};
      selectedLocationList.forEach((loc) => {
        if (loc.record_type === "item" || loc.record_type === "collection") {
          receivingItems[loc.record_id] = true;
        }
      });

      delete receivingItems[item.id];

      const updates = Object.keys(receivingItems).map((id) => ({
        type: "item",
        id,
        prop: "drawing",
        value: cloneDeep(item.drawing),
      }));

      if (updates.length) {
        group.update(updates);
      }
    } else {
      alerts.add({ type: "warning", message: "Unable to paste drawing." });
      return;
    }
  }

  function pasteLocationsFromClipboard({ inPlace = false } = {}) {
    if ($clipboard.data.group_id !== $group.id) {
      pasteLocationsFromGroup();
      return;
    }

    const locs = $clipboard.data.locations;
    const pdoc = $group.documents[$clipboard.data.document_id];
    const pdocVisible = getVisiblePages(pdoc, $hiddenPages);
    const pdocIndices = getVisibleIndices(pdocVisible);
    let minIndex = 0;
    const indices = locs.map((l) => pdocIndices[l.page_id]).filter((l) => l !== undefined);
    if (indices.length) {
      minIndex = Math.min(...indices);
    }
    const selectedIndices = Object.keys($selectedPages).map((id) => visibleIndices[id]);
    const p = selectedIndices.length ? Math.min(...selectedIndices) : minIndex;

    // FrameX (shpx build) only: pasting a frame is a true clone — its own
    // record with an incremented mark, the clip carried over, and the image
    // owned by the new placement from birth. A shared-record placement is
    // unrepresentable for a frame: an item has a single drawing.attachment_id,
    // so only one placement can show the image (LNA-30/LNA-31). The legacy
    // shape.exchange paste below is untouched.
    if (UX_MODE === "shpx") {
      // With no sheet selected, the selected frames' sheets are the working
      // context and therefore the destination. Pasting selects the pasted
      // frames, so repeat pastes keep landing on the sheet just pasted onto
      // instead of silently returning to the source sheet. Anchor on the
      // EARLIEST selected sheet: the mapping places the batch's earliest
      // source sheet at the anchor, so anchoring on "first clicked" would
      // displace a multi-sheet batch down the sheet set.
      let anchor = p;
      if (!selectedIndices.length) {
        const selIdxs = selectedLocationList
          .map((sl) => visibleIndices[sl.page_id])
          .filter((i) => i !== undefined);
        if (selIdxs.length) anchor = Math.min(...selIdxs);
      }

      const newLocs = [];
      const newRecords = [];
      // Marks minted in this batch dedupe against the group AND each other,
      // so pasting several frames (or pasting repeatedly) yields (2), (3)…
      const collectionMarks = collections.map((c) => c.mark);

      locs.forEach((l) => {
        const index = pdocIndices[l.page_id];
        const newIndex = index - minIndex + anchor;
        const page_id = visiblePages[newIndex];

        if (!page_id || !locRecord(l, $group)) return;

        let category = l.category;
        if ($documentViewLocationFilter?.operator === "is_equal_to") {
          category = $documentViewLocationFilter.value;
        }

        const id = crypto.randomUUID();

        if (l.record_type === "collection" && l.clip) {
          const record = cloneDeep(locRecord(l, $group));
          record.id = crypto.randomUUID();
          // The clone is new content by the pasting user, like cloneItems.
          record.created_by = $profile.id;
          record.created_at = new Date();
          // A null mark would throw inside incrementMarkCopy and abort the
          // whole paste; an unmarked frame clones as just the "(n)" suffix.
          record.mark = incrementMarkCopy(record.mark || "", collectionMarks);
          collectionMarks.push(record.mark);
          // Both shortcuts share the destination-sheet rule (the selected
          // sheet, else the selected frames' sheet, else the source sheet);
          // they differ only in placement there. Ctrl+Shift+V always lands at
          // the exact copied sheet-relative position. Ctrl+V nudges one
          // boundary width right ONLY on the frame's own sheet, where the
          // original sits underneath; on any other sheet it is an exact
          // transfer. The nudge is fixed, never cumulative — repeats land on
          // the same spot and the overlap border tells the story.
          const dx =
            inPlace || page_id !== l.page_id ? 0 : l.clip.width * 1.1;

          // Attach the cloned (already built) drawing to the new placement
          // directly: the image is on with no repair pass, and the built
          // dimensions survive instead of being regenerated from the clip.
          if (record.drawing) {
            record.drawing.attachment_id = id;
            // The overlay renders at the DRAWING's stored clip coordinates
            // (shape-overlay reads item.drawing.clip.x/y), not the
            // placement's. Give the clone's drawing the pasted boundary's
            // coordinates, or the overlay is born inside the SOURCE frame —
            // and dragging it there snaps the new boundary back onto the
            // old position, losing the paste placement.
            if (record.drawing.clip) {
              record.drawing.clip = {
                ...record.drawing.clip,
                x: l.clip.x + dx,
                y: l.clip.y,
                width: l.clip.width,
                height: l.clip.height,
              };
            }
          }
          newRecords.push(record);
          newLocs.push({
            ...l,
            category,
            id,
            record_id: record.id,
            document_id: doc.id,
            index: locations.length + newLocs.length,
            page_id,
            position: { ...l.position, x: l.position.x + dx },
            clip: { ...l.clip, x: l.clip.x + dx },
          });
          return;
        }

        newLocs.push({
          ...l,
          category,
          id,
          document_id: doc.id,
          index: locations.length + newLocs.length,
          page_id,
        });
      });

      if (newLocs.length) {
        group.update([
          ...(newRecords.length
            ? [{ type: "collection", action: "add", records: newRecords }]
            : []),
          { type: "location", action: "add", records: newLocs },
        ]);
        // Hand the selection to what was just pasted, exactly like the
        // clone-place path: the new boundary shows active immediately.
        selectedLocations.selectOnly(...newLocs.map((l) => l.id));
      }
      return;
    }

    const newLocs = locs
      .map((l) => {
        const index = pdocIndices[l.page_id];
        const newIndex = index - minIndex + p;
        const page_id = visiblePages[newIndex];

        if (!page_id) return null;

        let category = l.category;
        if ($documentViewLocationFilter?.operator === "is_equal_to") {
          category = $documentViewLocationFilter.value;
        }

        return {
          ...l,
          category,
          id: crypto.randomUUID(),
          document_id: doc.id,
          index: locations.length,
          page_id,
        };
      })
      .filter((l) => l && locRecord(l, $group));

    if (newLocs.length) {
      group.update([{ type: "location", action: "add", records: newLocs }]);
    }
  }

  function redraw() {
    overlayDwgs = overlayDwgs;
  }

  function deselectAll() {
    if (!isEmpty($selectedAnnotations)) $selectedAnnotations = {};
    if (!isEmpty($selectedLocations)) $selectedLocations = {};
    if (!isEmpty($selectedDimensions)) $selectedDimensions = {};
    if (!isEmpty($selectedPages)) $selectedPages = {};
    if (!isEmpty($selectedRecords)) $selectedRecords = {};
  }

  function handleAltDown() {
    altDown = true;

    if (currentHb) {
      const [type, id, index] = currentHb.name.split("_");
      if (type === "location" || (type === "loc-clip" && index === "outline")) {
        cursor = "copy";
      }
    }

    if (["location", "loc-clip-outline"].includes(dragging?.type)) {
      dragging.copy = true;
      cursor = "copy";
    }
  }

  function trackMouse(currentMousePos, shiftDown, adding, dragging, multiclicks, ct) {
    if (!overlayCtx || !viewer) return;
    overlayCanvas.width = overlayCanvas.clientWidth;
    overlayCanvas.height = overlayCanvas.clientHeight;
    overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    let lpm;
    if (adding && currentMousePos) {
      let mark = "";
      let style;
      if (adding?.record_id) {
        const record = locRecord(adding, $group);

        if (adding.clone) {
          const labelBy = record?.is_collection
            ? $group.data.settings.collections_label_by
            : $group.data.settings.label_by;

          if (!labelBy || labelBy === "mark") {
            mark = nextMark;
          } else {
            mark = get(record, labelBy);
          }
        } else {
          mark = record ? recordMark(adding, record) : "";
        }
        style = { ...markColor(record, { emphasized: true }), lineWidth: 2 };
      } else {
        if (adding?.record_type === "type") {
          mark = nextMarkFromList(types);
        } else if (adding?.record_type === "collection") {
          mark = nextMarkFromList(collections, $org?.default_item_mark);
        } else if (adding?.record_type === "item") {
          mark = nextMarkFromList(items, $org?.default_item_mark);
        } else if (adding?.record_type === "document_note") {
          mark = nextMarkFromList(documentNotes);
        }

        style = { fill: "#f59e0b", lineWidth: 1 };
      }

      let pos;
      if (clipping) {
        const mp = midpoint(clipping.tpStart, clipping.tpEnd);
        const tPos = { x: mp.x, y: mp.y, index: currentMousePos.index };
        const pxPos = viewer.pixelCoords(tPos);
        pos = { x: pxPos.x, y: -pxPos.y };
      } else {
        const pxPos = viewer.pixelCoords(currentMousePos);
        pos = { x: pxPos.x, y: -pxPos.y };
      }

      lpm = new Drawing().mark(pos, mark, { shape: markShapes[adding.record_type] }).style(style);
    } else if (dragging && currentMousePos) {
      if (dragging.type === "loc-clip-vertex") {
        const p = subtract(dragging.end, dragging.delta);
        const loc = dragging.location;
        const pageIndex = visibleIndices[loc.page_id];
        if (pageIndex != null) {
          const vpClip = screenScaledClip(loc.clip, viewer, pageIndex);
          const d = draggedClip(vpClip, dragging.index, p);
          const clip = clipRectangle("loc", d, loc.id);
          lpm = new Drawing().add(clip);
        }
      } else if (dragging.type === "loc-clip-outline") {
        const p = subtract(dragging.end, dragging.delta);
        const loc = dragging.location;
        const pageIndex = visibleIndices[loc.page_id];
        if (pageIndex != null) {
          const vpPos = viewer.pixelCoords({ x: loc.position.x, y: loc.position.y, index: pageIndex });
          const vpClip = screenScaledClip(loc.clip, viewer, pageIndex);
          const min = viewer.pixelCoords({ x: 0, y: 0, index: pageIndex });
          const max = viewer.pixelCoords({ ...dragging.tile_size, index: pageIndex });
          const delta = subtract({ x: p.x, y: p.y }, vpPos);
          const translated = translateRectangle(vpClip, delta);
          const constrained = constrainClip(translated, min.x, min.y, max.x, max.y);
          const clip = clipRectangle("loc", constrained, loc.id);

          // FrameX boundary alt-drag clones the frame, so the ghost carries
          // the marker too, labeled with the mark the clone will receive —
          // matching the marker-drag preview.
          if (UX_MODE === "shpx" && dragging.copy && loc.record_type === "collection") {
            const record = locRecord(loc, $group);
            const nextMark = incrementMarkCopy(
              record?.mark || "",
              collections.map((c) => c.mark),
            );
            const offset = subtract(vpPos, { x: vpClip.x, y: vpClip.y });
            const mpos = add({ x: constrained.x, y: constrained.y }, offset);
            const ghost = new Drawing()
              .mark({ x: mpos.x, y: -mpos.y }, nextMark, {
                shape: loc.shape === "default" ? markShapes[loc.record_type] : loc.shape,
                icon: locationIcon(loc),
              })
              .style({ ...markColor(record, { emphasized: true }), lineWidth: 2 });
            lpm = new Drawing().add(clip, ghost);
          } else {
            lpm = new Drawing().add(clip);
          }
        }
      } else if (dragging.type === "ai-clip-vertex") {
        const p = subtract(dragging.end, dragging.delta);
        const pageIndex = visibleIndices[aiClipRegion.page_id];
        if (pageIndex != null) {
          const vpClip = screenScaledClip(aiClipRegion.clip, viewer, pageIndex);
          const d = draggedClip(vpClip, dragging.index, p);
          const clip = clipRectangle("ai", d, "region");
          lpm = new Drawing().add(clip);
        }
      } else if (dragging.type === "ai-clip-outline") {
        const p = subtract(dragging.end, dragging.delta);
        const pageIndex = visibleIndices[aiClipRegion.page_id];
        if (pageIndex != null) {
          const vpClip = screenScaledClip(aiClipRegion.clip, viewer, pageIndex);
          const min = viewer.pixelCoords({ x: 0, y: 0, index: pageIndex });
          const max = viewer.pixelCoords({ ...dragging.tile_size, index: pageIndex });
          const vpPos = viewer.pixelCoords({
            x: aiClipRegion.clip.x,
            y: aiClipRegion.clip.y,
            index: pageIndex,
          });
          const delta = subtract({ x: p.x, y: p.y }, vpPos);
          const translated = translateRectangle(vpClip, delta);
          const constrained = constrainClip(translated, min.x, min.y, max.x, max.y);
          const clip = clipRectangle("ai", constrained, "region");
          lpm = new Drawing().add(clip);
        }
      } else if (dragging.type === "shapeoverlay") {
        const loc = dragging.location;
        const pageIndex = visibleIndices[loc.page_id];
        const rec = locRecord(loc, $group);
        if (pageIndex != null && rec) {
          const m = multiplier(rec.drawing.clip.drawing_scale);
          const clipScale = 300 / m;
          const l0 = viewer.pixelCoords({ x: loc.clip.x, y: loc.clip.y, index: pageIndex });
          const l1 = viewer.pixelCoords({
            x: loc.clip.x + loc.clip.width,
            y: loc.clip.y + loc.clip.height,
            index: pageIndex,
          });
          const clipHt = l1.y - l0.y;
          const clipWd = l1.x - l0.x;
          const vpScale = clipWd / rec.drawing.clip.width;
          const scale = clipScale * vpScale;
          const { width, height, xmin, ymin } = rec.cache.bbox;
          const sw = width * scale;
          const sh = height * scale;
          const sxm = xmin * scale;
          const sym = ymin * scale;
          const ox = (rec.drawing.clip.drawing_offset?.x || 0) * scale;
          const oy = (rec.drawing.clip.drawing_offset?.y || 0) * scale;
          const x = l0.x - ox;
          const y = -l0.y - clipHt - oy;
          const delta = subtract(dragging.end, dragging.start);
          const dx = Math.min(Math.max(delta.x, ox - sxm), clipWd + ox - sw - sxm);
          const dy = Math.max(Math.min(-oy + sym, delta.y), -clipHt - oy + sh + sym);

          const swf = rec.cache.shapeWithFeatures;
          lpm = new Drawing()
            .polyface(swf.shape)
            .scale(scale, scale)
            .translate(x + dx, y - dy)
            .style({
              stroke: "#0066E5",
              fill: "#0066E5",
              opacity: 0.1,
            });
        }
      }
    }

    let dimpreview;
    if (["dimension-linear", "dimension-rectangular", "pick-scale-dim"].includes(multiclicks.tool)) {
      const points = multiclicks.clicks.slice();
      if (points.length === 1 && currentMousePos) {
        if (shiftDown && ["dimension-linear", "pick-scale-dim"].includes(multiclicks.tool)) {
          const sp = multiclicks.clicks[0];
          const cp = currentMousePos;
          const dx = Math.abs(cp.x - sp.x);
          const dy = Math.abs(cp.y - sp.y);
          if (dx > dy) {
            points.push({ x: cp.x, y: sp.y, index: cp.index });
          } else {
            points.push({ x: sp.x, y: cp.y, index: cp.index });
          }
        } else {
          points.push(currentMousePos);
        }
      }

      if (points.length > 1) {
        const pageIndex = points[0].index;
        const pts = points.map((p) => {
          const pxPos = viewer.pixelCoords(p);
          if (!pxPos) return null;
          return { x: pxPos.x, y: -pxPos.y };
        });
        if (pts.every((p) => p) && points.every((p) => p.index === pageIndex)) {
          if (multiclicks.tool === "dimension-linear" || multiclicks.tool === "pick-scale-dim") {
            dimpreview = new Drawing()
              .aligned_dim(pts[0], pts[1], { override: "L" })
              .style({ fill: "#000000", stroke: "#FF0000", lineWidth: 2, textColor: "#FF0000" });
          } else if (multiclicks.tool === "dimension-rectangular") {
            dimpreview = new Drawing()
              .rectangle(pts[0], pts[1])
              .style({ fill: "transparent", stroke: "#FF0000", lineWidth: 2 });
          }
        }
      } else if (currentMousePos) {
        const pxPos = viewer.pixelCoords(currentMousePos);
        const pos = { x: pxPos.x, y: -pxPos.y };
        dimpreview = new Drawing().point(pos, "crosshairs").style({ stroke: "#FF0000", lineWidth: 1 });
      }
    } else if (
      ["dimension-linear", "dimension-rectangular", "pick-scale-dim"].includes(ct) &&
      currentMousePos
    ) {
      const pxPos = viewer.pixelCoords(currentMousePos);
      const pos = { x: pxPos.x, y: -pxPos.y };
      dimpreview = new Drawing().point(pos, "crosshairs").style({ stroke: "#FF0000", lineWidth: 1 });
    }

    if (lpm) {
      lpm.render({ ctx: overlayCtx, annoScale: 1 });
    }

    if (dimpreview) {
      dimpreview.render({ ctx: overlayCtx, annoScale: 1 });
    }

    if (!overlayFresh) overlayFresh = true;
  }

  function handleAltUp() {
    altDown = false;

    if (currentHb) {
      const [type, id, index] = currentHb.name.split("_");
      cursor = defMouseCursor(type, id, index);
    }

    if (dragging) {
      dragging.copy = false;
    }
  }

  function handleShiftDown() {
    shiftDown = true;
  }

  function handleShiftUp() {
    shiftDown = false;
  }

  function handleEscape() {
    deselectAll();
    window.getSelection()?.removeAllRanges();
    $currentTool = "select";
    tempTool = null;
    multiclicks = { tool: null, clicks: [] };
  }

  function reflowGrid() {
    if (viewer) {
      controller.emit("reflow-grid");
    }
  }

  function handleShortcut(evt) {
    if ($itemOverlay) {
      switch (evt.key) {
        case "Delete":
        case "Backspace":
          deleteFeatures(selectedFeatures);
          break;
        case "Escape":
          selectedFeatures = [];
          window.getSelection()?.removeAllRanges();
          break;
      }

      return;
    }

    if (!width || !height) {
      return;
    }
    switch (evt.key) {
      case "z":
        zoomSelected();
        break;
      case "m":
        if (selectedLocationList.length) {
          evt.preventDefault();
          confirmUpdateMarks();
        }
        break;
      case "g":
        reflowGrid();
        break;
      case "h":
        if (selectedLocationList.length) {
          evt.preventDefault();
          hideLocations();
        } else if (selectedPageList.length) {
          evt.preventDefault();
          tempHidePages();
        }
        break;
      case "i":
        if (selectedDimensionList.length || selectedLocationList.length) {
          evt.preventDefault();
          isolateLocationPages();
        } else if (selectedPageList.length) {
          evt.preventDefault();
          isolatePages();
        }
        break;
      case "s":
        if (hiddenLocationList.length) {
          showHiddenLocations();
        }

        if (hiddenPageList.length) {
          showHiddenPages();
        }
        break;
      case "Tab":
        if (selectedLocationList.length === 1) {
          evt.preventDefault();
          if (!nextLoc && prevLoc) {
            gotoFirstLoc();
          } else {
            gotoNextLoc();
          }
        }
        break;
      case "ArrowLeft":
        if (selectedLocationList.length > 0) {
          nudgeLocations("left");
        }
        break;
      case "ArrowRight":
        if (selectedLocationList.length > 0) {
          nudgeLocations("right");
        }
        break;
      case "ArrowUp":
        if (selectedLocationList.length > 0) {
          nudgeLocations("up");
        }
        break;
      case "ArrowDown":
        if (selectedLocationList.length > 0) {
          nudgeLocations("down");
        }
        break;
      case "Escape":
        if (markUpdateModal?.isModalOpen()) {
          markUpdateModal.close();
        } else {
          handleEscape();
        }
        break;
    }
  }

  function testVisibility() {
    if (!container?.offsetWidth || !container?.offsetHeight) return false;
    return true;
  }

  function updateDevicePixelRatio() {
    dpi = window.devicePixelRatio || 1;
    redraw();

    window
      .matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
      .addEventListener("change", updateDevicePixelRatio, { once: true });
  }

  onMount(() => {
    $itemOverlay = null;
    $itemOverlayMode = null;

    if (UX_MODE === "shpx" && !$profile?.has_seen_tutorial) {
      $showingTutorial = true;
    }

    eb.on("zoom-in-doc", zoomIn, testVisibility);
    eb.on("zoom-out-doc", zoomOut, testVisibility);
    eb.on("zoom-to-fit-doc", zoomToFit, testVisibility);
    eb.on("zoom-to-page", zoomToPage, testVisibility);
    eb.on("zoom-selected", zoomSelected, testVisibility);
    eb.on("location-item", handleLocationToolClick, testVisibility);
    eb.on("location-type", handleLocationToolClick, testVisibility);
    eb.on("location-collection", handleLocationToolClick, testVisibility);
    eb.on("location-document_note", handleLocationToolClick, testVisibility);
    eb.on("copy-to-clipboard", copyLocationsToClipboard, testVisibility);
    eb.on("paste-from-clipboard", pasteFromClipboard, testVisibility);
    eb.on("paste-from-clipboard-in-place", pasteFromClipboardInPlace, testVisibility);
    eb.on("key-alt-down", handleAltDown, testVisibility);
    eb.on("key-alt-up", handleAltUp, testVisibility);
    eb.on("key-shift-down", handleShiftDown, testVisibility);
    eb.on("key-shift-up", handleShiftUp, testVisibility);
    eb.on("reflow-grid", reflowGrid, testVisibility);
    eb.on("shortcut-key", handleShortcut, testVisibility);

    controller.on("canvas-double-click", handleCanvasDoubleClick);
    controller.on("canvas-press", handleLeftDown);
    controller.on("canvas-release", handleLeftUp);
    controller.on("canvas-drag", handleCanvasDrag);
    controller.on("canvas-drag-end", handleCanvasDragEnd);
    controller.on("canvas-mousemove", handleMousemove);
    controller.on("canvas-nonprimary-press", handleRightDown);
    controller.on("canvas-nonprimary-drag", handleRightDrag);
    controller.on("canvas-nonprimary-release", handleRightUp);
    controller.on("viewport-change", handleViewportChange);
    controller.on("open", finish);
    controller.on("mount", redraw);

    if ("matchMedia" in window) {
      window
        .matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
        .addEventListener("change", updateDevicePixelRatio, { once: true });
    }

    return () => {
      eb.unsubscribe("zoom-in-doc", zoomIn);
      eb.unsubscribe("zoom-out-doc", zoomOut);
      eb.unsubscribe("zoom-to-fit-doc", zoomToFit);
      eb.unsubscribe("zoom-to-page", zoomToPage);
      eb.unsubscribe("zoom-selected", zoomSelected);
      eb.unsubscribe("location-item", handleLocationToolClick);
      eb.unsubscribe("location-type", handleLocationToolClick);
      eb.unsubscribe("location-collection", handleLocationToolClick);
      eb.unsubscribe("location-document_note", handleLocationToolClick);
      eb.unsubscribe("copy-to-clipboard", copyLocationsToClipboard);
      eb.unsubscribe("paste-from-clipboard", pasteFromClipboard);
      eb.unsubscribe("paste-from-clipboard-in-place", pasteFromClipboardInPlace);
      eb.unsubscribe("key-alt-down", handleAltDown);
      eb.unsubscribe("key-alt-up", handleAltUp);
      eb.unsubscribe("key-shift-down", handleShiftDown);
      eb.unsubscribe("key-shift-up", handleShiftUp);
      eb.unsubscribe("shortcut-key", handleShortcut);
      eb.unsubscribe("reflow-grid", reflowGrid);
      controller.all.clear();

      if (timeout) {
        clearTimeout(timeout);
      }
    };
  });
</script>

{#if doc}
  <div
    class="w-full h-full flex relative"
    bind:this={container}
    bind:offsetWidth={width}
    bind:offsetHeight={height}
    class:flex-col={sidebarPosition === "bottom"}>
    {#if $showLeftPanel && width >= 640 && UX_MODE === "shpx" && $leftPanelMode === "framex"}
      <FrameXPanel
        {group}
        disabled={!!$itemOverlay}
        docid={isVirtual ? origDoc?.id : doc.id}
        {doc}
        {indexedLocations}
        {visiblePages}
        {visibleIndices}
        {selectedPageList}
        {selectedLocationList}
        {collections}
        {viewer}
        bind:spinners
        on:pick-dimension={beginPickingScaleDim}
        on:click-location-tool={handleLocationToolClick}
        on:edit-frame={(e) => showDetailDrawing(e.detail.locId)} />
    {:else if $showLeftPanel && width >= 640}
      <Sidebar>
        <svelte:fragment slot="header">
          <PrevNext
            prev={prevDoc}
            next={nextDoc}
            title={isVirtual ? origDoc?.name : doc?.name}
            on:gotoprev={gotoPrev}
            on:gotonext={gotoNext}
            sticky />
        </svelte:fragment>
        <svelte:fragment slot="content">
          <DocumentProperties
            {doc}
            origDoc={isVirtual ? origDoc : null}
            {group}
            {disabled}
            {annotations}
            {items}
            {viewer}
            filters="active"
            bind:hoveredFile
            bind:hoveredPage
            bind:lastSelectedPage
            bind:hoveredAnnotation
            on:update-annotations={updateAnnotationDimensions}
            on:zoom-to-page={(e) => zoomToPage(e.detail.page)}
            on:update-visible-pages={showHiddenPageModal} />
        </svelte:fragment>
      </Sidebar>
    {/if}
    <div class="w-full h-full flex relative">
      {#if !$itemOverlay && UX_MODE !== "shpx"}
        <div class="p-3 absolute z-10 space-y-2">
          <div class="text-blue-500 text-sm bg-gray-100 px-2 py-1 rounded">
            <Link to={"documents"} class="flex items-center space-x-1">
              <CaretLeftIcon />
              <div>All Documents</div>
            </Link>
          </div>
        </div>
      {/if}

      <div class="grow relative border">
        <div class="absolute w-full h-full overflow-hidden">
          <DocSearchWidget
            docid={doc.id}
            {group}
            {items}
            {collections}
            {types}
            {documentNotes}
            {locations}
            {visibleIndices}
            bind:searchResults
            bind:highlightedText
            bind:importSearchResultsAs
            on:move-to-location={(e) => moveToLocation(e.detail)}
            on:zoom-to-bounds={(e) => zoomToBounds(e.detail.points)}
            on:zoom-to-page={(e) => zoomToPage(e.detail.page)}
            on:isolate-pages={(e) => isolateEnumeratedPages(e.detail.pages)} />
          <DocPageRelabelWidget
            {group}
            {doc}
            {visiblePages}
            {visibleIndices}
            bind:relabeledPages
            bind:pageRegions
            bind:showPageRelabelRegions
            on:move-to-location={(e) => moveToLocation(e.detail)}
            on:zoom-to-page={(e) => zoomToPage(e.detail.page)} />
          <DocQuickSelectWidget {group} {doc} {hiddenLocationList} {locations} {dimensions} {filters} />
          <DocCornerPanels>
            <DocRulerWidget
              bind:multiclicks
              {currentMousePos}
              {shiftDown}
              {tempTool}
              {doc}
              {hoveredPageId}
              settings={$group?.data.settings}
              on:pick-dimension={beginPickingScaleDim} />
            <ScaleFromDimensionModal bind:this={scaleFromDimensionModal} {group} {doc} {indexedLocations} />
          </DocCornerPanels>
          <AiClipWidget bind:aiClipRegion {doc} />
          <DrawHintBanner />
          <FilterWidget
            docid={doc.id}
            {customColumns}
            {customColColumns}
            {standardColumns}
            bind:showItems={$documentViewShowItems}
            bind:showCollections={$documentViewShowCollections}
            bind:showTypes={$documentViewShowTypes}
            bind:showNotes={$documentViewShowNotes}
            bind:showDims={$documentViewShowDimensions}
            bind:locationFilter={$documentViewLocationFilter}
            bind:itemFilter={$documentViewItemFilter}
            bind:collectionFilter={$documentViewCollectionFilter}
            bind:typeFilter={$documentViewTypeFilter}
            bind:noteFilter={$documentViewNoteFilter} />
          {#if !$itemOverlay}
            {#if selectedDimensionList.length > 0}
              <SelectedActions
                deletable={!disabled}
                isolatable
                zoomable
                linkable
                isolateLabel="Isolate Pages (I)"
                selected={selectedDimensionList.length + selectedLocationList.length}
                positionable={!disabled}
                on:delete={confirmRemoveLocations}
                on:isolate={isolateLocationPages}
                on:zoom-selected={zoomSelected}
                on:link={createLocationLink}
                on:align-horizontal={alignLocationsHorizontal}
                on:align-vertical={alignLocationsVertical}
                on:distribute-horizontal={distributeLocationsHorizontal}
                on:distribute-vertical={distributeLocationsVertical} />
            {:else if selectedLocationList.length > 0}
              <SelectedActions
                deletable={!disabled}
                hideable
                cloneable
                isolatable
                selectable
                zoomable
                linkable
                remarkable
                positionable={!disabled}
                isolateLabel="Isolate Pages (I)"
                showLabel="Show"
                hideLabel="Hide (H)"
                selected={selectedLocationList.length}
                on:delete={confirmRemoveLocations}
                on:hide={hideLocations}
                on:isolate={isolateLocationPages}
                on:clone={copyLocationsToClipboard}
                on:select-similar={selectSimilarLocations}
                on:zoom-selected={zoomSelected}
                on:link={createLocationLink}
                on:align-horizontal={alignLocationsHorizontal}
                on:align-vertical={alignLocationsVertical}
                on:distribute-horizontal={distributeLocationsHorizontal}
                on:remark={confirmUpdateMarks}
                on:distribute-vertical={distributeLocationsVertical}
                on:download={downloadLocations} />
            {:else if selectedPageList.length > 0}
              <SelectedActions
                deletable={!isPageEditingDisabled}
                hideable
                isolatable
                zoomable
                linkable
                hidden={selectedPageList.every((p) => $hiddenPages[p.id])}
                selected={selectedPageList.length}
                isolateLabel="Isolate Pages (I)"
                tempHideLabel="Temporarily Hide (H)"
                on:delete={confirmRemovePages}
                on:hide={tempHidePages}
                on:isolate={isolatePages}
                on:show={tempShowPages}
                on:zoom-selected={zoomSelected}
                on:link={createPageLink} />
            {:else if !isEmpty($selectedRecords)}
              {@const selectedRecordsList = Object.keys($selectedRecords)}
              <SelectedActions
                locationcloneable={!disabled}
                selectable
                zoomable
                selected={selectedRecordsList.length}
                on:clone-locations={beginPlacingSelected}
                on:select-similar={selectRecords}
                on:zoom-selected={zoomSelectedRecords} />
            {/if}
          {/if}
          {#if !$itemOverlay}
            {#if hiddenLocationList.length || hiddenPageList.length || filtersApplied}
              <Portal target="#footer-status-container">
                <div class="flex items-center gap-4">
                  {#if hiddenLocationList.length}
                    <button class="btn-text light" on:click={showHiddenLocations}>
                      Show {hiddenLocationList.length} hidden locations
                    </button>
                  {/if}
                  {#if hiddenPageList.length}
                    <div class="flex gap-1">
                      <button class="btn-text light" on:click={showHiddenPages}>
                        Show Invisible Pages
                      </button>
                    </div>
                  {/if}
                  {#if filtersApplied}
                    <button class="btn-text light" on:click={() => eb.dispatch("open-doc-filter-settings")}>
                      Filters Applied
                    </button>
                  {/if}
                </div>
              </Portal>
            {/if}
          {/if}

          {#if adding}
            {@const addingItem = locRecord(adding, $group)}
            {@const l = locationsByRecord[adding.record_id] || []}
            {@const record = adding.record_type === "collection" ? "opening" : adding.record_type}
            <div class="absolute top-0 right-0 p-4 text-sm italic z-20">
              {#if addingItem}
                Adding location {l.length + 1}
                {#if adding.record_type === "item"}
                  / {addingItem.quantity}
                {/if}
                for {record}
                {addingItem.mark}
              {:else}
                Click to add new {record}s
              {/if}
            </div>
          {/if}
          {#key docid}
            <div class="absolute w-full h-full" style="cursor: {cursor};" bind:this={viewerContainer}>
              {#await setup()}
                <div class="w-full h-full flex flex-col justify-center items-center">
                  <div class="flex gap-2 items-center">
                    <div class="animate-spin">
                      <SpinnerIcon />
                    </div>
                    <div class="italic text-sm">Loading...</div>
                  </div>
                </div>
              {:then _}
                <OSDViewer
                  bind:this={viewer}
                  controlBus={controller}
                  options={{
                    zoomPerScroll: 1.8 + $settingScrollSpeed,
                    viewportMargins: {
                      left: 30,
                      top: 30,
                      right: 30,
                      bottom: 30,
                    },
                    timeout: $settingOsdTimeout,
                    tileRetryMax: $settingOsdTileRetryMax,
                    tileRetryDelay: $settingOsdTileRetryDelay,
                    drawer,
                  }}
                  pages={tileSources}
                  {pannable}
                  rightMousePan
                  {initialPage}
                  {initialBounds}
                  {tileOverlay}
                  {overlay}
                  spinners={spinnersList} />
                <canvas
                  bind:this={overlayCanvas}
                  class="absolute top-0 left-0 w-full h-full pointer-events-none" />
                {#if progress < 0.99999}
                  <div class="absolute bottom-0 right-0">
                    <div class="text-sm p-2">
                      {Math.round(progress * 100)}% complete
                    </div>
                  </div>
                {/if}
                {#if vddirty}
                  <div
                    class="absolute top-0 left-0 w-full h-full border-4 border-sky-400 pointer-events-none">
                    <div
                      class="absolute bottom-0 right-0 flex items-center gap-4 py-1 px-2 text-xs border-4 border-sky-400 bg-gray-100"
                      style="margin-bottom: -4px; margin-right: -4px;">
                      <div>View settings have changed:</div>
                      <button
                        class="px-2 py-0.5 bg-gray-200 hover:bg-gray-300 rounded font-bold pointer-events-auto border border-gray-400"
                        on:click={revertFilters}>Revert</button>
                      <button
                        class="px-2 py-0.5 bg-gray-200 hover:bg-gray-300 rounded font-bold pointer-events-auto border border-gray-400"
                        on:click={saveFilters}>Save</button>
                    </div>
                  </div>
                {/if}
                {#if $itemOverlay}
                  {@const item = $group.items[$itemOverlay]}
                  {@const type = $group.types[item?.type_id]}
                  {#if item}
                    {#if ioExtents}
                      <div
                        class="w-full h-full absolute top-0 left-0"
                        style="background: rgba(255, 255, 255, {item.show_drawing_overlay ? 0.8 : 1});"
                        style:opacity={itemOverlayPending ? 0 : null}
                        style:pointer-events={itemOverlayPending ? "none" : null}>
                        <Item2dEditor
                          {disabled}
                          isCollection={item.is_collection}
                          {group}
                          {item}
                          itemid={$itemOverlay}
                          initialExtents={ioExtents}
                          showDrawing={false}
                          showActionButtons
                          {type}
                          {types}
                          bind:selectedFeatures
                          on:updatebbox={trackDetailDrawing}
                          on:vpdblclick={(e) => {
                            if (!e.detail.hitbox) {
                              hideDetailDrawing();
                            }
                          }} />
                        <button
                          class="absolute right-0 bottom-0 m-4 btn btn-primary-alt"
                          on:click={hideDetailDrawing}>Return to Document</button>
                      </div>
                    {:else}
                      <div
                        class="w-full h-full absolute top-0 left-0 bg-white"
                        style:opacity={itemOverlayPending ? 0 : null}
                        style:pointer-events={itemOverlayPending ? "none" : null}>
                        <Item2dEditor
                          paddingTop={paddingY}
                          paddingRight={paddingX}
                          paddingBottom={paddingY + (sidebarPosition === "bottom" ? 224 : 0)}
                          paddingLeft={paddingX}
                          {disabled}
                          isCollection={item.is_collection}
                          {group}
                          {item}
                          itemid={$itemOverlay}
                          {type}
                          {types}
                          showActionButtons
                          bind:selectedFeatures
                          on:updatebbox={trackDetailDrawing}
                          on:vpdblclick={(e) => {
                            if (!e.detail.hitbox) {
                              hideDetailDrawing();
                            }
                          }} />
                        <button
                          class="absolute right-0 bottom-0 m-4 btn btn-primary-alt"
                          on:click={hideDetailDrawing}>Return to Document</button>
                      </div>
                    {/if}
                  {/if}
                {/if}
              {/await}
            </div>
          {/key}
        </div>
      </div>
      {#if $showRightPanel && UX_MODE === "shpx" && $leftPanelMode === "framex"}
        <RightPanelTabs
          {sidebarPosition}
          pageCount={visiblePages.length}
          {visiblePages}
          {group}
          {doc}
          {origDoc}
          {isVirtual}
          {disabled}
          {items}
          {types}
          {collections}
          {documentNotes}
          {customColumns}
          {customColColumns}
          {standardColumns}
          {indexedLocations}
          {locationsByRecord}
          {visibleIndices}
          {collectionItems}
          {selectedPageList}
          {selectedLocationList}
          {selectedDimensionList}
          {locations}
          {adding}
          bind:spinners
          bind:hoveredLocations
          bind:hoveredPage
          bind:lastSelectedPage
          {docLocations}
          {filters}
          on:updateItem={(e) => {
            group.updateItem($itemOverlay, e.detail.prop, e.detail.value, e.detail.diff);
          }}
          on:zoom-to-page={(e) => zoomToPage(e.detail.page)}
          on:add-location={beginAdding}
          on:clone-location={beginCloning}
          on:updateSupplier
          on:delete-record={confirmRemoveNote}
          on:dblclick-locations={handleLocationDblClick}
          on:update-visible-pages={showHiddenPageModal}
          on:set-scope={(e) => {
            group.update(
              selectedLocationList.map((loc) => ({
                type: loc.record_type === "collection" ? "item" : loc.record_type,
                id: loc.record_id,
                diff: { scope: e.detail.scope },
              })),
            );
          }}
          on:set-qty={(e) => {
            group.update(
              selectedLocationList.map((loc) => ({
                type: loc.record_type === "collection" ? "item" : loc.record_type,
                id: loc.record_id,
                diff: { quantity: e.detail.qty },
              })),
            );
          }}
          on:delete-frames={() => {
            // FrameX Delete removes the frame from the job (the record + all
            // its placements across the job), matching the legacy record
            // garbage-can — not just the selected placement. Undoable via
            // group.update. shpx-only path; legacy delete handlers above are
            // untouched.
            const recordIds = [...new Set(selectedLocationList.map((l) => l.record_id))];
            if (!recordIds.length) return;
            const locIds = $group.locations.order.filter((id) =>
              recordIds.includes($group.locations[id]?.record_id),
            );
            group.update([
              { type: "location", action: "remove", ids: locIds, block: true },
              { type: "item", action: "remove", ids: recordIds },
            ]);
            $selectedLocations = {};
          }} />
      {:else if $showRightPanel}
        <Sidebar
          position={sidebarPosition}
          type={sidebarPosition === "bottom" ? "absolute" : "flex"}
          expandable
          bind:expanded={$expandRightPanel}>
          <svelte:fragment slot="header">
            {#if $itemOverlay}
              {@const item = $group.items[$itemOverlay]}
              <div class="sticky top-0 bg-white z-10 py-4">
                <SidebarSection>
                  <div class="text-center font-bold truncate">{item?.mark}</div>
                </SidebarSection>
              </div>
            {:else if selectedLocationList.length === 1 && !selectedDimensionList.length}
              <PrevNext
                prev={prevLoc}
                next={nextLoc}
                title={`Location: ${locationRecord?.mark}`}
                on:gotoprev={gotoPrevLoc}
                on:gotonext={gotoNextLoc}
                sticky />
            {/if}
          </svelte:fragment>
          <svelte:fragment slot="content">
            {#if $itemOverlay}
              {@const item = $group.items[$itemOverlay]}
              {#if item}
                <ItemProperties
                  {disabled}
                  {group}
                  {item}
                  itemid={$itemOverlay}
                  {types}
                  {collections}
                  customColumns={item.is_collection ? customColumns : customColColumns}
                  {standardColumns}
                  isCollection={item.is_collection}
                  settings={$group.data.settings}
                  showDrawingScale
                  on:updateItem={(e) => {
                    group.updateItem($itemOverlay, e.detail.prop, e.detail.value, e.detail.diff);
                  }} />
              {/if}
            {:else if selectedPageList.length > 0}
              <PageProperties
                {disabled}
                {group}
                {origDoc}
                {doc}
                {indexedLocations}
                {isVirtual}
                pages={selectedPageList}
                on:zoom-to-page={(e) => zoomToPage(e.detail.page)} />
            {:else if selectedLocationList.length > 0 || selectedDimensionList.length > 0}
              <LocationProperties
                locations={selectedLocationList}
                dimensions={selectedDimensionList}
                docLocations={locations}
                {doc}
                {group}
                {types}
                {items}
                {locationsByRecord}
                {collections}
                {documentNotes}
                {disabled}
                {customColumns}
                {customColColumns}
                {collectionItems}
                {standardColumns}
                {visibleIndices}
                bind:spinners
                on:add-location={beginAdding}
                on:clone-location={beginCloning}
                on:updateSupplier />
            {:else if selectedLocationList.length === 0 && items}
              <div class="h-full flex flex-col gap-y-4" class:gap-4={sidebarPosition === "bottom"}>
                <DocLocationProperties
                  document={doc}
                  {disabled}
                  {group}
                  {items}
                  {types}
                  {documentNotes}
                  {collections}
                  {adding}
                  {customColumns}
                  {standardColumns}
                  {customColColumns}
                  bind:hoveredLocations
                  on:add-location={beginAdding}
                  on:clone-location={beginCloning}
                  on:delete-record={confirmRemoveNote}
                  on:dblclick-locations={handleLocationDblClick} />
              </div>
            {/if}
          </svelte:fragment>
        </Sidebar>
      {/if}
    </div>
  </div>
{/if}

<Modal
  bind:this={confirmRemoveLocationsModal}
  on:confirm={removeLocations}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete from Document</div>
  <div slot="content" class="space-y-2">
    <div class="space-y-2">
      {#if locationsToDelete.length && dimensionsToDelete.length}
        <p>
          Are you sure you want to delete {locationsToDelete.length} location marker(s) and {dimensionsToDelete.length}
          dimension(s)?
        </p>
      {:else if locationsToDelete.length}
        <p>Are you sure you want to delete {locationsToDelete.length} location marker(s)?</p>
      {:else}
        <p>Are you sure you want to delete {dimensionsToDelete.length} dimension(s)?</p>
      {/if}
    </div>
  </div>
</Modal>

<Modal
  bind:this={hiddenPageModal}
  on:confirm={updateHiddenPages}
  closeable
  closeOnOutclick
  fullframe
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Update", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">Update Visible Pages</div>
  <div slot="content" class="space-y-2 text-xs overflow-y-auto p-4">
    <div class="font-bold">Page Visibility</div>
    <div>
      {#each hiddenPageStatus as page, index (page.id)}
        <div class="flex items-center justify-between hover:bg-gray-100 px-1 gap-2">
          <div class="flex-none">{index + 1}</div>
          <div class:text-gray-400={page.hidden} class="grow truncate">{page.label}</div>
          <div class="flex-none">
            <button
              class:text-gray-400={page.hidden}
              on:click={() => (hiddenPageStatus[index].hidden = !hiddenPageStatus[index].hidden)}
              on:mousedown={() => (hiddenPageStatus[index].hidden = !hiddenPageStatus[index].hidden)}
              on:mouseenter={(e) => {
                if (e.buttons === 1) {
                  hiddenPageStatus[index].hidden = !hiddenPageStatus[index].hidden;
                }
              }}>
              {#if page.hidden}
                <HideIcon />
              {:else}
                <ShowIcon />
              {/if}
            </button>
          </div>
        </div>
      {/each}
    </div>
  </div>
</Modal>

<Modal
  bind:this={confirmRemovePagesModal}
  on:confirm={removePages}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete Pages</div>
  <div slot="content" class="space-y-2">
    <div class="space-y-2">
      <p>Are you sure you want to delete {pagesToDelete.length} page(s) from this document?</p>
    </div>
  </div>
</Modal>

<MarkUpdateModal
  bind:this={markUpdateModal}
  marks={markUpdateMarks}
  otherMarks={markUpdateOtherMarks}
  defaultStart={markUpdateStart}
  reverse={reverseMarkUpdate}
  on:reverse={setMarkUpdateReverse}
  on:confirm={updateMarks} />

<Modal
  bind:this={confirmRemoveNotesModal}
  on:confirm={removeNotes}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete Notes</div>
  <div slot="content" class="space-y-2">
    <div class="space-y-2">
      <p>Are you sure you want to delete {notesToDelete.length} note(s)?</p>
    </div>
  </div>
</Modal>

<Modal
  bind:this={setPageScaleModal}
  on:confirm={setPageScale}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Set Scale", type: "confirm", style: "primary" },
  ]}
  closeable
  closeOnOutclick>
  <div slot="title">Set Page Scale</div>
  <div slot="content" class="space-y-2">
    <SelectInput
      label="Page Scale"
      bind:value={pageScaleValue}
      on:option-click={() => customScaleModal.open(pageScaleValue)}
      options={[
        { label: "None", value: null },
        { label: "Set Custom Scale", value: "custom", unclickable: true },
        ...scaleOptions,
        ...(!pageScaleValue || !definedMultipliers[pageScaleValue]
          ? [{ label: `Custom Scale: ${pageScaleValue}`, value: pageScaleValue }]
          : []),
      ]}
      border />
  </div>
</Modal>
<CustomScaleModal
  bind:this={customScaleModal}
  on:setScale={(e) => {
    pageScaleValue = e.detail.value;
    setPageScale();
  }} />
<CountGrid {group} {docLocations} {selectedLocationList} docid={doc.id} {disabled} {filters} />
<ListDownloadModal
  {group}
  {types}
  {items}
  locations={selectedLocationList}
  {collections}
  name={$group?.job.name} />
